import { LearningProgressProvider, ActivityStatus, useActivity } from "./LearningProgress";
import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../services/api";
import { searchCatalogue } from "../services/catalogSearchApi";
import SearchDropdown, { EMPTY_SEARCH_RESULTS } from "./SearchDropdown";
import { buildModuleSearchTarget } from "./searchNavigation";
import {
  NouveautesContext, Pastille, PointNouveauContenu, marquerOuvert, useCompteurs, useNotificationsDeLaRubrique,
} from "../services/compteurs";
import ProfileModal from "./ProfileModal";
import AssistanceEleve from "./assistance/AssistanceEleve";
import Parametres from "./Parametres";
import BarreAccesRapide from "./BarreAccesRapide";
import NotifBell from "./NotifBell";
import IncomingCallListener from "./IncomingCallListener";
import MathKeyboard from "./MathKeyboard";
import MathEditor from "./MathEditor";
import MathText, { MathDisplay, ExpectedAnswer } from "./MathText";
import { qlAlert } from "../utils/dialogs";
import DownloadLink from "./DownloadLink";
import VisiteGuidee, { visiteDejaVue } from "./VisiteGuidee";
import ManuelSwitcher from "./ManuelSwitcher";
import DocumentViewer, { adresseComplete } from "./DocumentViewer";
import CoursAudio from "./CoursAudio";
import { BandeauHorsConnexion, BoutonHorsConnexion, MesContenusHorsConnexion, PdfLocal, useEnvoiAutomatiqueDesCopies, useFichierHorsConnexion } from "./HorsConnexion";
import { erreurReseau, getAvecCache, mettreCopieEnAttente, useEnLigne } from "../services/horsConnexion";
import VideoSuivie from "./VideoSuivie";
import { AUTORISATIONS_LECTEUR, urlLecteurYoutube } from "../services/lecteurYoutube";
import NotesOrganizationView from "./NotesOrganizationView";
import { openCinetPayExternally } from "../teacher/services/externalCheckout";
import { Chart as ChartJS, ArcElement, Tooltip, LineElement, PointElement, CategoryScale, LinearScale, Legend, Filler } from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";
import BannierePublicitaire from "./BannierePublicitaire";

// Écran de l'élève → rubriques de notifications qu'il « ouvre ».
const RUBRIQUES_ELEVE = {
  lecons: ["lecons"], videos: ["videos"], exercices: ["exercices"], "exercice-solver": ["exercices"],
  qcm: ["qcm"], "qcm-player": ["qcm"], devoirs: ["devoirs"], evaluations: ["evaluations"],
  formules: ["formules"], lives: ["lives"], messages: ["messages"], module: ["lecons", "videos", "exercices", "qcm"],
};

ChartJS.register(ArcElement, Tooltip, LineElement, PointElement, CategoryScale, LinearScale, Legend, Filler);

// ── CorrectionFileViewer ─────────────────────────────
// Détecte le type de fichier depuis l'URL et affiche image ou PDF inline.
// Les PDF sont affichés via Google Docs Viewer (iframe) — Google récupère et
// rend le fichier côté serveur, ce qui évite les soucis de chargement/rendu
// PDF côté client rencontrés sur certains navigateurs mobiles.
function CorrectionFileViewer({ url, label = "Fichier de correction" }) {
  const [open, setOpen] = useState(false);
  if (!url) return null;
  return (
    <div className="ql-doc-buttons">
      <button className="btn btn-sm btn-outline-success" onClick={() => setOpen(true)}>
        <i className="bi bi-eye me-1"></i>{`Voir : ${label}`}
      </button>
      <DownloadLink url={url} filename={url.split("?")[0].split("/").pop()} target="_blank" rel="noreferrer" className="btn btn-sm btn-success">
        <i className="bi bi-download me-1"></i>Télécharger
      </DownloadLink>
      {open && <DocumentViewer titre={label} url={url} onClose={() => setOpen(false)} />}
    </div>
  );
}

// Visite guidée de l'espace élève (premier accès, puis bouton « ? »).
const ETAPES_ELEVE = [
  { cible: '[data-visite="menu"]', icone: "bi-list", titre: "Le menu", texte: "Touche ici pour ouvrir toutes les rubriques : leçons, vidéos, exercices, évaluations, devoirs, messages…" },
  { cible: '[data-visite="dashboard"]', icone: "bi-house-fill", titre: "Ton tableau de bord", texte: "Ton point de départ : ce que tu as fait, ce qui t'attend et les nouveautés." },
  { cible: '[data-visite="lecons"]', icone: "bi-file-text", titre: "Les leçons", texte: "Les cours de ton manuel, chapitre par chapitre. Un point rouge signale une leçon que tu n'as pas encore ouverte." },
  { cible: '[data-visite="videos"]', icone: "bi-play-circle", titre: "Les vidéos", texte: "Des explications en vidéo ; tu reprends là où tu t'étais arrêté." },
  { cible: '[data-visite="exercices"]', icone: "bi-pencil", titre: "Les exercices", texte: "Entraîne-toi et demande l'aide d'un enseignant si tu bloques." },
  { cible: '[data-visite="evaluations"]', icone: "bi-clipboard", titre: "Les évaluations", texte: "Les épreuves à traiter. Une épreuve « Obligatoire » doit être rendue avant son échéance." },
  { cible: '[data-visite="devoirs"]', icone: "bi-pencil-square", titre: "Les devoirs", texte: "Tes devoirs à rendre : traite-les sur ta copie, scanne-la et envoie-la ici." },
  { cible: '[data-visite="messages"]', icone: "bi-headset", titre: "L'assistance", texte: "Pose tes questions à un enseignant et suis ta place dans la file d'attente ; c'est aussi là que tu reçois tes corrections." },
  { cible: '[data-visite="recherche"]', icone: "bi-search", titre: "La recherche", texte: "Tape un titre ou un code (ex. MEC-LDN-QCM-01) pour trouver un contenu tout de suite." },
  { cible: '[data-visite="notifications"]', icone: "bi-bell", titre: "Les notifications", texte: "Le point rouge t'indique une nouveauté ; il disparaît quand tu ouvres la rubrique concernée." },
  { cible: '[data-visite="progression"]', icone: "bi-bar-chart-line-fill", titre: "Ta progression", texte: "Ton avancement, tes prochains devoirs et tes épreuves obligatoires." },
  { cible: '[data-visite="aide"]', icone: "bi-question-circle", titre: "Besoin d'aide ?", texte: "Touche ce bouton pour revoir cette visite à tout moment." },
  { cible: '[data-visite="profil"]', icone: "bi-person-circle", titre: "Ton profil", texte: "Ta photo, ta classe et tes informations personnelles." },
];

// Conseil affiché partout où l'élève joint un fichier.
function ConseilScan() {
  return (
    <div className="ql-conseil-scan">
      <i className="bi bi-camera"></i>
      <span>
        Tu peux traiter en dehors de l'application : après avoir traité sur ta copie, scanne-la avec une
        application comme CamScanner et télécharge-la dans l'application avant de soumettre.
      </span>
    </div>
  );
}

// ── Navigation items ───────────────────────────────
const NAV_APPRENDRE = [
  { icon: "bi-book",        label: "Modules",    view: "modules" },
  { icon: "bi-file-text",   label: "Leçons",     view: "lecons", compteur: "lecons" },
  { icon: "bi-play-circle", label: "Vidéos",     view: "videos", compteur: "videos" },
  { icon: "bi-headphones",  label: "Cours audio", view: "audios" },
  { icon: "bi-grid",        label: "QCM",        view: "qcm" },
  { icon: "bi-pencil",      label: "Exercices",  view: "exercices", compteur: "exercices" },
  { icon: "bi-folder",      label: "Ressources", view: "ressources" },
];
const NAV_EVALUER = [
  { icon: "bi-clipboard",     label: "Évaluations", view: "evaluations", compteur: "evaluations" },
  { icon: "bi-pencil-square", label: "Devoirs",      view: "devoirs", compteur: "devoirs" },
  { icon: "bi-bar-chart",     label: "Mes résultats",view: "resultats" },
];
const NAV_OUTILS = [
  { icon: "bi-heart",      label: "Favoris",        view: "favoris" },
  { icon: "bi-sticky",     label: "Notes",          view: "notes" },
  { icon: "bi-calculator", label: "Calculatrice",   view: "calculatrice" },
  { icon: "bi-infinity",   label: "Formules utiles",view: "formules" },
  { icon: "bi-cloud-check", label: "Hors connexion", view: "hors-connexion" },
];
const NAV_COMMUNIQUER = [
  { icon: "bi-headset",    label: "Assistance",     view: "messages", compteur: "messages" },
];
const NAV_LIVE = [
  { icon: "bi-broadcast",  label: "Lives",           view: "lives" },
];
const ALL_NAV = [...NAV_APPRENDRE, ...NAV_EVALUER, ...NAV_OUTILS, ...NAV_COMMUNIQUER, ...NAV_LIVE];

const CHAPTER_COLORS = ["#0d6efd", "#6f42c1", "#198754", "#f5820d", "#20c997", "#e83e8c", "#fd7e14", "#0dcaf0"];

const ASTUCES = [
  { text: "Révise régulièrement et n'hésite pas à refaire les exercices pour progresser davantage !", sign: "Bonne continuation ! 👍" },
  { text: "La constance est la clé du succès. Même 20 minutes par jour peuvent faire une grande différence !", sign: "Tu peux le faire ! 💪" },
  { text: "N'aie pas peur de faire des erreurs — elles sont tes meilleures leçons. Analyse-les et avance !", sign: "En avant ! 🚀" },
  { text: "Chaque exercice résolu est une victoire. Continue à construire ta confiance pas à pas.", sign: "Bravo pour tes efforts ! 🌟" },
  { text: "Pose des questions à ton enseignant sans hésitation. Comprendre, c'est progresser.", sign: "Courage ! 📚" },
  { text: "Révise tes leçons le soir même. La mémoire retient mieux ce qu'elle voit deux fois.", sign: "Bonne étude ! 🎯" },
  { text: "Les grandes réussites commencent par de petits efforts quotidiens. Sois régulier et patient.", sign: "On croit en toi ! ⭐" },
  { text: "Prends le temps de comprendre plutôt que de mémoriser. La compréhension dure toute la vie !", sign: "Bonne continuation ! 👍" },
  { text: "Chaque leçon vue, chaque QCM passé te rapproche de ton objectif. Ne t'arrête pas !", sign: "Tu es sur la bonne voie ! 🏆" },
  { text: "Organise ton temps d'étude : des sessions courtes et régulières valent mieux qu'une longue séance rare.", sign: "Méthode et persévérance ! 📅" },
];

const ACTIVITIES = [
  { icon: "bi-check-circle-fill",    color: "#198754", type: "Exercice résolu",       name: "OND-03 : Propagation des ondes",    score: "Score : 85%", scoreColor: "#198754", time: "Il y a 2h" },
  { icon: "bi-file-earmark-text-fill",color:"#6f42c1", type: "QCM terminé",           name: "OSC-01 : Oscillations mécaniques",  score: "Score : 70%", scoreColor: "#f5b400", time: "Il y a 4h" },
  { icon: "bi-play-circle-fill",     color: "#f5820d", type: "Vidéo regardée",         name: "La période d'un oscillateur",       score: "100%",        scoreColor: "#198754", time: "Hier" },
  { icon: "bi-download",             color: "#0d6efd", type: "Ressource téléchargée",  name: "Fiches de révision – Ondes",        score: "",            scoreColor: "",        time: "Hier" },
];
// BADGES définis dynamiquement à partir des stats réelles (voir computeBadges)
function computeBadges(stats) {
  const streak        = stats?.streak          || 0;
  const exercicesDone = stats?.exercises_done  || 0;
  const avgScore      = stats?.average_score   || 0;
  return [
    {
      icon:    "bi-shield-fill",
      color:   "#6f42c1",
      label:   "Assidu",
      sub:     streak > 0 ? `${streak} jour${streak > 1 ? "s" : ""} d'affilée` : "Connectez-vous chaque jour",
      earned:  streak >= 3,
      locked:  `${3 - Math.min(streak, 3)} jour${3 - Math.min(streak, 3) !== 1 ? "s" : ""} restant${3 - Math.min(streak, 3) !== 1 ? "s" : ""}`,
    },
    {
      icon:    "bi-shield-fill-check",
      color:   "#198754",
      label:   "Persévérant",
      sub:     `${exercicesDone} exercice${exercicesDone !== 1 ? "s" : ""} résolu${exercicesDone !== 1 ? "s" : ""}`,
      earned:  exercicesDone >= 5,
      locked:  `Encore ${Math.max(0, 5 - exercicesDone)} exercice${5 - exercicesDone !== 1 ? "s" : ""}`,
    },
    {
      icon:    "bi-trophy-fill",
      color:   "#f5820d",
      label:   "Bien joué",
      sub:     avgScore > 0 ? `Score moyen : ${avgScore}%` : "Passez un QCM",
      earned:  avgScore >= 70,
      locked:  `Score moyen : ${avgScore}% (seuil 70%)`,
    },
  ];
}
// Calcule le badge et le délai à partir d'une date d'échéance
function devoirDelay(dateStr) {
  if (!dateStr) return { badge: null, delay: "Pas de date limite" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due   = new Date(dateStr);
  const diff  = Math.round((due - today) / 86400000);
  if (diff < 0)  return { badge: "En retard", badgeClass: "ql-devoir-badge--retard", delay: `${Math.abs(diff)} jour${Math.abs(diff) > 1 ? "s" : ""} de retard` };
  if (diff === 0) return { badge: "Aujourd'hui", badgeClass: "ql-devoir-badge--urgent", delay: "Aujourd'hui !" };
  if (diff === 1) return { badge: "Demain", badgeClass: "ql-devoir-badge--urgent", delay: "Demain" };
  if (diff <= 3) return { badge: "Urgent", badgeClass: "ql-devoir-badge--urgent", delay: `Dans ${diff} jours` };
  if (diff <= 7) return { badge: "À rendre", badgeClass: "ql-devoir-badge", delay: `Dans ${diff} jours` };
  return { badge: null, delay: `Dans ${diff} jours` };
}

const MOIS_FR = ["JAN","FÉV","MAR","AVR","MAI","JUN","JUL","AOÛ","SEP","OCT","NOV","DÉC"];

// ── Helpers ────────────────────────────────────────
function getYouTubeId(url) {
  if (!url) return null;
  // ?v=ID (youtube.com/watch, m.youtube.com, etc.)
  let m = url.match(/[?&]v=([^?&\s]+)/);
  if (m) return m[1];
  // youtu.be/ID | youtube.com/shorts/ID | youtube.com/embed/ID | youtube.com/v/ID
  m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:shorts|embed|v)\/)([^?&\s]+)/);
  return m ? m[1] : null;
}
function chColor(i) { return CHAPTER_COLORS[i % CHAPTER_COLORS.length]; }

// ── Rendu mathématique ────────────────────────────
// Tokenise un texte en segments : texte brut, exposants ^(...) ou ^x, indices _(...)ou _x, fractions a/b
function parseMath(text) {
  if (!text) return [];
  // Ordre de priorité : exposant avec parenthèses, exposant simple, indice avec parenthèses, indice simple, fraction
  const TOKEN = /\^\(([^)]+)\)|\^([a-zA-Z0-9+\-]+)|_\(([^)]+)\)|_([a-zA-Z0-9+\-]+)|((?:[a-zA-Z0-9]+)(?:[+\-][a-zA-Z0-9]+)*)\/((?:[a-zA-Z0-9]+)(?:[+\-][a-zA-Z0-9]+)*)/g;
  const parts = [];
  let last = 0;
  let m;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", val: text.slice(last, m.index) });
    if (m[1] !== undefined) parts.push({ type: "sup", val: m[1] });        // ^(...)
    else if (m[2] !== undefined) parts.push({ type: "sup", val: m[2] });   // ^x
    else if (m[3] !== undefined) parts.push({ type: "sub", val: m[3] });   // _(...)
    else if (m[4] !== undefined) parts.push({ type: "sub", val: m[4] });   // _x
    else parts.push({ type: "frac", num: m[5], den: m[6] });               // a/b
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", val: text.slice(last) });
  return parts;
}

function InlineMath({ text }) {
  if (!text) return null;
  const parts = parseMath(text);
  if (parts.length === 0) return null;
  return (
    <>
      {parts.map((p, i) => {
        if (p.type === "text") return <React.Fragment key={i}>{p.val}</React.Fragment>;
        if (p.type === "sup")  return <sup key={i}>{p.val}</sup>;
        if (p.type === "sub")  return <sub key={i}>{p.val}</sub>;
        if (p.type === "frac") return (
          <span key={i} className="ql-frac" title={`${p.num}/${p.den}`}>
            <span className="ql-frac-n">{p.num}</span>
            <span className="ql-frac-d">{p.den}</span>
          </span>
        );
        return null;
      })}
    </>
  );
}

// Le champ "formules" d'une QuestionSavoirFaire liste plusieurs variantes
// acceptées (notation lisible + LaTeX), séparées par une virgule ou un
// saut de ligne — convention déjà utilisée côté admin. La dernière variante
// est toujours celle en LaTeX propre (\frac, \sqrt...) ; les précédentes
// (ex: "√(...)" en notation libre) ne sont pas du LaTeX valide et rendent
// mal via KaTeX (le radical ne s'affiche pas correctement autour de son
// argument). On n'affiche donc que la dernière à l'élève.
function lastFormulaCandidate(formulesText) {
  const candidates = formulesText.split(/[,\n]+/).map(s => s.trim()).filter(Boolean);
  return candidates.length ? candidates[candidates.length - 1] : formulesText;
}

// Rendu LaTeX + texte formaté (sauts de ligne + $...$)
function FormattedText({ text, className, style }) {
  if (!text) return null;
  return (
    <span className={className} style={{ display: "block", ...style }}>
      <MathText text={text} />
    </span>
  );
}

// ── Small reusables ────────────────────────────────
function ProgressRow({ label, val, max, color }) {
  const pct = Math.min(100, Math.round((val / max) * 100));
  return (
    <div className="ql-prog-row">
      <span className="ql-prog-label">{label}</span>
      <div className="ql-mini-bar"><div style={{ width: `${pct}%`, background: color }}></div></div>
      <span className="ql-prog-val">{val} / {max}</span>
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div className="ql-empty-state">
      <i className={`bi ${icon}`}></i>
      <p>{text}</p>
    </div>
  );
}

// ── RessourcesView ────────────────────────────────
function RessourcesView({ modules, navigate }) {
  const [counts,  setCounts]  = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("lecons/").catch(() => ({ data: [] })),
      api.get("videos/").catch(() => ({ data: [] })),
      api.get("exercices/").catch(() => ({ data: [] })),
      api.get("qcms/").catch(() => ({ data: [] })),
      api.get("devoirs/").catch(() => ({ data: [] })),
      api.get("evaluations/").catch(() => ({ data: [] })),
    ]).then(([lecons, videos, exs, qcms, devoirs, evals]) => {
      setCounts({
        lecons:      lecons.data.length,
        videos:      videos.data.length,
        exercices:   exs.data.length,
        qcms:        qcms.data.length,
        devoirs:     devoirs.data.length,
        evaluations: evals.data.length,
      });
    }).finally(() => setLoading(false));
  }, []);

  const CATEGORIES = [
    { key: "lecons",      icon: "bi-book-fill",            label: "Cours & Leçons",  color: "#0d6efd", bg: "#eff6ff", view: "lecons"      },
    { key: "videos",      icon: "bi-play-circle-fill",     label: "Vidéos",          color: "#f5820d", bg: "#fff7ed", view: "videos"      },
    { key: "exercices",   icon: "bi-pencil-square",        label: "Exercices",       color: "#198754", bg: "#f0fdf4", view: "exercices"   },
    { key: "qcms",        icon: "bi-grid-fill",            label: "QCM",             color: "#6f42c1", bg: "#faf5ff", view: "qcm"         },
    { key: "devoirs",     icon: "bi-pencil-fill",          label: "Devoirs",         color: "#dc3545", bg: "#fff5f5", view: "devoirs"     },
    { key: "evaluations", icon: "bi-clipboard-check-fill", label: "Évaluations",     color: "#0891b2", bg: "#ecfeff", view: "evaluations" },
  ];

  const total = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0;

  return (
    <div>
      <div className="ql-view-header">
        <h3 className="ql-view-title"><i className="bi bi-folder2-open me-2"></i>Ressources</h3>
        {!loading && (
          <span className="ql-view-count">{total} ressource{total !== 1 ? "s" : ""}</span>
        )}
      </div>

      <div className="ql-res-grid">
        {CATEGORIES.map(cat => (
          <div
            key={cat.key}
            className="ql-res-card"
            style={{ "--res-color": cat.color, "--res-bg": cat.bg }}
            onClick={() => navigate(cat.view)}
          >
            <div className="ql-res-icon-wrap">
              <i className={`bi ${cat.icon}`}></i>
            </div>
            <div className="ql-res-body">
              <div className="ql-res-label">{cat.label}</div>
              <div className="ql-res-count">
                {loading
                  ? <span className="spinner-border spinner-border-sm" style={{ width: "14px", height: "14px", borderWidth: "2px" }}></span>
                  : <><strong>{counts[cat.key]}</strong> disponible{counts[cat.key] !== 1 ? "s" : ""}</>
                }
              </div>
            </div>
            <i className="bi bi-chevron-right ql-res-arrow"></i>
          </div>
        ))}
      </div>

      {!loading && counts && (
        <p className="text-muted mt-3" style={{ fontSize: "0.8rem" }}>
          <i className="bi bi-info-circle me-1"></i>
          Cliquez sur une catégorie pour accéder directement aux ressources correspondantes.
        </p>
      )}
    </div>
  );
}

// ── MesResultatsView ─────────────────────────────────
function MesResultatsView() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("tous");

  useEffect(() => {
    api.get("resultats/")
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;
  if (!data)   return null;

  function fmt(d) {
    if (!d) return "";
    return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  }

  const allItems = [
    ...data.qcm.map(r        => ({ ...r, _type: "qcm"        })),
    ...data.exercices.map(r  => ({ ...r, _type: "exercice"   })),
    ...data.exercices_sf.map(r => ({ ...r, _type: "sf"       })),
    ...data.devoirs.map(r    => ({ ...r, _type: "devoir"     })),
    ...data.evaluations.map(r => ({ ...r, _type: "evaluation"})),
  ].sort((a, b) => new Date(b.date || b.submitted_at) - new Date(a.date || a.submitted_at));

  const TABS = [
    { key: "tous",       label: "Tout",        count: allItems.length },
    { key: "qcm",       label: "QCM",         count: data.qcm.length },
    { key: "exercice",  label: "Exercices",   count: data.exercices.length + data.exercices_sf.length },
    { key: "devoir",    label: "Devoirs",     count: data.devoirs.length },
    { key: "evaluation",label: "Évaluations", count: data.evaluations.length },
  ];

  const filtered = tab === "tous"
    ? allItems
    : allItems.filter(i => tab === "exercice" ? (i._type === "exercice" || i._type === "sf") : i._type === tab);

  const avgQcm = data.qcm.length > 0
    ? Math.round(data.qcm.reduce((acc, r) => acc + r.score, 0) / data.qcm.length)
    : null;

  function ResultCard({ item }) {
    const date = fmt(item.date || item.submitted_at);
    if (item._type === "qcm") {
      return (
        <div className="ql-result-card">
          <div className="ql-result-icon" style={{ background: item.passed ? "#f0fdf4" : "#fff5f5", color: item.passed ? "#16a34a" : "#dc2626" }}>
            <i className={`bi bi-${item.passed ? "check-circle-fill" : "x-circle-fill"}`}></i>
          </div>
          <div className="ql-result-body">
            <div className="ql-result-title">{item.titre}</div>
            {item.module && <div className="ql-result-meta"><i className="bi bi-collection me-1"></i>{item.module}</div>}
          </div>
          <div className="ql-result-right">
            <span className={`ql-result-score ${item.passed ? "success" : "danger"}`}>{item.score}%</span>
            <div className="ql-result-date">{date}</div>
          </div>
        </div>
      );
    }
    if (item._type === "exercice") {
      return (
        <div className="ql-result-card">
          <div className="ql-result-icon" style={{ background: "#f0fdf4", color: "#16a34a" }}>
            <i className="bi bi-pencil-square"></i>
          </div>
          <div className="ql-result-body">
            <div className="ql-result-title">{item.titre}</div>
            {item.module && <div className="ql-result-meta"><i className="bi bi-collection me-1"></i>{item.module}</div>}
          </div>
          <div className="ql-result-right">
            {item.score > 0 && <span className="ql-result-score success">{item.score}%</span>}
            <div className="ql-result-date">{date}</div>
          </div>
        </div>
      );
    }
    if (item._type === "sf") {
      const pct = item.score_max > 0 ? Math.round((item.score / item.score_max) * 100) : 0;
      return (
        <div className="ql-result-card">
          <div className="ql-result-icon" style={{ background: "#faf5ff", color: "#6f42c1" }}>
            <i className="bi bi-stars"></i>
          </div>
          <div className="ql-result-body">
            <div className="ql-result-title">{item.titre}</div>
            {item.module && <div className="ql-result-meta"><i className="bi bi-collection me-1"></i>{item.module}</div>}
          </div>
          <div className="ql-result-right">
            <span className="ql-result-score" style={{ color: pct >= 70 ? "#16a34a" : pct >= 50 ? "#f5820d" : "#dc2626" }}>
              {item.score}/{item.score_max}
            </span>
            <div className="ql-result-date">{date}</div>
          </div>
        </div>
      );
    }
    if (item._type === "devoir") {
      const graded = !!item.grade;
      return (
        <div className="ql-result-card">
          <div className="ql-result-icon" style={{ background: graded ? "#f0fdf4" : "#fff7ed", color: graded ? "#16a34a" : "#f5820d" }}>
            <i className={`bi bi-${graded ? "patch-check-fill" : "clock-history"}`}></i>
          </div>
          <div className="ql-result-body">
            <div className="ql-result-title">
              {item.titre}
              {!graded && <span className="badge bg-warning text-dark ms-2" style={{ fontSize: "0.68rem" }}>En attente</span>}
            </div>
            {item.module && <div className="ql-result-meta"><i className="bi bi-collection me-1"></i>{item.module}</div>}
            {item.feedback && graded && <div className="ql-result-feedback">{item.feedback}</div>}
          </div>
          <div className="ql-result-right">
            {graded && <span className="ql-result-score success">{item.grade}</span>}
            <div className="ql-result-date">{fmt(item.submitted_at)}</div>
          </div>
        </div>
      );
    }
    if (item._type === "evaluation") {
      const graded = !!item.grade;
      return (
        <div className="ql-result-card">
          <div className="ql-result-icon" style={{ background: graded ? "#ecfeff" : "#fff7ed", color: graded ? "#0891b2" : "#f5820d" }}>
            <i className={`bi bi-${graded ? "clipboard-check-fill" : "clipboard-x"}`}></i>
          </div>
          <div className="ql-result-body">
            <div className="ql-result-title">
              {item.titre}
              {!graded && <span className="badge bg-warning text-dark ms-2" style={{ fontSize: "0.68rem" }}>En attente</span>}
            </div>
            {item.matiere && <div className="ql-result-meta"><i className="bi bi-book me-1"></i>{item.matiere}</div>}
            {item.feedback && graded && <div className="ql-result-feedback">{item.feedback}</div>}
          </div>
          <div className="ql-result-right">
            {graded && <span className="ql-result-score" style={{ color: "#0891b2" }}>{item.grade}</span>}
            <div className="ql-result-date">{fmt(item.submitted_at)}</div>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div>
      <div className="ql-view-header">
        <h3 className="ql-view-title"><i className="bi bi-bar-chart-line me-2"></i>Mes résultats</h3>
      </div>

      <div className="ql-result-stats">
        <div className="ql-result-stat">
          <div className="ql-result-stat-num">{allItems.length}</div>
          <div className="ql-result-stat-label">Activités</div>
        </div>
        {avgQcm !== null && (
          <div className="ql-result-stat">
            <div className="ql-result-stat-num" style={{ color: avgQcm >= 70 ? "#16a34a" : avgQcm >= 50 ? "#f5820d" : "#dc2626" }}>
              {avgQcm}%
            </div>
            <div className="ql-result-stat-label">Moy. QCM</div>
          </div>
        )}
        <div className="ql-result-stat">
          <div className="ql-result-stat-num">{data.devoirs.filter(d => d.grade).length}/{data.devoirs.length}</div>
          <div className="ql-result-stat-label">Devoirs notés</div>
        </div>
        <div className="ql-result-stat">
          <div className="ql-result-stat-num">{data.evaluations.filter(e => e.grade).length}/{data.evaluations.length}</div>
          <div className="ql-result-stat-label">Évals notées</div>
        </div>
      </div>

      <div className="ql-result-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`ql-result-tab${tab === t.key ? " active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.count > 0 && <span className="ql-result-tab-count">{t.count}</span>}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox fs-1 d-block mb-3"></i>
          <p>Aucun résultat dans cette catégorie pour l'instant.</p>
        </div>
      ) : (
        <div className="ql-result-list">
          {filtered.map((item, i) => <ResultCard key={i} item={item} />)}
        </div>
      )}
    </div>
  );
}

// ── MesFavorisView ────────────────────────────────────
function MesFavorisView({ navigate }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("favoris/")
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;
  if (!data)   return null;

  const totalItems = (data.videos?.length || 0) + (data.lecons?.length || 0);

  function fmt(d) {
    if (!d) return "";
    return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  }

  function getThumb(url) {
    const id = getYouTubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
  }

  return (
    <div>
      <div className="ql-view-header">
        <h3 className="ql-view-title"><i className="bi bi-heart me-2"></i>Favoris</h3>
        {totalItems > 0 && (
          <span className="ql-view-count">{totalItems} ressource{totalItems !== 1 ? "s" : ""} consultée{totalItems !== 1 ? "s" : ""}</span>
        )}
      </div>

      {totalItems === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-heart fs-1 d-block mb-3" style={{ color: "#f1b9c5" }}></i>
          <p className="fw-semibold">Aucune ressource consultée pour l'instant.</p>
          <p className="small">Regarde des vidéos et lis des leçons pour les retrouver ici !</p>
          <div className="d-flex gap-2 justify-content-center mt-3">
            <button className="btn btn-outline-warning btn-sm" onClick={() => navigate("videos")}>
              <i className="bi bi-play-circle me-1"></i>Vidéos
            </button>
            <button className="btn btn-outline-primary btn-sm" onClick={() => navigate("lecons")}>
              <i className="bi bi-book me-1"></i>Leçons
            </button>
          </div>
        </div>
      ) : (
        <>
          {data.videos?.length > 0 && (
            <div className="mb-4">
              <div className="ql-favori-section-title">
                <i className="bi bi-play-circle-fill me-2" style={{ color: "#f5820d" }}></i>
                Vidéos regardées
                <span className="ql-favori-count">{data.videos.length}</span>
              </div>
              <div className="ql-favori-grid">
                {data.videos.map(v => {
                  const thumb = getThumb(v.youtube_url);
                  return (
                    <div key={v.id} className="ql-favori-card" onClick={() => navigate("videos")}>
                      <div className="ql-favori-thumb">
                        {thumb
                          ? <><img src={thumb} alt={v.titre} /><div className="ql-favori-play"><i className="bi bi-play-circle-fill"></i></div></>
                          : <i className="bi bi-play-circle fs-2 text-muted"></i>
                        }
                      </div>
                      <div className="ql-favori-body">
                        <div className="ql-favori-title">{v.titre}</div>
                        <div className="ql-favori-meta">
                          {v.module && <span><i className="bi bi-collection me-1"></i>{v.module}</span>}
                          {v.watched_at && <span className="ms-auto">{fmt(v.watched_at)}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {data.lecons?.length > 0 && (
            <div className="mb-4">
              <div className="ql-favori-section-title">
                <i className="bi bi-book-fill me-2" style={{ color: "#0d6efd" }}></i>
                Leçons consultées
                <span className="ql-favori-count">{data.lecons.length}</span>
              </div>
              <div className="ql-favori-list">
                {data.lecons.map(l => (
                  <div key={l.id} className="ql-favori-lecon" onClick={() => navigate("lecons")}>
                    <div className="ql-favori-lecon-icon">
                      <i className="bi bi-file-earmark-text-fill"></i>
                    </div>
                    <div className="ql-favori-lecon-body">
                      <div className="ql-favori-title">{l.titre}</div>
                      {l.module && <div className="ql-favori-meta"><i className="bi bi-collection me-1"></i>{l.module}</div>}
                    </div>
                    <div className="ql-favori-lecon-date">{fmt(l.viewed_at)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── NotesView ─────────────────────────────────────────
const PALETTE = ["#0d6efd","#198754","#f5820d","#dc2626","#6f42c1","#0891b2","#db2777","#0f172a"];
const JOURS_ORDER = ["lundi","mardi","mercredi","jeudi","vendredi","samedi","dimanche"];
const JOURS_LABEL = { lundi:"Lundi", mardi:"Mardi", mercredi:"Mercredi", jeudi:"Jeudi", vendredi:"Vendredi", samedi:"Samedi", dimanche:"Dimanche" };

function NotesView() {
  const [tab, setTab] = useState("todo");

  // ── TODO ──
  const [todos,    setTodos]    = useState([]);
  const [todoText, setTodoText] = useState("");
  const [todoLoad, setTodoLoad] = useState(true);

  // ── EMPLOI DU TEMPS ──
  const [slots,     setSlots]     = useState([]);
  const [slotLoad,  setSlotLoad]  = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [editSlot,  setEditSlot]  = useState(null);
  const [form, setForm] = useState({ jour:"lundi", heure_debut:"08:00", heure_fin:"09:00", label:"", couleur:"#0d6efd" });

  useEffect(() => {
    api.get("todos/").then(r => setTodos(r.data)).catch(() => {}).finally(() => setTodoLoad(false));
    api.get("emploi-du-temps/").then(r => setSlots(r.data)).catch(() => {}).finally(() => setSlotLoad(false));
  }, []);

  // ── Todo handlers ──
  function addTodo(e) {
    e.preventDefault();
    if (!todoText.trim()) return;
    api.post("todos/", { text: todoText.trim(), completed: false, order: todos.length })
      .then(r => { setTodos(prev => [...prev, r.data]); setTodoText(""); })
      .catch(() => {});
  }

  function toggleTodo(todo) {
    api.patch(`todos/${todo.id}/`, { completed: !todo.completed })
      .then(r => setTodos(prev => prev.map(t => t.id === r.data.id ? r.data : t)))
      .catch(() => {});
  }

  function deleteTodo(id) {
    api.delete(`todos/${id}/`)
      .then(() => setTodos(prev => prev.filter(t => t.id !== id)))
      .catch(() => {});
  }

  const pending   = todos.filter(t => !t.completed);
  const completed = todos.filter(t => t.completed);

  // ── Slot handlers ──
  function openNew() {
    setEditSlot(null);
    setForm({ jour:"lundi", heure_debut:"08:00", heure_fin:"09:00", label:"", couleur:"#0d6efd" });
    setShowForm(true);
  }

  function openEdit(slot) {
    setEditSlot(slot);
    setForm({
      jour:        slot.jour,
      heure_debut: slot.heure_debut.slice(0,5),
      heure_fin:   slot.heure_fin.slice(0,5),
      label:       slot.label,
      couleur:     slot.couleur,
    });
    setShowForm(true);
  }

  function saveSlot(e) {
    e.preventDefault();
    const req = editSlot
      ? api.put(`emploi-du-temps/${editSlot.id}/`, form)
      : api.post("emploi-du-temps/", form);
    req.then(r => {
      if (editSlot) setSlots(prev => prev.map(s => s.id === r.data.id ? r.data : s));
      else          setSlots(prev => [...prev, r.data]);
      setShowForm(false);
    }).catch(() => {});
  }

  function deleteSlot(id) {
    api.delete(`emploi-du-temps/${id}/`)
      .then(() => setSlots(prev => prev.filter(s => s.id !== id)))
      .catch(() => {});
  }

  const slotsByDay = JOURS_ORDER.reduce((acc, j) => {
    acc[j] = slots.filter(s => s.jour === j).sort((a,b) => a.heure_debut.localeCompare(b.heure_debut));
    return acc;
  }, {});

  const activeDays = JOURS_ORDER.filter(j => slotsByDay[j].length > 0);

  return (
    <div>
      <div className="ql-view-header">
        <h3 className="ql-view-title"><i className="bi bi-sticky me-2"></i>Notes & Organisation</h3>
      </div>

      {/* Tabs */}
      <div className="ql-result-tabs mb-4">
        <button className={`ql-result-tab${tab === "todo" ? " active" : ""}`} onClick={() => setTab("todo")}>
          <i className="bi bi-check2-square me-1"></i>Ma liste de tâches
          {pending.length > 0 && <span className="ql-result-tab-count">{pending.length}</span>}
        </button>
        <button className={`ql-result-tab${tab === "edt" ? " active" : ""}`} onClick={() => setTab("edt")}>
          <i className="bi bi-calendar-week me-1"></i>Emploi du temps
          {slots.length > 0 && <span className="ql-result-tab-count">{slots.length}</span>}
        </button>
      </div>

      {/* ── TODO ── */}
      {tab === "todo" && (
        <div>
          <form onSubmit={addTodo} className="ql-todo-add">
            <input
              className="form-control"
              placeholder="Ajouter une tâche…"
              value={todoText}
              onChange={e => setTodoText(e.target.value)}
            />
            <button type="submit" className="btn btn-primary" disabled={!todoText.trim()}>
              <i className="bi bi-plus-lg"></i>
            </button>
          </form>

          {todoLoad ? (
            <div className="text-center py-4"><div className="spinner-border text-primary spinner-border-sm"></div></div>
          ) : todos.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-check2-all fs-1 d-block mb-3" style={{ color: "#cbd5e1" }}></i>
              <p>Aucune tâche pour l'instant. Ajoute ta première tâche ci-dessus !</p>
            </div>
          ) : (
            <>
              {pending.length > 0 && (
                <div className="ql-todo-section">
                  <div className="ql-todo-section-label">À faire — {pending.length}</div>
                  {pending.map(t => (
                    <div key={t.id} className="ql-todo-item">
                      <button className="ql-todo-check" onClick={() => toggleTodo(t)} title="Marquer comme fait">
                        <i className="bi bi-circle"></i>
                      </button>
                      <span className="ql-todo-text">{t.text}</span>
                      <button className="ql-todo-del" onClick={() => deleteTodo(t.id)} title="Supprimer">
                        <i className="bi bi-trash3"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {completed.length > 0 && (
                <div className="ql-todo-section">
                  <div className="ql-todo-section-label" style={{ color: "#94a3b8" }}>Terminées — {completed.length}</div>
                  {completed.map(t => (
                    <div key={t.id} className="ql-todo-item ql-todo-done">
                      <button className="ql-todo-check done" onClick={() => toggleTodo(t)} title="Marquer comme non fait">
                        <i className="bi bi-check-circle-fill"></i>
                      </button>
                      <span className="ql-todo-text">{t.text}</span>
                      <button className="ql-todo-del" onClick={() => deleteTodo(t.id)} title="Supprimer">
                        <i className="bi bi-trash3"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── EMPLOI DU TEMPS ── */}
      {tab === "edt" && (
        <div>
          <div className="d-flex justify-content-end mb-3">
            <button className="btn btn-primary btn-sm" onClick={openNew}>
              <i className="bi bi-plus-lg me-1"></i>Ajouter un créneau
            </button>
          </div>

          {slotLoad ? (
            <div className="text-center py-4"><div className="spinner-border text-primary spinner-border-sm"></div></div>
          ) : slots.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-calendar-week fs-1 d-block mb-3" style={{ color: "#cbd5e1" }}></i>
              <p>Ton emploi du temps est vide.</p>
              <p className="small">Clique sur "Ajouter un créneau" pour commencer !</p>
            </div>
          ) : (
            <div className="ql-edt-grid">
              {activeDays.map(jour => (
                <div key={jour} className="ql-edt-day">
                  <div className="ql-edt-day-label">{JOURS_LABEL[jour]}</div>
                  {slotsByDay[jour].map(slot => (
                    <div key={slot.id} className="ql-edt-slot" style={{ borderLeftColor: slot.couleur }}>
                      <div className="ql-edt-slot-time" style={{ color: slot.couleur }}>
                        {slot.heure_debut.slice(0,5)} – {slot.heure_fin.slice(0,5)}
                      </div>
                      <div className="ql-edt-slot-label">{slot.label}</div>
                      <div className="ql-edt-slot-actions">
                        <button onClick={() => openEdit(slot)} title="Modifier"><i className="bi bi-pencil"></i></button>
                        <button onClick={() => deleteSlot(slot.id)} title="Supprimer"><i className="bi bi-trash3"></i></button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Modal form */}
          {showForm && (
            <div className="ql-modal-overlay" onClick={() => setShowForm(false)}>
              <div className="ql-modal" onClick={e => e.stopPropagation()}>
                <div className="ql-modal-header">
                  <span className="fw-bold">{editSlot ? "Modifier le créneau" : "Nouveau créneau"}</span>
                  <button className="ql-modal-close" onClick={() => setShowForm(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <form onSubmit={saveSlot} className="ql-modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>Jour</label>
                    <select className="form-select form-select-sm" value={form.jour} onChange={e => setForm(f => ({ ...f, jour: e.target.value }))}>
                      {JOURS_ORDER.map(j => <option key={j} value={j}>{JOURS_LABEL[j]}</option>)}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>Matière / Activité</label>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Ex: Mathématiques, Sport, Révisions…"
                      value={form.label}
                      onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>Début</label>
                      <input type="time" className="form-control form-control-sm" value={form.heure_debut}
                        onChange={e => setForm(f => ({ ...f, heure_debut: e.target.value }))} required />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>Fin</label>
                      <input type="time" className="form-control form-control-sm" value={form.heure_fin}
                        onChange={e => setForm(f => ({ ...f, heure_fin: e.target.value }))} required />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>Couleur</label>
                    <div className="ql-color-palette">
                      {PALETTE.map(c => (
                        <button
                          key={c}
                          type="button"
                          className={`ql-color-dot${form.couleur === c ? " selected" : ""}`}
                          style={{ background: c }}
                          onClick={() => setForm(f => ({ ...f, couleur: c }))}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="d-flex gap-2 justify-content-end">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowForm(false)}>Annuler</button>
                    <button type="submit" className="btn btn-sm btn-primary" disabled={!form.label.trim()}>
                      {editSlot ? "Enregistrer" : "Ajouter"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Calculatrice scientifique + Grapheur ─────────────

function calcFact(n) {
  n = Math.round(+n);
  if (!isFinite(n) || n < 0 || n > 170) return NaN;
  let r = 1; for (let i = 2; i <= n; i++) r *= i; return r;
}

// Remplace a^b par pow(a,b) pour éviter le conflit `-x**2` en JS strict
function replaceCarets(s) {
  // (expr)^n ou (expr)^(expr)
  s = s.replace(/\(([^()]+)\)\s*\^\s*(-?[\w.]+)/g, 'pow(($1),$2)');
  s = s.replace(/\(([^()]+)\)\s*\^\s*\(([^()]+)\)/g, 'pow(($1),($2))');
  // mot^n  (x^2, x^0.5, 2^n …)
  s = s.replace(/([\w.]+)\s*\^\s*(-?[\w.]+)/g, 'pow($1,$2)');
  return s;
}

function calcEval(raw, isRad) {
  try {
    const D = Math.PI / 180;
    const sc = {
      sin:   isRad ? Math.sin   : x => Math.sin(x * D),
      cos:   isRad ? Math.cos   : x => Math.cos(x * D),
      tan:   isRad ? Math.tan   : x => Math.tan(x * D),
      asin:  isRad ? Math.asin  : x => Math.asin(x) / D,
      acos:  isRad ? Math.acos  : x => Math.acos(x) / D,
      atan:  isRad ? Math.atan  : x => Math.atan(x) / D,
      sinh: Math.sinh, cosh: Math.cosh, tanh: Math.tanh,
      sqrt: Math.sqrt, cbrt: Math.cbrt, abs: Math.abs,
      log: Math.log10, ln: Math.log, log2: Math.log2, exp: Math.exp,
      floor: Math.floor, ceil: Math.ceil, round: Math.round, sign: Math.sign,
      pow: Math.pow, pi: Math.PI, e: Math.E,
    };
    const e = replaceCarets(raw)
      .replace(/÷/g, "/").replace(/×/g, "*")
      .replace(/(\d+(?:\.\d+)?)\s*!/g, (_, n) => String(calcFact(+n)));
    // eslint-disable-next-line no-new-func
    const v = new Function(...Object.keys(sc), `"use strict";return(${e})`)(...Object.values(sc));
    if (typeof v !== "number" || !isFinite(v)) return null;
    const a = Math.abs(v);
    if (a >= 1e15 || (a !== 0 && a < 1e-10)) return v.toExponential(6);
    return String(parseFloat(v.toPrecision(12)));
  } catch { return null; }
}

function calcEvalX(raw, x) {
  try {
    const sc = {
      x, sin: Math.sin, cos: Math.cos, tan: Math.tan,
      asin: Math.asin, acos: Math.acos, atan: Math.atan,
      sinh: Math.sinh, cosh: Math.cosh, tanh: Math.tanh,
      sqrt: Math.sqrt, cbrt: Math.cbrt, abs: Math.abs,
      log: Math.log10, ln: Math.log, log2: Math.log2, exp: Math.exp,
      floor: Math.floor, ceil: Math.ceil, round: Math.round, sign: Math.sign,
      pow: Math.pow, pi: Math.PI, e: Math.E,
    };
    const e = replaceCarets(raw)
      .replace(/(\d+(?:\.\d+)?)\s*!/g, (_, n) => String(calcFact(+n)));
    // eslint-disable-next-line no-new-func
    return new Function(...Object.keys(sc), `"use strict";return(${e})`)(...Object.values(sc));
  } catch { return NaN; }
}

function ScientificCalc() {
  const [expr,    setExpr]    = useState("");
  const [result,  setResult]  = useState("0");
  const [history, setHistory] = useState([]);
  const [memory,  setMemory]  = useState(null);
  const [isRad,   setIsRad]   = useState(true);
  const [afterEq, setAfterEq] = useState(false);
  const [err,     setErr]     = useState(false);

  function press(a) {
    setErr(false);
    if (a === "mode")  { setIsRad(r => !r); return; }
    if (a === "clear") { setExpr(""); setResult("0"); setAfterEq(false); return; }
    if (a === "back")  {
      if (afterEq) { setExpr(""); setResult("0"); setAfterEq(false); return; }
      const n = expr.slice(0, -1);
      setExpr(n); setResult(n || "0"); return;
    }
    if (a === "eval") {
      if (!expr) return;
      const r = calcEval(expr, isRad);
      if (r === null) { setErr(true); return; }
      setHistory(h => [{ expr, r }, ...h.slice(0, 9)]);
      setResult(r); setExpr(r); setAfterEq(true); return;
    }
    if (a === "neg") {
      const base = afterEq ? result : expr;
      if (!base) return;
      const r = calcEval(`-(${base})`, isRad);
      if (r !== null) { setExpr(r); setResult(r); setAfterEq(false); } return;
    }
    if (a === "mc")   { setMemory(null); return; }
    if (a === "mr")   {
      if (memory === null) return;
      const ms = String(memory);
      if (afterEq) { setExpr(ms); setResult(ms); setAfterEq(false); }
      else { const n = expr + ms; setExpr(n); setResult(n); } return;
    }
    if (a === "m+") {
      const v = parseFloat(afterEq ? result : calcEval(expr, isRad) ?? result);
      if (!isNaN(v)) setMemory(m => (m ?? 0) + v); return;
    }
    const OPS = ["+", "-", "*", "/", "^", "%"];
    let next;
    if (afterEq && !OPS.includes(a)) { next = a; setAfterEq(false); }
    else if (afterEq)                 { next = result + a; setAfterEq(false); }
    else                              { next = expr + a; }
    setExpr(next); setResult(next);
  }

  const ROWS = [
    [{ l: isRad?"RAD":"DEG",a:"mode",c:"fn mode"},{ l:"π",a:"pi",c:"fn"},{ l:"e",a:"e",c:"fn"},{ l:"C",a:"clear",c:"fn red"},{ l:"⌫",a:"back",c:"fn ora"}],
    [{ l:"sin",a:"sin(",c:"fn"},{ l:"cos",a:"cos(",c:"fn"},{ l:"tan",a:"tan(",c:"fn"},{ l:"(",a:"(",c:"fn"},{ l:")",a:")",c:"fn"}],
    [{ l:"asin",a:"asin(",c:"fn"},{ l:"acos",a:"acos(",c:"fn"},{ l:"atan",a:"atan(",c:"fn"},{ l:"x²",a:"^2",c:"fn"},{ l:"√",a:"sqrt(",c:"fn"}],
    [{ l:"log",a:"log(",c:"fn"},{ l:"ln",a:"ln(",c:"fn"},{ l:"exp",a:"exp(",c:"fn"},{ l:"xʸ",a:"^",c:"fn"},{ l:"n!",a:"!",c:"fn"}],
    [{ l:"7",a:"7",c:"d"},{ l:"8",a:"8",c:"d"},{ l:"9",a:"9",c:"d"},{ l:"÷",a:"/",c:"op"},{ l:"MC",a:"mc",c:"mem"}],
    [{ l:"4",a:"4",c:"d"},{ l:"5",a:"5",c:"d"},{ l:"6",a:"6",c:"d"},{ l:"×",a:"*",c:"op"},{ l:"MR",a:"mr",c:"mem"}],
    [{ l:"1",a:"1",c:"d"},{ l:"2",a:"2",c:"d"},{ l:"3",a:"3",c:"d"},{ l:"−",a:"-",c:"op"},{ l:"M+",a:"m+",c:"mem"}],
    [{ l:"±",a:"neg",c:"d"},{ l:"0",a:"0",c:"d"},{ l:".",a:".",c:"d"},{ l:"+",a:"+",c:"op"},{ l:"=",a:"eval",c:"eq"}],
  ];

  return (
    <div className="ql-calc-layout">
      <div className="ql-calc-box">
        <div className={`ql-calc-screen${err ? " err" : ""}`}>
          <div className="ql-calc-screen-top">
            <span className="ql-calc-badge-mode">{isRad ? "RAD" : "DEG"}</span>
            {memory !== null && (
              <span className="ql-calc-badge-mem">M = {parseFloat((+memory).toPrecision(8))}</span>
            )}
          </div>
          <div className="ql-calc-expr-line">{!afterEq && expr}</div>
          <div className="ql-calc-val-line">{err ? "Erreur de syntaxe" : result}</div>
        </div>
        <div className="ql-calc-grid">
          {ROWS.map((row, ri) =>
            row.map((btn, bi) => (
              <button key={`${ri}-${bi}`} className={`ql-calc-btn ${btn.c}`} onClick={() => press(btn.a)}>
                {btn.l}
              </button>
            ))
          )}
        </div>
      </div>

      {history.length > 0 && (
        <div className="ql-calc-hist">
          <div className="ql-calc-hist-title"><i className="bi bi-clock-history me-1"></i>Historique</div>
          {history.map((h, i) => (
            <div key={i} className="ql-calc-hist-item"
              onClick={() => { setExpr(h.r); setResult(h.r); setAfterEq(true); setErr(false); }}>
              <span className="ql-calc-hist-expr">{h.expr}</span>
              <span className="ql-calc-hist-res">= {h.r}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const GRAPH_COLORS = ["#0d6efd", "#dc2626", "#198754"];
const GRAPH_LABELS = ["f₁(x)", "f₂(x)", "f₃(x)"];
const GRAPH_EXAMPLES = [
  { label: "sin(x)",        funcs: ["sin(x)", "", ""] },
  { label: "x²",            funcs: ["x^2", "", ""] },
  { label: "e^(−x²)",      funcs: ["exp(-x^2)", "", ""] },
  { label: "1/x",           funcs: ["1/x", "", ""] },
  { label: "sin + cos",     funcs: ["sin(x)", "cos(x)", ""] },
  { label: "√x",           funcs: ["sqrt(x)", "", ""] },
];

function FunctionGrapher() {
  const [funcs,    setFuncs]    = useState(["sin(x)", "", ""]);
  const [xMin,     setXMin]     = useState("-10");
  const [xMax,     setXMax]     = useState("10");
  const [plotData, setPlotData] = useState(null);
  const [plotKey,  setPlotKey]  = useState(0);
  const [funcErrs, setFuncErrs] = useState([false, false, false]);

  function plot() {
    const xmin = parseFloat(xMin), xmax = parseFloat(xMax);
    if (isNaN(xmin) || isNaN(xmax) || xmin >= xmax) return;
    const N = 600, step = (xmax - xmin) / N;
    const errs = [false, false, false];
    const datasets = funcs.map((f, i) => {
      if (!f.trim()) return null;
      const pts = [];
      for (let xi = xmin; xi <= xmax + step / 2; xi += step) {
        const y = calcEvalX(f, xi);
        pts.push({ x: parseFloat(xi.toPrecision(10)), y: (isFinite(y) && !isNaN(y)) ? y : null });
      }
      if (pts.every(p => p.y === null)) { errs[i] = true; return null; }
      return {
        label: `${GRAPH_LABELS[i]} = ${f}`,
        data: pts,
        borderColor: GRAPH_COLORS[i],
        backgroundColor: GRAPH_COLORS[i] + "18",
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0,
        spanGaps: false,
      };
    }).filter(Boolean);
    setFuncErrs(errs);
    setPlotData({ datasets });
    setPlotKey(k => k + 1);
  }

  const chartOpts = {
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    scales: {
      x: {
        type: "linear",
        position: "center",
        grid: { color: "#e2e8f0" },
        ticks: { font: { size: 11 }, maxTicksLimit: 10, color: "#475569" },
        border: { color: "#475569", width: 2 },
      },
      y: {
        type: "linear",
        position: "center",
        grid: { color: "#e2e8f0" },
        ticks: { font: { size: 11 }, maxTicksLimit: 8, color: "#475569" },
        border: { color: "#475569", width: 2 },
      },
    },
    plugins: {
      legend: { labels: { font: { size: 12 }, boxWidth: 20 } },
      tooltip: {
        callbacks: {
          label: ctx => `${ctx.dataset.label.split("=")[0].trim()}: (${ctx.parsed.x?.toFixed(3)}, ${ctx.parsed.y?.toFixed(4) ?? "—"})`,
        },
      },
    },
  };

  return (
    <div className="ql-graph-wrap">
      <div className="ql-graph-controls">
        {funcs.map((f, i) => (
          <div key={i} className="ql-graph-func-row">
            <span className="ql-graph-func-lbl" style={{ color: GRAPH_COLORS[i] }}>{GRAPH_LABELS[i]} =</span>
            <input
              className={`form-control form-control-sm${funcErrs[i] ? " is-invalid" : ""}`}
              placeholder={i === 0 ? "Ex : sin(x), x^2, exp(-x^2)…" : "Optionnel…"}
              value={f}
              onChange={e => setFuncs(fs => fs.map((v, j) => j === i ? e.target.value : v))}
              onKeyDown={e => e.key === "Enter" && plot()}
            />
          </div>
        ))}

        <div className="ql-graph-range">
          <label className="ql-graph-range-lbl">x min</label>
          <input type="number" className="form-control form-control-sm" value={xMin} onChange={e => setXMin(e.target.value)} />
          <label className="ql-graph-range-lbl">x max</label>
          <input type="number" className="form-control form-control-sm" value={xMax} onChange={e => setXMax(e.target.value)} />
          <button className="btn btn-primary btn-sm" onClick={plot}>
            <i className="bi bi-graph-up me-1"></i>Tracer
          </button>
        </div>

        <div className="ql-graph-examples">
          {GRAPH_EXAMPLES.map((ex, i) => (
            <button key={i} className="ql-graph-ex" onClick={() => { setFuncs(ex.funcs); }}>
              {ex.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ql-graph-canvas">
        {plotData ? (
          <Line key={plotKey} data={plotData} options={chartOpts} />
        ) : (
          <div className="ql-graph-empty">
            <i className="bi bi-graph-up fs-1"></i>
            <p className="mt-2 text-muted">Entre une fonction et clique sur <strong>Tracer</strong></p>
          </div>
        )}
      </div>
    </div>
  );
}

function CalculatriceView() {
  const [tab, setTab] = useState("calc");
  return (
    <div>
      <div className="ql-view-header">
        <h3 className="ql-view-title"><i className="bi bi-calculator me-2"></i>Calculatrice scientifique</h3>
      </div>
      <div className="ql-result-tabs mb-4">
        <button className={`ql-result-tab${tab === "calc" ? " active" : ""}`} onClick={() => setTab("calc")}>
          <i className="bi bi-calculator me-1"></i>Calculatrice
        </button>
        <button className={`ql-result-tab${tab === "graph" ? " active" : ""}`} onClick={() => setTab("graph")}>
          <i className="bi bi-graph-up me-1"></i>Tracer des fonctions
        </button>
      </div>
      {tab === "calc"  && <ScientificCalc />}
      {tab === "graph" && <FunctionGrapher />}
    </div>
  );
}

function FloatingCalc({ onClose }) {
  const [pos, setPos] = useState({ x: Math.max(0, window.innerWidth - 420), y: 80 });
  const drag = useRef(null);

  const onMouseDown = (e) => {
    drag.current = { startX: e.clientX - pos.x, startY: e.clientY - pos.y };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };
  const onMouseMove = (e) => {
    if (!drag.current) return;
    setPos({ x: e.clientX - drag.current.startX, y: e.clientY - drag.current.startY });
  };
  const onMouseUp = () => {
    drag.current = null;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  };

  return (
    <div className="ql-float-calc" style={{ left: pos.x, top: pos.y }}>
      <div className="ql-float-calc-header" onMouseDown={onMouseDown}>
        <span><i className="bi bi-calculator me-2"></i>Calculatrice</span>
        <button className="ql-float-calc-close" onClick={onClose} title="Fermer">✕</button>
      </div>
      <div className="ql-float-calc-body">
        <ScientificCalc />
      </div>
    </div>
  );
}

// ── Formules utiles (vue élève) ────────────────────
// PDF d'une formule : copie de l'appareil si elle a été gardée, sinon visionneuse en ligne.
function FormulePdf({ formule: f }) {
  const local = useFichierHorsConnexion(`formule:${f.id}`);
  const [ouvert, setOuvert] = useState(false);
  if (local) {
    return (
      <>
        <button type="button" className="ql-formule-pdf-btn border-0" onClick={() => setOuvert(true)}>
          <i className="bi bi-file-earmark-pdf-fill me-1"></i>Voir le PDF
        </button>
        {ouvert && <PdfLocal plein adresse={local.adresse} titre={f.titre} onClose={() => setOuvert(false)} />}
      </>
    );
  }
  return (
    <DownloadLink url={f.pdf_url} filename={`${f.titre || "formule"}.pdf`} mimeType="application/pdf" downloadOnWeb={false} className="ql-formule-pdf-btn">
      <i className="bi bi-file-earmark-pdf-fill me-1"></i>Voir le PDF
    </DownloadLink>
  );
}

function FormulesView() {
  const [formules,  setFormules]  = useState([]);
  const [modules,   setModules]   = useState([]);
  const [moduleId,  setModuleId]  = useState("");
  const [loading,   setLoading]   = useState(true);
  const [expanded,  setExpanded]  = useState({});
  const [lightbox,  setLightbox]  = useState(null); // URL de l'image agrandie

  useEffect(() => {
    getAvecCache("modules/").then(r => setModules(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = moduleId ? `?module=${moduleId}` : "";
    getAvecCache(`formules/${params}`).then(r => setFormules(r.data)).catch(() => []).finally(() => setLoading(false));
  }, [moduleId]);

  const toggleVideo = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const ytId = (url) => {
    if (!url) return null;
    let m = url.match(/[?&]v=([^?&\s]+)/);
    if (m) return m[1];
    m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:shorts|embed|v)\/)([^?&\s]+)/);
    return m ? m[1] : null;
  };

  return (
    <div className="ql-formules-wrap">
      {/* Lightbox plein écran */}
      {lightbox && (
        <div className="ql-lightbox" onClick={() => setLightbox(null)}>
          <button className="ql-lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <img src={lightbox} alt="Aperçu" className="ql-lightbox-img" onClick={e => e.stopPropagation()} />
        </div>
      )}
      <div className="ql-formules-header">
        <h2 className="ql-formules-title"><i className="bi bi-infinity me-2 text-primary"></i>Formules utiles</h2>
        <select className="form-select form-select-sm ql-formules-filter" value={moduleId} onChange={e => setModuleId(e.target.value)}>
          <option value="">Tous les modules</option>
          {modules.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      ) : formules.length === 0 ? (
        <div className="ql-wip">
          <i className="bi bi-infinity ql-wip-icon"></i>
          <p className="text-muted">Aucune formule disponible pour le moment.</p>
        </div>
      ) : (
        <div className="ql-formules-grid">
          {formules.map(f => (
            <div key={f.id} className="ql-formule-card">
              <div className="ql-formule-card-top">
                <span className="ql-formule-titre">{f.titre}</span>
                {f.module_titre && <span className="ql-formule-module-badge">{f.module_titre}</span>}
              </div>

              {/* Formule principale rendue en LaTeX */}
              <div className="ql-formule-content">
                <MathText text={f.contenu} raw />
              </div>

              {/* Commentaire */}
              {f.commentaire && <p className="ql-formule-comment" style={{ whiteSpace: "pre-wrap" }}>{f.commentaire}</p>}

              {/* Image */}
              {f.image_url && (
                <img
                  src={f.image_url}
                  alt={f.titre}
                  className="ql-formule-img"
                  style={{ cursor: "zoom-in" }}
                  onClick={() => setLightbox(f.image_url)}
                />
              )}

              {/* Vidéo YouTube */}
              {f.video_url && ytId(f.video_url) && (
                <div className="ql-formule-video-wrap">
                  {expanded[f.id] ? (
                    <div className="ql-formule-video-embed">
                      <iframe
                        src={urlLecteurYoutube(ytId(f.video_url))}
                        title={f.titre}
                        frameBorder="0"
                        allow={AUTORISATIONS_LECTEUR}
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <div className="ql-formule-video-thumb" onClick={() => toggleVideo(f.id)}
                      style={{ backgroundImage: `url(https://img.youtube.com/vi/${ytId(f.video_url)}/hqdefault.jpg)` }}>
                      <div className="ql-formule-play-btn"><i className="bi bi-play-circle-fill"></i></div>
                    </div>
                  )}
                </div>
              )}

              {/* PDF */}
              {f.pdf_url && (
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <FormulePdf formule={f} />
                  <BoutonHorsConnexion compact cle={`formule:${f.id}`} url={f.pdf_url} titre={f.titre} rubrique="formules" details={{ module: f.module_titre }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WIPView({ label }) {
  return (
    <div className="ql-wip">
      <i className="bi bi-tools ql-wip-icon"></i>
      <h4>{label}</h4>
      <p>Cette section est en cours de développement. Revenez bientôt !</p>
    </div>
  );
}

// ── NavSection ─────────────────────────────────────
function NavSection({ label, items, activeView, onNav, compteurs = {} }) {
  return (
    <>
      <div className="ql-sb-section-label">{label}</div>
      <nav className="ql-sb-nav">
        {items.map((item) => (
          <button
            key={item.label}
            className={`ql-sb-link${activeView === item.view ? " active" : ""}`}
            data-visite={item.view}
            onClick={() => onNav(item.view)}
          >
            <i className={`bi ${item.icon}`}></i>
            <span>{item.label}</span>
            <Pastille nombre={item.compteur ? compteurs[item.compteur] : 0} />
          </button>
        ))}
      </nav>
    </>
  );
}

// ── ModulesView ────────────────────────────────────
function ModulesView({ modules, onOpen }) {
  return (
    <div>
      <div className="ql-view-header">
        <h3 className="ql-view-title"><i className="bi bi-book me-2"></i>Modules</h3>
        <span className="ql-view-count">{modules.length} module{modules.length !== 1 ? "s" : ""}</span>
      </div>
      {modules.length === 0
        ? <EmptyState icon="bi-book-half" text="Aucun module disponible pour le moment." />
        : (
          <div className="ql-ch-grid">
            {modules.map((mod, i) => (
              <div key={mod.id} className="ql-ch-card" onClick={() => onOpen(mod)}>
                <div className="ql-ch-card-num" style={{ background: chColor(i) }}>
                  M-{String(mod.order || i + 1).padStart(2, "0")}
                </div>
                <div className="ql-ch-card-body">
                  <div className="ql-ch-card-title">{mod.title}</div>
                  {mod.description && <div className="ql-ch-card-desc">{mod.description}</div>}
                  <div className="ql-ch-card-meta mt-2">
                    {(mod.lecons?.length > 0) && (
                      <span><i className="bi bi-file-text me-1"></i>{mod.lecons.length} leçon{mod.lecons.length > 1 ? "s" : ""}</span>
                    )}
                    {(mod.video_count > 0) && (
                      <span><i className="bi bi-play-circle me-1"></i>{mod.video_count} vidéo{mod.video_count > 1 ? "s" : ""}</span>
                    )}
                    {(mod.qcm_count > 0) && (
                      <span><i className="bi bi-grid me-1"></i>{mod.qcm_count} QCM</span>
                    )}
                    {(mod.exercice_count > 0) && (
                      <span><i className="bi bi-pencil me-1"></i>{mod.exercice_count} exercice{mod.exercice_count > 1 ? "s" : ""}</span>
                    )}
                    {(mod.audio_count > 0) && (
                      <span><i className="bi bi-headphones me-1"></i>{mod.audio_count} audio{mod.audio_count > 1 ? "s" : ""}</span>
                    )}
                  </div>
                </div>
                <div className="ql-ch-card-footer">
                  <button className="btn btn-sm btn-primary w-100" onClick={(e) => { e.stopPropagation(); onOpen(mod); }}>
                    <i className="bi bi-play-fill me-1"></i>Ouvrir
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ── LeconsView ─────────────────────────────────────
function LeconsView({ modules, onOpenModule }) {
  const allLecons = modules.flatMap(mod =>
    (mod.lecons || []).map(l => ({ ...l, moduleTitre: mod.title, moduleId: mod.id, moduleOrder: mod.order }))
  );
  return (
    <div>
      <div className="ql-view-header">
        <h3 className="ql-view-title"><i className="bi bi-file-text me-2"></i>Leçons</h3>
        <span className="ql-view-count">{allLecons.length} leçon{allLecons.length !== 1 ? "s" : ""}</span>
      </div>
      {allLecons.length === 0
        ? <EmptyState icon="bi-file-text" text="Aucune leçon disponible pour le moment." />
        : (
          <div className="ql-lecon-list">
            {allLecons.map((l, i) => (
              <div key={l.id} className="ql-lecon-row">
                <div className="ql-lecon-icon" style={{ background: chColor(i) }}>
                  <i className="bi bi-file-earmark-text-fill"></i>
                </div>
                <div className="ql-lecon-body">
                  <div className="ql-lecon-title"><PointNouveauContenu type="lecons" id={l.id} />{l.title}</div>
                  <ActivityStatus kind="lecon" id={l.id} />
                  <div className="ql-lecon-module">
                    <i className="bi bi-book me-1"></i>Module {l.moduleOrder} — {l.moduleTitre}
                  </div>
                </div>
                {l.pdf_url && (
                  <button className="btn btn-sm btn-outline-primary" onClick={() => {
                    const fullMod = modules.find(m => m.id === l.moduleId);
                    if (fullMod) onOpenModule({ ...fullMod, _openLeconId: l.id });
                  }}>
                    <i className="bi bi-eye me-1"></i><ActivityStatus kind="lecon" id={l.id} action fallback="Lire" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ── VideoCard ──────────────────────────────────────
function VideoCard({ v, isPlaying, onPlay, onProgression, chapterLabel }) {
  const ytId = getYouTubeId(v.youtube_url);
  const [replay, setReplay] = useState(0);
  const suivi = useActivity("video", v.id);
  const depart = suivi && (suivi.percent ?? 100) < 100 ? (suivi.position_seconds || 0) : 0;
  useEffect(() => { if (isPlaying) marquerOuvert("videos", v.id); }, [isPlaying, v.id]);

  return (
    <div className="ql-video-card">
      {isPlaying ? (
        <>
          {!ytId ? (
            <div className="ql-video-error">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <p>Lien YouTube invalide.</p>
            </div>
          ) : (
            <div className="ql-video-embed" key={replay}>
              <VideoSuivie videoId={v.id} ytId={ytId} titre={v.title} depart={depart} onProgression={onProgression} />
            </div>
          )}
        </>
      ) : (
        <div
          className="ql-video-thumb"
          onClick={onPlay}
          style={{ backgroundImage: ytId ? `url(https://img.youtube.com/vi/${ytId}/hqdefault.jpg)` : "none" }}
        >
          {!ytId && <i className="bi bi-camera-video ql-video-no-thumb"></i>}
          <div className="ql-video-play-btn"><i className="bi bi-play-circle-fill"></i></div>
        </div>
      )}
      <div className="ql-video-info">
        <div className="ql-video-title"><PointNouveauContenu type="videos" id={v.id} />{v.title}</div>
                  <ActivityStatus kind="video" id={v.id} />
        {chapterLabel && <div className="ql-video-chapter"><i className="bi bi-book me-1"></i>{chapterLabel}</div>}
        {v.duration && <div className="ql-video-duration"><i className="bi bi-clock me-1"></i>{v.duration}</div>}
        {v.description && <div className="ql-video-desc">{v.description}</div>}
        <button className="btn btn-sm btn-outline-primary mt-2" onClick={() => { setReplay(n => n + 1); onPlay?.(); }}><ActivityStatus kind="video" id={v.id} action fallback="Voir la vidéo" /></button>
      </div>
    </div>
  );
}

// ── PDFViewer ──────────────────────────────────────
// Utilise Google Docs Viewer en iframe — le même mécanisme déjà utilisé
// pour les devoirs/évaluations (fonctionne de façon fiable sur Android et
// iPhone). Google récupère et rend le PDF côté serveur ; le navigateur du
// téléphone n'a donc plus besoin de parser/streamer le fichier lui-même,
// ce qui contourne les soucis d'affichage/chargement rencontrés avec un
// rendu 100% client (iframe brute ou react-pdf) sur certains appareils.
function PDFViewer({ url, title, cleHorsConnexion, rubrique = "lecons", details }) {
  const [fullscreen, setFullscreen] = useState(false);
  const local = useFichierHorsConnexion(cleHorsConnexion);
  const enLigne = useEnLigne();

  if (!url && !local) return null;

  // Copie gardée sur l'appareil : lue sans réseau, sans visionneuse externe.
  if (local || !enLigne) {
    return (
      <div className="ql-pdf-wrap">
        <div className="ql-pdf-toolbar">
          <span className="ql-pdf-name">
            <i className="bi bi-file-earmark-pdf-fill me-2 text-danger"></i>{title}
          </span>
          <div className="d-flex align-items-center gap-2">
            {cleHorsConnexion && <BoutonHorsConnexion cle={cleHorsConnexion} url={url} titre={title} rubrique={rubrique} details={details} variante="btn-outline-light" />}
            {local && (
              <button className="btn btn-sm btn-light" onClick={() => setFullscreen(true)}>
                <i className="bi bi-arrows-fullscreen me-1"></i>Plein écran
              </button>
            )}
          </div>
        </div>
        {local
          ? <>
              {fullscreen && <PdfLocal plein adresse={local.adresse} titre={title} onClose={() => setFullscreen(false)} />}
              <div className="ql-pdf-body"><PdfLocal adresse={local.adresse} titre={title} /></div>
            </>
          : <div className="ql-hc-absent"><i className="bi bi-wifi-off"></i>Pas de réseau : cette leçon n'a pas été gardée sur l'appareil.</div>}
      </div>
    );
  }

  return (
    <div className="ql-pdf-wrap">
      <div className="ql-pdf-toolbar">
        <span className="ql-pdf-name">
          <i className="bi bi-file-earmark-pdf-fill me-2 text-danger"></i>{title}
        </span>
        <div className="d-flex align-items-center gap-2">
          {cleHorsConnexion && <BoutonHorsConnexion cle={cleHorsConnexion} url={url} titre={title} rubrique={rubrique} details={details} variante="btn-outline-light" />}
          <DownloadLink url={url} filename={`${title || "document"}.pdf`} mimeType="application/pdf" className="btn btn-sm btn-outline-light">
            <i className="bi bi-download me-1"></i>Télécharger
          </DownloadLink>
          <button className="btn btn-sm btn-light" onClick={() => setFullscreen(true)}>
            <i className="bi bi-arrows-fullscreen me-1"></i>Plein écran
          </button>
        </div>
      </div>
      {fullscreen && <DocumentViewer titre={title} url={url} onClose={() => setFullscreen(false)} />}
      <div className="ql-pdf-body">
        <iframe
          src={`https://docs.google.com/viewer?url=${encodeURIComponent(adresseComplete(url))}&embedded=true`}
          title={title}
          style={{ width: "100%", height: "100%", minHeight: "600px", border: "none" }}
        />
      </div>
    </div>
  );
}

// ── ModuleDetail ───────────────────────────────────
function ModuleDetail({ module, allModules, onBack, onOpen, onSolve, onPlay, onWatched, onReady }) {
  const [tab, setTab]         = useState("lecons");
  const [videos, setVideos]   = useState([]);
  const [exercises, setExercises] = useState([]);
  const [qcms, setQcms]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [activeLecon, setActiveLecon]   = useState(null);

  const idx   = allModules.findIndex(c => c.id === module.id);
  const color = chColor(idx);
  const prevCh = idx > 0                     ? allModules[idx - 1] : null;
  const nextCh = idx < allModules.length - 1 ? allModules[idx + 1] : null;

  useEffect(() => {
    setLoading(true);
    setPlayingVideo(null);
    setTab(module._openVideoId ? "videos" : (module._returnTab || "lecons"));
    setActiveLecon(null);
    Promise.allSettled([
      api.get(`videos/?module=${module.id}`),
      api.get(`exercices/?module=${module.id}`),
      api.get(`qcms/?module=${module.id}&sans_reponses=1`),
    ]).then(([vR, eR, qR]) => {
      if (vR.status === "fulfilled") {
        const loadedVideos = Array.isArray(vR.value.data)
          ? vR.value.data
          : (vR.value.data?.results || []);
        const requestedVideoId = Number(module._openVideoId);
        const requestedVideo = loadedVideos.find(video => video.id === requestedVideoId);
        setVideos(requestedVideo
          ? [requestedVideo, ...loadedVideos.filter(video => video.id !== requestedVideoId)]
          : loadedVideos);
        if (requestedVideo) {
          setTab("videos");
          setPlayingVideo(requestedVideo.id);
        }
      }
      if (eR.status === "fulfilled") setExercises(eR.value.data);
      if (qR.status === "fulfilled") setQcms(qR.value.data);
    }).finally(() => {
      setLoading(false);
      if (module._openLeconId) {
        const lecon = (module.lecons || []).find(l => l.id === module._openLeconId);
        if (lecon) {
          setActiveLecon(lecon);
          marquerOuvert("lecons", lecon.id); api.post("leconprogress/", { lecon: lecon.id }).then(() => { if (onWatched) onWatched(); }).catch(() => {});
        }
      }
    });
  }, [module.id, module._openLeconId, module._openVideoId, module._returnTab, onWatched]);

  useEffect(() => { if (!loading) onReady?.(); }, [loading, onReady]);

  const lecons = module.lecons || [];

  const TABS = [
    { key: "lecons",     icon: "bi-file-text",   label: `Leçons${lecons.length ? ` (${lecons.length})` : ""}` },
    { key: "videos",     icon: "bi-play-circle",  label: `Vidéos${videos.length ? ` (${videos.length})` : ""}` },
    { key: "audios",     icon: "bi-headphones",   label: `Audios${module.audio_count ? ` (${module.audio_count})` : ""}` },
    { key: "exercices",  icon: "bi-pencil",       label: `Exercices${exercises.length ? ` (${exercises.length})` : ""}` },
    { key: "qcm",        icon: "bi-grid",         label: `QCM${qcms.length ? ` (${qcms.length})` : ""}` },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <div className="ql-breadcrumb">
        <button className="ql-bc-link" onClick={onBack}>
          <i className="bi bi-chevron-left me-1"></i>Modules
        </button>
        <i className="bi bi-chevron-right ql-bc-sep"></i>
        <span className="ql-bc-current">{module.title}</span>
      </div>

      {/* Header */}
      <div className="ql-detail-header" style={{ borderLeftColor: color }}>
        <div className="ql-detail-num" style={{ background: color }}>
          M-{String(module.order || idx + 1).padStart(2, "0")}
        </div>
        <div className="flex-fill">
          <h3 className="ql-detail-title">{module.title}</h3>
          {module.description && <p className="ql-detail-desc">{module.description}</p>}
        </div>
      </div>

      {/* Tabs */}
      <div className="ql-detail-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`ql-detail-tab${tab === t.key ? " active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            <i className={`bi ${t.icon} me-1`}></i>{t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-5 text-muted">
          <div className="spinner-border spinner-border-sm me-2"></div>Chargement...
        </div>
      ) : (
        <div className="ql-detail-content">
          {/* ── Leçons ── */}
          {tab === "lecons" && (
            lecons.length === 0
              ? <EmptyState icon="bi-file-text" text="Aucune leçon disponible pour ce module." />
              : activeLecon
                ? (
                  <div>
                    <button className="btn btn-sm btn-outline-secondary mb-3" onClick={() => setActiveLecon(null)}>
                      <i className="bi bi-arrow-left me-1"></i>Liste des leçons
                    </button>
                    <PDFViewer url={activeLecon.pdf_url} title={activeLecon.title} cleHorsConnexion={`lecon:${activeLecon.id}`} details={{ module: module.title }} />
                  </div>
                )
                : (
                  <div className="ql-lecon-list">
                    {lecons.map((l, i) => (
                      <div key={l.id} className="ql-lecon-row">
                        <div className="ql-lecon-icon" style={{ background: chColor(i) }}>
                          <i className="bi bi-file-earmark-text-fill"></i>
                        </div>
                        <div className="ql-lecon-body">
                          <div className="ql-lecon-title"><PointNouveauContenu type="lecons" id={l.id} />{l.title}</div>
                  <ActivityStatus kind="lecon" id={l.id} />
                          {l.description && <div className="ql-lecon-module">{l.description}</div>}
                        </div>
                        {l.pdf_url && <BoutonHorsConnexion compact cle={`lecon:${l.id}`} url={l.pdf_url} titre={l.title} rubrique="lecons" details={{ module: module.title }} className="me-1" />}
                        {l.pdf_url
                          ? <button className="btn btn-sm btn-primary" onClick={() => {
                              setActiveLecon(l);
                              marquerOuvert("lecons", l.id); api.post("leconprogress/", { lecon: l.id }).then(() => { if (onWatched) onWatched(); }).catch(() => {});
                            }}>
                              <i className="bi bi-eye me-1"></i><ActivityStatus kind="lecon" id={l.id} action fallback="Lire" />
                            </button>
                          : <span className="text-muted small">PDF non disponible</span>
                        }
                      </div>
                    ))}
                  </div>
                )
          )}

          {/* ── Vidéos ── */}
          {tab === "videos" && (
            videos.length === 0
              ? <EmptyState icon="bi-camera-video" text="Aucune vidéo disponible pour ce module." />
              : (
                <div className="ql-video-grid">
                  {videos.map(v => (
                    <VideoCard
                      key={v.id}
                      v={v}
                      isPlaying={playingVideo === v.id}
                      onPlay={() => setPlayingVideo(v.id)}
                      onProgression={onWatched}
                    />
                  ))}
                </div>
              )
          )}

          {/* ── Cours audio ── */}
          {tab === "audios" && <CoursAudio moduleId={module.id} onProgression={onWatched} />}

          {/* ── Exercices ── */}
          {tab === "exercices" && (
            exercises.length === 0
              ? <EmptyState icon="bi-pencil-square" text="Aucun exercice disponible pour ce module." />
              : (
                <div className="ql-exercise-list">
                  {exercises.map((ex, i) => (
                    <div key={ex.id} className="ql-exercise-card">
                      <div className="ql-ex-num" style={{ background: color }}>{i + 1}</div>
                      <div className="ql-ex-body">
                        <div className="ql-ex-title"><PointNouveauContenu type="exercices" id={ex.id} />{ex.title}</div>
                  <ActivityStatus kind="exercice" id={ex.id} />
                        {ex.pedagogical_type && (
                          <span className="ql-ex-type-badge">{ex.get_pedagogical_type_display || ex.pedagogical_type}</span>
                        )}
                        {ex.description && <FormattedText text={ex.description} className="ql-ex-desc" />}
                        {ex.image_url && (
                          <img src={ex.image_url} alt="" className="ql-ex-image" />
                        )}
                      </div>
                      <button className="btn btn-sm btn-primary flex-shrink-0" onClick={() => onSolve && onSolve(ex, "chapitre")}>
                        <i className="bi bi-pencil-square me-1"></i><ActivityStatus kind="exercice" id={ex.id} action fallback="Résoudre" />
                      </button>
                    </div>
                  ))}
                </div>
              )
          )}

          {/* ── QCM ── */}
          {tab === "qcm" && (
            qcms.length === 0
              ? <EmptyState icon="bi-grid" text="Aucun QCM disponible pour ce module." />
              : (
                <div className="ql-exercise-list">
                  {qcms.map((q, i) => (
                    <div key={q.id} className="ql-exercise-card">
                      <div className="ql-ex-num" style={{ background: "#6f42c1" }}>{i + 1}</div>
                      <div className="ql-ex-body">
                        <div className="ql-ex-title">{q.title}</div>
                  <ActivityStatus kind="qcm" id={q.id} />
                        {q.questions && <div style={{ fontSize: "0.8rem", color: "#64748b" }}>{q.questions.length} question{q.questions.length !== 1 ? "s" : ""}</div>}
                        {q.description && <div className="ql-ex-desc">{q.description}</div>}
                      </div>
                      <button className="btn btn-sm btn-outline-purple flex-shrink-0" onClick={() => onPlay && onPlay(q, "chapitre")}>
                        <i className="bi bi-play-fill me-1"></i><ActivityStatus kind="qcm" id={q.id} action fallback="Commencer" />
                      </button>
                    </div>
                  ))}
                </div>
              )
          )}
        </div>
      )}

      {/* Prev / Next */}
      <div className="ql-ch-nav-btns">
        {prevCh
          ? <button className="btn btn-outline-secondary btn-sm" onClick={() => onOpen(prevCh)}>
              <i className="bi bi-chevron-left me-1"></i>{prevCh.title}
            </button>
          : <div />}
        {nextCh
          ? <button className="btn btn-primary btn-sm" onClick={() => onOpen(nextCh)}>
              {nextCh.title}<i className="bi bi-chevron-right ms-1"></i>
            </button>
          : <div />}
      </div>
    </div>
  );
}

// ── VideosView ─────────────────────────────────────
function VideosView({ chapters, onWatched }) {
  const [videos, setVideos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("videos/").then(r => setVideos(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const chapLabel = (id) => chapters.find(c => c.id === id)?.title || "";

  if (loading) return <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2"></div>Chargement...</div>;

  return (
    <div>
      <div className="ql-view-header">
        <h3 className="ql-view-title"><i className="bi bi-play-circle me-2"></i>Vidéos</h3>
        <span className="ql-view-count">{videos.length} vidéo{videos.length !== 1 ? "s" : ""}</span>
      </div>
      {videos.length === 0
        ? <EmptyState icon="bi-camera-video" text="Aucune vidéo disponible." />
        : (
          <div className="ql-video-grid">
            {videos.map(v => (
              <VideoCard key={v.id} v={v} isPlaying={playing === v.id} onPlay={() => setPlaying(v.id)} onProgression={onWatched} chapterLabel={chapLabel(v.module)} />
            ))}
          </div>
        )}
    </div>
  );
}

// ── ExercicesView ──────────────────────────────────
function ExercicesView({ chapters, onSolve, onReady }) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => { if (!loading) onReady?.(); }, [loading, onReady]);

  useEffect(() => {
    api.get("exercices/").then(r => setExercises(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const chapLabel = (id) => chapters.find(c => c.id === id)?.title || "";

  if (loading) return <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2"></div>Chargement...</div>;

  return (
    <div>
      <div className="ql-view-header">
        <h3 className="ql-view-title"><i className="bi bi-pencil me-2"></i>Exercices</h3>
        <span className="ql-view-count">{exercises.length} exercice{exercises.length !== 1 ? "s" : ""}</span>
      </div>
      {exercises.length === 0
        ? <EmptyState icon="bi-pencil-square" text="Aucun exercice disponible." />
        : (
          <div className="ql-exercise-list">
            {exercises.map((ex, i) => (
              <div key={ex.id} className="ql-exercise-card">
                <div className="ql-ex-num" style={{ background: chColor(i) }}>{i + 1}</div>
                <div className="ql-ex-body">
                  <div className="ql-ex-title"><PointNouveauContenu type="exercices" id={ex.id} />{ex.title}</div>
                  <ActivityStatus kind="exercice" id={ex.id} />
                  {chapLabel(ex.module) && <div style={{ fontSize: "0.8rem", color: "#64748b" }}><i className="bi bi-book me-1"></i>{chapLabel(ex.module)}</div>}
                  {ex.description && <FormattedText text={ex.description} className="ql-ex-desc" />}
                  {ex.image_url && (
                    <img src={ex.image_url} alt="" className="ql-ex-image" />
                  )}
                </div>
                <button className="btn btn-sm btn-primary flex-shrink-0" onClick={() => onSolve && onSolve(ex, "exercices")}>
                  <i className="bi bi-pencil-square me-1"></i><ActivityStatus kind="exercice" id={ex.id} action fallback="Résoudre" />
                </button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ── QCMView ────────────────────────────────────────
function QCMView({ chapters, onPlay, onReady }) {
  const [qcms, setQcms]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (!loading) onReady?.(); }, [loading, onReady]);

  useEffect(() => {
    api.get("qcms/?sans_reponses=1").then(r => setQcms(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const chapLabel = (id) => chapters.find(c => c.id === id)?.title || "";

  if (loading) return <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2"></div>Chargement...</div>;

  return (
    <div>
      <div className="ql-view-header">
        <h3 className="ql-view-title"><i className="bi bi-grid me-2"></i>QCM</h3>
        <span className="ql-view-count">{qcms.length} QCM</span>
      </div>
      {qcms.length === 0
        ? <EmptyState icon="bi-grid" text="Aucun QCM disponible." />
        : (
          <div className="ql-exercise-list">
            {qcms.map((q, i) => (
              <div key={q.id} className="ql-exercise-card">
                <div className="ql-ex-num" style={{ background: "#6f42c1" }}>{i + 1}</div>
                <div className="ql-ex-body">
                  <div className="ql-ex-title">{q.title}</div>
                  <ActivityStatus kind="qcm" id={q.id} />
                  {chapLabel(q.module) && <div style={{ fontSize: "0.8rem", color: "#64748b" }}><i className="bi bi-book me-1"></i>{chapLabel(q.module)}</div>}
                  {q.questions && <div style={{ fontSize: "0.8rem", color: "#64748b" }}>{q.questions.length} question{q.questions.length !== 1 ? "s" : ""}</div>}
                </div>
                <button className="btn btn-sm btn-outline-purple flex-shrink-0" onClick={() => onPlay && onPlay(q, "qcm")}>
                  <i className="bi bi-play-fill me-1"></i><ActivityStatus kind="qcm" id={q.id} action fallback="Commencer" />
                </button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ── QCMPlayerView ─────────────────────────────────
function QCMPlayerView({ qcm, onBack, onSave }) {
  const [answers,   setAnswers]   = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [results,   setResults]   = useState(null);

  const questions = [...(qcm.questions || [])].sort((a, b) => a.order - b.order);
  const answered  = Object.keys(answers).length;
  const allDone   = questions.length > 0 && answered === questions.length;

  const pick = (qId, rId) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [qId]: rId }));
  };

  const handleSubmit = () => {
    // La copie est notée par le serveur, qui renvoie ensuite les bonnes réponses.
    api.post(`qcms/${qcm.id}/soumettre/`, { answers })
      .then(({ data }) => {
        setResults(data);
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        if (onSave) onSave();
      })
      .catch(() => qlAlert("Impossible d'enregistrer le QCM. Vérifie ta connexion puis réessaie.", "error"));
  };

  const scoreColor = !results ? "#0d6efd"
    : results.score >= 70 ? "#198754"
    : results.score >= 50 ? "#f5820d"
    : "#dc2626";

  const restart = () => { setAnswers({}); setSubmitted(false); setResults(null); };

  return (
    <div className="ql-qcm-player">
      {/* Breadcrumb */}
      <div className="ql-breadcrumb">
        <button className="ql-bc-link" onClick={onBack}>
          <i className="bi bi-chevron-left me-1"></i>QCM
        </button>
        <i className="bi bi-chevron-right ql-bc-sep"></i>
        <span className="ql-bc-current">{qcm.title}</span>
      </div>

      {/* En-tête */}
      <div className="ql-qcm-header">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
          <h4 className="ql-qcm-title">{qcm.title}</h4>
          <span className="ql-qcm-badge">
            <i className="bi bi-question-circle me-1"></i>
            {questions.length} question{questions.length !== 1 ? "s" : ""}
          </span>
        </div>
        {qcm.description && <FormattedText text={qcm.description} className="ql-qcm-desc" />}
      </div>

      {/* Bannière de résultat */}
      {submitted && (
        <div className="ql-qcm-result-banner" style={{ borderLeftColor: scoreColor }}>
          <div className="ql-qcm-score-circle" style={{ background: scoreColor }}>
            <span>{results.score}%</span>
          </div>
          <div>
            <div className="ql-qcm-result-title" style={{ color: scoreColor }}>
              {results.score >= 70 ? "🎉 Excellent !" : results.score >= 50 ? "👍 Bien !" : "📚 À revoir"}
            </div>
            <div className="ql-qcm-result-sub">
              {results.correct} bonne{results.correct !== 1 ? "s" : ""} réponse{results.correct !== 1 ? "s" : ""} sur {results.total}
            </div>
          </div>
          <button className="btn btn-outline-secondary btn-sm ms-auto" onClick={restart}>
            <i className="bi bi-arrow-counterclockwise me-1"></i>Recommencer
          </button>
        </div>
      )}

      {/* Questions */}
      {questions.length === 0 ? (
        <EmptyState icon="bi-grid" text="Ce QCM ne contient encore aucune question." />
      ) : (
        <div className="ql-qcm-questions">
          {questions.map((q, qi) => {
            const res = results?.qResults[q.id];
            return (
              <div key={q.id} className={`ql-qcm-q${submitted ? (res?.ok ? " ql-qcm-q--ok" : " ql-qcm-q--ko") : ""}`}>
                {/* Numéro + verdict */}
                <div className="ql-qcm-q-head">
                  <span className="ql-qcm-q-num">Q{qi + 1}</span>
                  {submitted && (
                    res?.ok
                      ? <span className="ql-verdict ok"><i className="bi bi-check-circle-fill me-1"></i>Correct</span>
                      : <span className="ql-verdict ko"><i className="bi bi-x-circle-fill me-1"></i>Incorrect</span>
                  )}
                </div>

                {/* Texte de la question */}
                <div className="ql-qcm-q-text"><FormattedText text={q.text} /></div>

                {/* Figures / Images de la question */}
                {q.figures && q.figures.some(f => f.image_url) && (
                  <div className="ql-qcm-figures">
                    {q.figures.filter(f => f.image_url).map(fig => (
                      <figure key={fig.id} className="ql-qcm-figure">
                        <img
                          src={fig.image_url}
                          alt={fig.legende || `Figure ${fig.ordre}`}
                          className="ql-qcm-figure-img"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const el = document.getElementById(`fig-err-${fig.id}`);
                            if (el) el.style.display = "block";
                          }}
                        />
                        <div id={`fig-err-${fig.id}`} style={{display:"none",background:"#fee2e2",padding:"6px 10px",borderRadius:"6px",fontSize:"11px",color:"#991b1b",wordBreak:"break-all"}}>
                          ❌ Image inaccessible — URL : {fig.image_url}
                        </div>
                        {fig.legende && <figcaption className="ql-qcm-figure-caption">{fig.legende}</figcaption>}
                      </figure>
                    ))}
                  </div>
                )}

                {/* Choix */}
                <div className="ql-qcm-choices">
                  {q.reponses.map((r, ri) => {
                    const isSel   = answers[q.id] === r.id;
                    const isRight = res ? res.goodId === r.id : !!r.is_correct;
                    let cls = "ql-qcm-choice";
                    if (isSel) cls += " selected";
                    if (submitted && isRight)              cls += " right";
                    if (submitted && isSel && !isRight)    cls += " wrong";
                    return (
                      <label key={r.id} className={cls} onClick={() => pick(q.id, r.id)}>
                        <input
                          type="radio"
                          name={`q${q.id}`}
                          checked={isSel}
                          onChange={() => pick(q.id, r.id)}
                          disabled={submitted}
                          className="ql-qcm-radio"
                        />
                        <span className="ql-choice-letter">{String.fromCharCode(65 + ri)}</span>
                        <span className="ql-choice-text"><FormattedText text={r.text} /></span>
                        {submitted && isRight  && <i className="bi bi-check-circle-fill ql-choice-icon text-success"></i>}
                        {submitted && isSel && !isRight && <i className="bi bi-x-circle-fill ql-choice-icon text-danger"></i>}
                      </label>
                    );
                  })}
                </div>

                {/* Correction si mauvaise réponse */}
                {submitted && !res?.ok && res?.goodId && (
                  <div className="ql-qcm-correction">
                    <i className="bi bi-lightbulb-fill me-1 text-warning"></i>
                    Bonne réponse : <strong><FormattedText text={q.reponses.find(r => r.id === res.goodId)?.text || ""} /></strong>
                  </div>
                )}

                {/* Explication / justification de la bonne réponse */}
                {submitted && q.explication && (
                  <div className="ql-qcm-explication">
                    <i className="bi bi-info-circle-fill me-1"></i>
                    <FormattedText text={q.explication} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Boutons d'action */}
      <div className="d-flex gap-2 mt-4">
        <button className="btn btn-outline-secondary" onClick={onBack}>
          <i className="bi bi-arrow-left me-1"></i>Retour
        </button>
        {!submitted && (
          <button
            className="btn btn-primary flex-fill"
            onClick={handleSubmit}
            disabled={!allDone}
          >
            <i className="bi bi-send-fill me-1"></i>
            Soumettre ({answered}/{questions.length})
          </button>
        )}
      </div>
    </div>
  );
}

// ── SavoirFaireView ────────────────────────────────
const TYPE_VALEUR_LABELS = {
  exact:          "Valeur exacte",
  fraction:       "Fraction (ex : 2/3)",
  arrondi_exces:  "Arrondi par excès à 10⁻³",
  arrondi_defaut: "Arrondi par défaut à 10⁻³",
};

function SavoirFaireView({ exercice, onBack, onSave }) {
  const [questions,         setQuestions]         = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [reponses,          setReponses]          = useState({});   // { qId: { formule (LaTeX), valeur (LaTeX) } }
  const [submitting,        setSubmitting]        = useState(false);
  const [results,           setResults]           = useState(null);
  const [assistanceSent,    setAssistanceSent]    = useState(false);
  const [assistanceLoading, setAssistanceLoading] = useState(false);
  const [correctMode,       setCorrectMode]       = useState(false);
  const [prevResults,       setPrevResults]       = useState(null);
  const mathRefs = React.useRef({});  // { `${qId}_formule`: MathEditor ref, `${qId}_valeur`: ref }

  useEffect(() => {
    api.get(`exercices/${exercice.id}/questions-sf/`)
      .then(r => {
        setQuestions(r.data);
        const init = {};
        r.data.forEach(q => { init[q.id] = { formule: "", valeur: "" }; });
        setReponses(init);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [exercice.id]);

  const updateReponse = (qId, field, value) =>
    setReponses(prev => ({ ...prev, [qId]: { ...prev[qId], [field]: value } }));

  // MathLive gère son propre clavier — pas besoin d'insertIntoMath

  const submit = async () => {
    setSubmitting(true);
    try {
      const reponsesList = questions.map(q => ({
        question_id: q.id,
        formule: reponses[q.id]?.formule || "",
        valeur:  reponses[q.id]?.valeur  || "",
      }));
      const r = await api.post(`exercices/${exercice.id}/soumettre-sf/`, { reponses: reponsesList });
      setResults(r.data);
      if (onSave) onSave();
    } catch (err) {
      qlAlert(err.response?.data?.detail || "Erreur lors de la soumission.", "error");
    }
    setSubmitting(false);
  };

  // Question correcte lors de la tentative précédente ?
  const wasCorrect = (qId) => {
    if (!correctMode || !prevResults) return false;
    const r = prevResults.results?.find(r => r.question_id === qId);
    return r?.points === 2;
  };

  // Retour au formulaire en conservant les réponses (mode correction)
  const enterCorrectMode = () => {
    setPrevResults(results);
    setResults(null);
    setCorrectMode(true);
    setAssistanceSent(false);
    // reponses conservé tel quel — réponses précédentes pré-remplies
  };

  // Recommencer de zéro
  const resetAnswers = () => {
    const init = {};
    questions.forEach(q => { init[q.id] = { formule: "", valeur: "" }; });
    setReponses(init);
    setResults(null);
    setPrevResults(null);
    setCorrectMode(false);
    setAssistanceSent(false);
  };

  const breadcrumb = (
    <div className="ql-breadcrumb">
      <button className="ql-bc-link" onClick={onBack}>
        <i className="bi bi-chevron-left me-1"></i>Exercices
      </button>
      <i className="bi bi-chevron-right ql-bc-sep"></i>
      <span className="ql-bc-current">{exercice.title}</span>
    </div>
  );

  const header = (
    <div className="ql-solver-header">
      <div className="d-flex align-items-center gap-2 mb-2">
        <span className="ql-ex-type-badge"><i className="bi bi-calculator me-1"></i>Savoir-faire</span>
      </div>
      <h4 className="ql-solver-title">{exercice.title}</h4>
      {exercice.description && <FormattedText text={exercice.description} className="ql-solver-statement" />}
      {exercice.image_url && <img src={exercice.image_url} alt="" className="ql-ex-image mt-2" />}
    </div>
  );

  if (loading) return (
    <div className="ql-solver">
      {breadcrumb}
      <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
    </div>
  );

  if (questions.length === 0) return (
    <div className="ql-solver">
      {breadcrumb}
      {header}
      <div className="text-center py-5 text-muted">
        <i className="bi bi-exclamation-circle" style={{ fontSize: "2rem", color: "#cbd5e1" }}></i>
        <p className="mt-3">Aucune question n'a encore été définie pour cet exercice.</p>
        <button className="btn btn-outline-secondary" onClick={onBack}><i className="bi bi-arrow-left me-1"></i>Retour</button>
      </div>
    </div>
  );

  /* ── Écran résultats ── */
  if (results) {
    const pct      = results.score_max > 0 ? Math.round(results.score / results.score_max * 100) : 0;
    const passed   = results.passed;
    const passed80 = results.passed_80;

    const bannerClass = passed80 ? "sf-banner-pass" : passed ? "sf-banner-mid" : "sf-banner-fail";
    const bannerIcon  = passed80 ? "bi-check-circle-fill" : passed ? "bi-emoji-neutral-fill" : "bi-x-circle-fill";
    const bannerMsg   = passed80 ? "Excellent travail !" : passed ? "Assez bien, mais des erreurs subsistent" : "À améliorer";

    return (
      <div className="ql-solver">
        {breadcrumb}
        {header}

        <div className={`ql-sf-result-banner ${bannerClass}`}>
          <i className={`bi ${bannerIcon} fs-1 mb-2`}></i>
          <h4 className="mb-1">{bannerMsg}</h4>
          <div className="ql-sf-score-big">{results.score} / {results.score_max} points</div>
          <div className="ql-sf-note-label">Note : <strong>{results.note}/20</strong> ({pct}%)</div>
        </div>

        <div className="ql-sf-questions-results mt-3">
          {results.results.map(r => {
            const isWrong = r.points < 2;
            const ptsCls  = r.points === 2 ? "sfr-pts-ok" : r.points === 1 ? "sfr-pts-mid" : "sfr-pts-fail";
            const cardCls = r.points === 2 ? "sfr-ok" : r.points === 1 ? "sfr-mid" : "sfr-fail";
            return (
              <div key={r.question_id} className={`ql-sf-q-result-card ${cardCls}`}>
                <div className="sfr-header">
                  <span className="sfr-num">Question {r.numero}</span>
                  <span className={`sfr-pts ${ptsCls}`}>{r.points}/2 pts</span>
                </div>
                {r.enonce && <div className="sfr-enonce"><FormattedText text={r.enonce} /></div>}
                <div className="sfr-checks">
                  <span className={r.formule_ok ? "sfr-check-ok" : "sfr-check-fail"}>
                    <i className={`bi ${r.formule_ok ? "bi-check2-circle" : "bi-x-circle"} me-1`}></i>Formule
                  </span>
                  {r.has_valeur && (
                    <span className={r.valeur_ok ? "sfr-check-ok" : "sfr-check-fail"}>
                      <i className={`bi ${r.valeur_ok ? "bi-check2-circle" : "bi-x-circle"} me-1`}></i>
                      {r.type_valeur === "exact" && !r.valeur_attendue?.match(/^\d/) ? "Réponse" : "Valeur"}
                    </span>
                  )}
                </div>
                {passed80 && isWrong && (
                  <>
                    {!r.formule_ok && r.formule_attendue && (
                      <div className="sfr-hint">
                        <i className="bi bi-lightbulb-fill me-1 text-warning"></i>
                        <span>Formule(s) attendue(s) :</span>
                        <MathDisplay latex={lastFormulaCandidate(r.formule_attendue)} />
                      </div>
                    )}
                    {r.has_valeur && !r.valeur_ok && r.valeur_attendue && (
                      <div className="sfr-hint">
                        <i className="bi bi-lightbulb-fill me-1 text-warning"></i>
                        <span>Réponse attendue : <strong><ExpectedAnswer value={r.valeur_attendue} /></strong></span>
                        {r.type_valeur !== "exact" && (
                          <span className="sfr-type-hint ms-2">({r.type_valeur_label})</span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {!passed ? (
          <div className="ql-sf-fail-actions mt-3">
            <p className="text-muted text-center mb-3">Score inférieur à 50 %. Que souhaitez-vous faire ?</p>
            {assistanceSent ? (
              <div className="ql-assistance-sent">
                <i className="bi bi-envelope-check-fill me-2 text-success"></i>
                <strong>Demande envoyée !</strong>
                <p className="text-muted mt-1 mb-0">Un enseignant de la matière va vous aider.</p>
              </div>
            ) : (
              <div className="d-flex gap-2 justify-content-center flex-wrap">
                <button className="btn btn-outline-primary" onClick={enterCorrectMode}>
                  <i className="bi bi-pencil-square me-1"></i>Corriger les questions ratées
                </button>
                <button
                  className="btn btn-warning"
                  disabled={assistanceLoading}
                  onClick={async () => {
                    setAssistanceLoading(true);
                    try {
                      await api.post(`exercices/${exercice.id}/assistance/`, {
                        student_answer: `Score savoir-faire : ${results.score}/${results.score_max} (${pct}%)`,
                      });
                      setAssistanceSent(true);
                    } catch { setAssistanceSent(true); }
                    setAssistanceLoading(false);
                  }}
                >
                  {assistanceLoading
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Envoi…</>
                    : <><i className="bi bi-person-raised-hand me-2"></i>Demander l'aide</>}
                </button>
              </div>
            )}
          </div>
        ) : !passed80 ? (
          <div className="ql-sf-fail-actions mt-3">
            <p className="text-muted text-center mb-2">
              Vous avez {pct}% — les réponses attendues s'affichent à partir de 80 %.
            </p>
            <div className="text-center">
              <button className="btn btn-outline-primary" onClick={resetAnswers}>
                <i className="bi bi-arrow-counterclockwise me-1"></i>Recommencer
              </button>
            </div>
          </div>
        ) : null}

        <div className="text-center mt-3">
          <button className="btn btn-outline-secondary" onClick={onBack}>
            <i className="bi bi-arrow-left me-1"></i>Retour aux exercices
          </button>
        </div>
      </div>
    );
  }

  /* ── Écran de saisie ── */
  const allFilled = questions.every(q => {
    if (correctMode && wasCorrect(q.id)) return true; // questions déjà correctes ignorées
    const r = reponses[q.id];
    return r?.formule?.trim() && (!q.has_valeur || r?.valeur?.trim());
  });

  return (
    <div className="ql-solver">
      {breadcrumb}
      {header}

      {correctMode ? (
        <div className="ql-sf-intro ql-sf-intro-correct">
          <i className="bi bi-pencil-square me-2 text-warning"></i>
          Corrigez uniquement les questions ratées — vos réponses correctes sont conservées.
        </div>
      ) : (
        <div className="ql-sf-intro">
          <i className="bi bi-info-circle me-2 text-primary"></i>
          Pour chaque question, saisissez la <strong>formule</strong> utilisée
          {questions.some(q => q.has_valeur) && <> et la <strong>valeur / réponse</strong></>}.
          Chaque élément correct rapporte <strong>1 point</strong> (2 pts si tout est juste).
          Le clavier de maths s'ouvre sous chaque champ quand vous cliquez dessus.
        </div>
      )}

      <div className="ql-sf-questions">
        {questions.map((q) => {
          const locked = correctMode && wasCorrect(q.id);
          return (
            <div key={q.id} className={`ql-sf-question-card${locked ? " ql-sf-q-locked" : ""}`}>
              <div className="ql-sf-q-title">
                <span className="ql-sf-q-badge">Q{q.numero}</span>
                {q.enonce && <FormattedText text={q.enonce} className="ql-sf-q-enonce-text" />}
                {locked && (
                  <span className="ql-sf-q-correct-tag">
                    <i className="bi bi-check-circle-fill me-1"></i>Déjà correct
                  </span>
                )}
              </div>

              {/* ── Champ Formule (MathLive) ── */}
              <div className="ql-sf-field">
                <label className="ql-sf-field-label">
                  <i className="bi bi-calculator me-1 text-primary"></i>Formule
                </label>
                <MathEditor
                  ref={el => { if (el) mathRefs.current[`${q.id}_formule`] = el; }}
                  value={reponses[q.id]?.formule || ""}
                  onChange={v => updateReponse(q.id, "formule", v)}
                  placeholder="Ex : v = d/t"
                  multiline
                  readOnly={locked}
                />
              </div>

              {/* ── Champ Valeur/Réponse (MathLive) — si has_valeur ── */}
              {q.has_valeur && (
                <div className="ql-sf-field">
                  <label className="ql-sf-field-label">
                    <i className="bi bi-123 me-1 text-primary"></i>
                    {q.type_valeur === "exact" ? "Valeur / Réponse" : "Valeur numérique"}
                    <span className="ql-sf-type-hint">— {TYPE_VALEUR_LABELS[q.type_valeur]}</span>
                  </label>
                  <MathEditor
                    ref={el => { if (el) mathRefs.current[`${q.id}_valeur`] = el; }}
                    value={reponses[q.id]?.valeur || ""}
                    onChange={v => updateReponse(q.id, "valeur", v)}
                    placeholder={
                      q.type_valeur === "fraction" ? "Ex : 2/3"
                      : q.type_valeur === "exact"  ? "Ex : conducteur"
                      : "Ex : 1.414"
                    }
                    readOnly={locked}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="d-flex gap-2 mt-3">
        <button className="btn btn-outline-secondary" onClick={onBack}>
          <i className="bi bi-arrow-left me-1"></i>Retour
        </button>
        <button
          className="btn btn-primary flex-fill"
          disabled={!allFilled || submitting}
          onClick={submit}
        >
          {submitting
            ? <><span className="spinner-border spinner-border-sm me-2"></span>Correction en cours…</>
            : correctMode
              ? <><i className="bi bi-send-fill me-1"></i>Soumettre les corrections</>
              : <><i className="bi bi-send-fill me-1"></i>Soumettre toutes mes réponses</>}
        </button>
      </div>
    </div>
  );
}

// ── ExerciceSolverView (dispatcher) ────────────────
function ExerciceSolverView({ exercice, onBack, onSave }) {
  const solver = exercice.pedagogical_type === "savoir-faire"
    ? <SavoirFaireView exercice={exercice} onBack={onBack} onSave={onSave} />
    : <CoursSolverView exercice={exercice} onBack={onBack} onSave={onSave} />;
  return <>{solver}<CorrectionEnseignant exerciceId={exercice.id} /></>;
}

// Correction débloquée par un enseignant après une demande d'aide.
function CorrectionEnseignant({ exerciceId }) {
  const [corrections, setCorrections] = useState([]);
  useEffect(() => {
    api.get(`exercices/${exerciceId}/correction-debloquee/`)
      .then(r => setCorrections(r.data?.debloquee ? r.data.corrections : []))
      .catch(() => {});
  }, [exerciceId]);
  if (!corrections.length) return null;
  return <div className="mt-3"><CorrigeEleve titre="Correction de l'exercice" texte={corrections.join("\n\n")} /></div>;
}

function CoursSolverView({ exercice, onBack, onSave }) {
  const [answer,            setAnswer]            = useState("");
  const [submitted,         setSubmitted]         = useState(false);
  const [correction,        setCorrection]        = useState(null);
  const [hasCorrection,     setHasCorrection]     = useState(false);
  const [submitting,        setSubmitting]        = useState(false);
  const [assistanceSent,    setAssistanceSent]    = useState(false);
  const [assistanceLoading, setAssistanceLoading] = useState(false);
  const [savedAnswer,       setSavedAnswer]       = useState("");
  const inputRef = React.useRef(null);

  const insertAtCursor = (text) => {
    const el = inputRef.current;
    if (!el) { setAnswer(prev => prev + text); return; }
    const s = el.selectionStart ?? answer.length;
    const e = el.selectionEnd   ?? answer.length;
    const newVal = answer.slice(0, s) + text + answer.slice(e);
    const cur = s + text.length;
    setAnswer(newVal);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(cur, cur); });
  };

  const typeLabel = {
    cours: "Question de cours", "savoir-faire": "Savoir-faire", competence: "Compétence",
  };

  return (
    <div className="ql-solver">
      {/* Breadcrumb */}
      <div className="ql-breadcrumb">
        <button className="ql-bc-link" onClick={onBack}>
          <i className="bi bi-chevron-left me-1"></i>Exercices
        </button>
        <i className="bi bi-chevron-right ql-bc-sep"></i>
        <span className="ql-bc-current">{exercice.title}</span>
      </div>

      {/* En-tête exercice */}
      <div className="ql-solver-header">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="ql-ex-type-badge">
            <i className="bi bi-pencil-square me-1"></i>
            {typeLabel[exercice.pedagogical_type] || exercice.pedagogical_type || "Exercice"}
          </span>
        </div>
        <h4 className="ql-solver-title">{exercice.title}</h4>
        {exercice.description && (
          <FormattedText text={exercice.description} className="ql-solver-statement" />
        )}
      </div>

      {submitted ? (
        /* ── Écran résultat ── */
        <div>
          {correction ? (
            /* ✅ Correction trouvée */
            <div className="ql-solver-success">
              <i className="bi bi-check-circle-fill fs-1 mb-2 text-success"></i>
              <h5>Bonne réponse !</h5>
              <p className="text-muted mb-0">Votre réponse contient les éléments clés. Voici la correction :</p>
              <div className="ql-correction-card mt-3">
                <div className="ql-correction-header">
                  <i className="bi bi-lightbulb-fill me-2 text-warning"></i>
                  <strong>Correction</strong>
                </div>
                <div className="ql-correction-body">
                  <FormattedText text={correction.content} />
                </div>
              </div>
              <button className="btn btn-primary mt-3" onClick={onBack}>
                <i className="bi bi-arrow-left me-1"></i>Retour aux exercices
              </button>
            </div>
          ) : hasCorrection ? (
            /* ❌ Correction existe mais mots-clés insuffisants */
            <div className="ql-solver-fail">
              <i className="bi bi-x-circle-fill fs-1 mb-2 text-danger"></i>
              <h5>Vous n'avez pas trouvé la solution</h5>
              <p className="text-muted">
                Votre réponse ne contient pas suffisamment d'éléments attendus pour cet exercice.
              </p>

              {assistanceSent ? (
                <div className="ql-assistance-sent">
                  <i className="bi bi-envelope-check-fill me-2 text-success"></i>
                  <strong>Demande envoyée !</strong>
                  <p className="text-muted mt-1 mb-0">Un enseignant a reçu votre demande et vous aidera bientôt.</p>
                </div>
              ) : (
                <div className="ql-assistance-prompt">
                  <p className="ql-assistance-question">
                    Souhaitez-vous recevoir une assistance d'un enseignant ?
                  </p>
                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                    <button
                      className="btn btn-warning"
                      disabled={assistanceLoading}
                      onClick={async () => {
                        setAssistanceLoading(true);
                        try {
                          await api.post(`exercices/${exercice.id}/assistance/`, {
                            student_answer: savedAnswer,
                          });
                          setAssistanceSent(true);
                        } catch {
                          setAssistanceSent(true);
                        } finally {
                          setAssistanceLoading(false);
                        }
                      }}
                    >
                      {assistanceLoading
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Envoi…</>
                        : <><i className="bi bi-person-raised-hand me-2"></i>Oui, demander de l'aide</>
                      }
                    </button>
                    <button className="btn btn-outline-secondary" onClick={onBack}>
                      Non, continuer seul
                    </button>
                  </div>
                </div>
              )}

              <button className="btn btn-link mt-3 text-muted" onClick={onBack}>
                <i className="bi bi-arrow-left me-1"></i>Retour aux exercices
              </button>
            </div>
          ) : (
            /* ℹ️ Pas de correction définie pour cet exercice */
            <div className="ql-solver-success">
              <i className="bi bi-check-circle-fill fs-1 mb-3 text-success"></i>
              <h5>Réponse soumise !</h5>
              <p className="text-muted">Votre réponse a été enregistrée avec succès.</p>
              <button className="btn btn-primary mt-2" onClick={onBack}>
                <i className="bi bi-arrow-left me-1"></i>Retour aux exercices
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Zone de réponse */}
          <div className="ql-solver-area">
            <div className="ql-solver-label">
              <i className="bi bi-pencil me-1"></i>Votre réponse
              <span className="ql-solver-label-hint">— tapez ci-dessous ou utilisez le clavier</span>
            </div>
            <textarea
              ref={inputRef}
              className="ql-math-input"
              value={answer}
              onChange={e => setAnswer(e.target.value)}
              placeholder="Tapez votre réponse ou utilisez le clavier scientifique…"
              rows={4}
            />
            {answer.trim() && (
              <div className="ql-math-preview">
                <div className="ql-math-preview-label">
                  <i className="bi bi-eye me-1"></i>Aperçu
                </div>
                <div className="ql-math-preview-body">
                  <MathText text={answer} />
                </div>
              </div>
            )}
          </div>

          {/* Clavier mathématique */}
          <MathKeyboard
            onInsert={insertAtCursor}
            insertLabel="Insérer dans la réponse"
          />

          {/* Actions */}
          <div className="d-flex gap-2 mt-3">
            <button className="btn btn-outline-secondary" onClick={onBack}>
              <i className="bi bi-arrow-left me-1"></i>Retour
            </button>
            <button
              className="btn btn-primary flex-fill"
              disabled={!answer.trim() || submitting}
              onClick={async () => {
                setSubmitting(true);
                try {
                  const res = await api.post(
                    `exercices/${exercice.id}/soumettre/`,
                    { student_answer: answer }
                  );
                  setSavedAnswer(answer);
                  setHasCorrection(!!res.data.has_correction);
                  if (res.data.correction) setCorrection(res.data.correction);
                  setSubmitted(true);
                  if (onSave) onSave();
                } catch {
                  qlAlert("Votre réponse n’a pas été enregistrée. Vérifiez votre connexion et réessayez.", "error");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {submitting
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Envoi…</>
                : <><i className="bi bi-send-fill me-1"></i>Soumettre ma réponse</>
              }
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Plans & méthodes de paiement ──────────────────
const SUB_PLANS = [
  { days: 1,   label: "1 jour",    price: 100,   badge: "Essai",       color: "#64748b" },
  { days: 7,   label: "7 jours",   price: 700,   badge: "Semaine",     color: "#3b82f6" },
  { days: 30,  label: "30 jours",  price: 3000,  badge: "Mensuel",     color: "#f5820d", popular: true },
  { days: 90,  label: "90 jours",  price: 9000,  badge: "Trimestriel", color: "#10b981" },
  { days: 180, label: "6 mois",    price: 15000, badge: "Semestriel",  color: "#6f42c1" },
  { days: 270, label: "9 mois",    price: 20000, badge: "Annuel",      color: "#0d6efd" },
];
const SUB_METHODS = [
  { key: "MTN_CM", label: "MTN Mobile Money", icon: "bi-phone" },
  { key: "OM_CM",  label: "Orange Money",      icon: "bi-phone" },
];

// ── RenewalModal ───────────────────────────────────
export function RenewalModal({ onClose, onSuccess }) {
  const [plan,       setPlan]      = useState(SUB_PLANS[2]);
  const [method,     setMethod]    = useState(SUB_METHODS[0].key);
  const [phone,      setPhone]     = useState("");
  const [loading,    setLoading]   = useState(false);
  const [polling,    setPolling]   = useState(false);
  const [error,      setError]     = useState(null);
  const [success,    setSuccess]   = useState(false);
  const [paymentUrl, setPaymentUrl] = useState(null);

  const pollStatus = (merchantId, attempts = 0) => {
    if (attempts > 40) {
      setPolling(false);
      setLoading(false);
      setError("Délai dépassé. Vérifiez votre téléphone et réessayez.");
      return;
    }
    setTimeout(async () => {
      try {
        const r = await api.get(`paiements/cinetpay/status/${merchantId}/`);
        if (r.data.status === "success") {
          setPolling(false);
          setSuccess(true);
          setTimeout(() => onSuccess(r.data), 2000);
        } else if (r.data.status === "failed") {
          setPolling(false);
          setLoading(false);
          setError("Paiement échoué. Vérifiez votre solde et réessayez.");
        } else {
          pollStatus(merchantId, attempts + 1);
        }
      } catch {
        pollStatus(merchantId, attempts + 1);
      }
    }, 4000);
  };

  const handlePay = async () => {
    if (!phone.trim()) { setError("Veuillez entrer votre numéro de téléphone."); return; }
    setError(null);
    setLoading(true);
    try {
      const digits = phone.trim().replace(/\D/g, "").replace(/^237/, "");
      const r = await api.post("paiements/cinetpay/init/", {
        days: plan.days,
        payment_method: method,
        phone_number: `237${digits}`,
      });
      if (!r.data.payment_url) throw new Error("URL_CINETPAY_ABSENTE");
      setPaymentUrl(r.data.payment_url);
      setPolling(true);
      pollStatus(r.data.merchant_transaction_id);
      try {
        await openCinetPayExternally(r.data.payment_url);
      } catch {
        setError("Le paiement est initialisé, mais la page CinetPay n’a pas pu être ouverte. Utilisez le bouton Réouvrir la page CinetPay.");
      }
    } catch (e) {
      setError(e.response?.data?.detail || (e.message === "URL_CINETPAY_ABSENTE" ? "CinetPay n’a pas fourni de page de paiement." : "Erreur lors de l'initiation du paiement."));
      setLoading(false);
    }
  };

  return (
    <div className="ql-modal-overlay" onClick={e => e.target === e.currentTarget && !polling && onClose()}>
      <div className="ql-modal" style={{ maxWidth: 480, padding: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h5 style={{ margin: 0, fontWeight: 700 }}><i className="bi bi-arrow-repeat me-2 text-primary"></i>Renouveler mon abonnement</h5>
          {!polling && <button className="ql-modal-close" style={{ position: "static" }} onClick={onClose}>×</button>}
        </div>
        <div>
          {success ? (
            <div className="text-center py-4">
              <i className="bi bi-check-circle-fill text-success" style={{ fontSize: "3rem" }}></i>
              <div className="fw-bold mt-3 fs-5">Paiement confirmé !</div>
              <div className="text-muted mt-1">Votre abonnement a été renouvelé.</div>
            </div>
          ) : polling ? (
            <div style={{ textAlign: "center", padding: "32px 16px" }}>
              <div className="spinner-border text-primary mb-3" style={{ width: 48, height: 48 }}></div>
              <h6 className="fw-semibold mb-1">Paiement en cours…</h6>
              {error && <div className="alert alert-warning py-2 mt-2" role="alert">{error}</div>}
              <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                Effectuez votre paiement de <strong>{plan.price.toLocaleString("fr-FR")} FCFA</strong> sur la page CinetPay.<br />
                <small>Cette fenêtre se mettra à jour automatiquement après confirmation.</small>
              </p>
              {paymentUrl && (
                <button type="button" onClick={() => openCinetPayExternally(paymentUrl).catch(() => setError("Impossible d’ouvrir la page CinetPay."))} className="btn btn-sm btn-outline-primary mt-2">
                  <i className="bi bi-box-arrow-up-right me-1"></i>
                  Réouvrir la page CinetPay
                </button>
              )}
            </div>
          ) : (
            <>
              <h6 className="fw-semibold mb-3">Choisissez votre forfait</h6>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8, marginBottom: 20 }}>
                {SUB_PLANS.map(p => (
                  <div
                    key={p.days}
                    onClick={() => setPlan(p)}
                    style={{
                      border: `2px solid ${plan.days === p.days ? p.color : "#e2e8f0"}`,
                      borderRadius: 10, padding: "12px 10px", cursor: "pointer",
                      background: plan.days === p.days ? p.color + "10" : "#fff",
                      position: "relative",
                    }}
                  >
                    {p.popular && (
                      <span style={{
                        position: "absolute", top: -8, right: 8,
                        background: p.color, color: "#fff",
                        fontSize: "0.65rem", fontWeight: 700, borderRadius: 4, padding: "1px 6px",
                      }}>Populaire</span>
                    )}
                    <div style={{ fontSize: "0.72rem", fontWeight: 700, color: p.color, marginBottom: 2 }}>{p.badge}</div>
                    <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>{p.label}</div>
                    <div style={{ color: "#1e293b", fontWeight: 800, fontSize: "1rem", marginTop: 4 }}>
                      {p.price.toLocaleString("fr-FR")} <span style={{ fontSize: "0.75rem", fontWeight: 500 }}>FCFA</span>
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "#94a3b8" }}>{Math.round(p.price / p.days)} FCFA/jour</div>
                  </div>
                ))}
              </div>

              <h6 className="fw-semibold mb-2">Méthode de paiement</h6>
              <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                {SUB_METHODS.map(m => (
                  <label
                    key={m.key}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      border: `2px solid ${method === m.key ? "#0d6efd" : "#e2e8f0"}`,
                      borderRadius: 8, padding: "8px 12px", cursor: "pointer",
                      background: method === m.key ? "#eff6ff" : "#fff", flex: 1,
                    }}
                  >
                    <input type="radio" name="renewal-method" value={m.key} checked={method === m.key} onChange={() => setMethod(m.key)} style={{ display: "none" }} />
                    <i className={`bi ${m.icon}`} style={{ color: method === m.key ? "#0d6efd" : "#64748b" }}></i>
                    <span style={{ fontSize: "0.82rem", fontWeight: 500 }}>{m.label}</span>
                  </label>
                ))}
              </div>

              <h6 className="fw-semibold mb-2">Numéro de téléphone</h6>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", background: "#f8fafc" }}>
                  <span style={{ padding: "10px 12px", background: "#e2e8f0", fontWeight: 600, color: "#475569", fontSize: "0.95rem", flexShrink: 0, userSelect: "none" }}>+237</span>
                  <input
                    type="tel"
                    placeholder="6XX XX XX XX"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 9))}
                    maxLength={9}
                    style={{ flex: 1, padding: "10px 12px", border: "none", fontSize: "0.95rem", background: "transparent", outline: "none" }}
                  />
                </div>
                <p style={{ fontSize: "0.78rem", color: "#64748b", marginTop: 4, marginBottom: 0 }}>
                  Entrez votre numéro sans le préfixe +237
                </p>
              </div>

              {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: "0.85rem" }}>{error}</div>}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc", borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                <span style={{ fontSize: "0.85rem", color: "#64748b" }}>Total à payer</span>
                <strong style={{ color: "#1e293b" }}>{plan.price.toLocaleString("fr-FR")} FCFA</strong>
              </div>

              <button
                className="btn btn-primary w-100 py-2"
                disabled={loading}
                onClick={handlePay}
              >
                {loading
                  ? <span className="spinner-border spinner-border-sm me-2"></span>
                  : <i className="bi bi-lock-fill me-2"></i>}
                Payer {plan.price.toLocaleString("fr-FR")} FCFA — {plan.label}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── SubscriptionCountdown ─────────────────────────
export function SubscriptionCountdown({ endDateStr, onRenew, consumptionOnly = false, onRefresh }) {
  const calc = () => {
    if (!endDateStr) return null;
    const diff = new Date(endDateStr).getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, mins: 0, expired: true };
    const totalSecs = Math.floor(diff / 1000);
    const days  = Math.floor(totalSecs / 86400);
    const hours = Math.floor((totalSecs % 86400) / 3600);
    const mins  = Math.floor((totalSecs % 3600) / 60);
    return { days, hours, mins, expired: false };
  };

  const [remaining, setRemaining] = useState(calc);

  useEffect(() => {
    const id = setInterval(() => setRemaining(calc()), 60_000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDateStr]);

  if (!remaining) return null;

  const urgency = remaining.expired         ? "expired"
    : remaining.days === 0                  ? "urgent"
    : remaining.days < 3                    ? "warning"
    : "ok";

  const COLORS = {
    ok:      { bg: "#f0fdf4", border: "#bbf7d0", text: "#166534", icon: "#22c55e", label: "#16a34a" },
    warning: { bg: "#fffbeb", border: "#fde68a", text: "#92400e", icon: "#f59e0b", label: "#b45309" },
    urgent:  { bg: "#fff1f2", border: "#fecdd3", text: "#881337", icon: "#ef4444", label: "#dc2626" },
    expired: { bg: "#f1f5f9", border: "#cbd5e1", text: "#475569", icon: "#94a3b8", label: "#64748b" },
  };
  const c = COLORS[urgency];

  const pad = n => String(n).padStart(2, "0");

  const endFmt = new Date(endDateStr).toLocaleString("fr-FR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  const canRenew = !consumptionOnly && !!onRenew;

  return (
    <div
      className="ql-sub-countdown"
      style={{ background: c.bg, borderColor: c.border, cursor: canRenew ? "pointer" : "default" }}
      onClick={canRenew ? onRenew : undefined}
      title={canRenew ? "Cliquer pour renouveler" : undefined}
    >
      <div className="ql-sub-cd-left">
        <i className="bi bi-clock-history" style={{ color: c.icon, fontSize: "1.3rem" }}></i>
        <div>
          <div className="ql-sub-cd-title" style={{ color: c.text }}>
            {remaining.expired ? "Abonnement expiré — cliquez pour renouveler" : "Abonnement actif — expire le"}
          </div>
          <div className="ql-sub-cd-date" style={{ color: c.label }}>{endFmt}</div>
        </div>
      </div>

      <div className="ql-sub-cd-actions">
        {!remaining.expired && (
          <div className="ql-sub-cd-blocks">
            {[
              { val: pad(remaining.days),  lbl: "JOURS" },
              { val: pad(remaining.hours), lbl: "HEURES" },
              { val: pad(remaining.mins),  lbl: "MIN" },
            ].map((b, i) => (
              <React.Fragment key={b.lbl}>
                {i > 0 && <span className="ql-sub-cd-sep" style={{ color: c.icon }}>:</span>}
                <div className="ql-sub-cd-block" style={{ borderColor: c.border }}>
                  <span className="ql-sub-cd-val" style={{ color: c.text }}>{b.val}</span>
                  <span className="ql-sub-cd-lbl" style={{ color: c.label }}>{b.lbl}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
        {consumptionOnly && (
          <button
            className="btn btn-sm btn-outline-primary ql-account-refresh-btn"
            style={{ borderRadius: 8, fontWeight: 600 }}
            onClick={e => { e.stopPropagation(); onRefresh?.(); }}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>Actualiser mon compte
          </button>
        )}
        {canRenew && (
          <button
            className="btn btn-sm"
            style={{ background: c.icon, color: "#fff", borderRadius: 8, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}
            onClick={e => { e.stopPropagation(); onRenew(); }}
          >
            <i className="bi bi-arrow-repeat me-1"></i>Renouveler
          </button>
        )}
      </div>
    </div>
  );
}

// ── Live YouTube components ───────────────────────

function useCountdown(targetIso) {
  // Initialisation synchrone pour éviter le flash null au premier rendu
  const calc = () => {
    const diff = new Date(targetIso) - Date.now();
    if (diff <= 0) return null;
    return {
      h: Math.floor(diff / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
      total: diff,
    };
  };
  const [remaining, setRemaining] = useState(calc);
  useEffect(() => {
    setRemaining(calc());
    const timer = setInterval(() => setRemaining(calc()), 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIso]);
  return remaining;
}

// React.memo avec comparaison précise : ne se re-rend que si statut, youtube_id ou scheduled_at change.
// onStart change de référence à chaque refresh → on l'ignore dans la comparaison pour éviter les re-renders inutiles.
const LiveCountdownCard = React.memo(function LiveCountdownCard({ live, onStart }) {
  const remaining = useCountdown(live.scheduled_at);
  const startedRef = useRef(false);
  const pad = n => String(n).padStart(2, "0");

  // Quand le décompte atteint zéro → basculer automatiquement en lecteur
  useEffect(() => {
    if (!remaining && !startedRef.current) {
      startedRef.current = true;
      if (onStart) onStart();
    }
  }, [remaining, onStart]);

  // Afficher le lecteur si : statut déjà en_cours OU heure passée (remaining null)
  // Le live ne s'arrête QUE lorsque statut passe à "terminé" (géré dans LivesPage/Section)
  if (live.statut === "en_cours" || !remaining) {
    return <LivePlayerCard live={live} />;
  }

  return (
    <div className="card border-danger mb-3" style={{ borderRadius: 12 }}>
      <div className="card-body">
        <div className="d-flex align-items-start gap-3">
          <div style={{ width: 48, height: 48, borderRadius: 10, background: "#fff5f5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <i className="bi bi-broadcast fs-4 text-danger"></i>
          </div>
          <div className="flex-fill">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="badge bg-danger" style={{ fontSize: "0.65rem" }}>LIVE</span>
              {live.manuel_titre && <span className="badge bg-light text-dark border" style={{ fontSize: "0.65rem" }}>{live.manuel_titre}</span>}
            </div>
            <div className="fw-semibold" style={{ fontSize: "0.95rem", color: "var(--navy, #1e3a5f)" }}>{live.titre}</div>
            {live.description && <div className="text-muted" style={{ fontSize: "0.8rem" }}>{live.description}</div>}
            <div className="mt-2 d-flex align-items-center gap-2">
              <span className="text-muted" style={{ fontSize: "0.78rem" }}>Commence dans</span>
              <div className="d-flex gap-1">
                {[{ v: remaining.h, u: "h" }, { v: remaining.m, u: "m" }, { v: remaining.s, u: "s" }].map(({ v, u }) => (
                  <div key={u} style={{ background: "#fee2e2", borderRadius: 6, padding: "2px 8px", textAlign: "center" }}>
                    <div className="fw-bold text-danger" style={{ fontSize: "1rem", lineHeight: 1.2 }}>{pad(v)}</div>
                    <div style={{ fontSize: "0.6rem", color: "#666" }}>{u}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}, (prev, next) =>
  prev.live.statut === next.live.statut &&
  prev.live.youtube_id === next.live.youtube_id &&
  prev.live.scheduled_at === next.live.scheduled_at
);

// LivePlayerCard : état interne `seed` pour forcer un rechargement manuel sans recharger la page.
// React.memo avec comparateur étendu : ne se re-rend que si youtube_id ou seed change.
// Cela isole l'iframe des re-renders du composant parent (polls 15s, transitions, etc.)
const LivePlayerCard = React.memo(function LivePlayerCard({ live }) {
  const [seed, setSeed] = React.useState(0);
  return (
    <div className="card border-danger mb-3" style={{ borderRadius: 12, overflow: "hidden" }}>
      <div style={{ background: "#1a1a1a", padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
        <span className="badge bg-danger" style={{ animation: "pulse 1.5s infinite" }}>🔴 EN DIRECT</span>
        <span className="text-white fw-semibold" style={{ fontSize: "0.9rem", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{live.titre}</span>
        {live.manuel_titre && <span className="badge bg-secondary" style={{ fontSize: "0.65rem", flexShrink: 0 }}>{live.manuel_titre}</span>}
        <button
          title="Relancer le lecteur si le live semble arrêté"
          onClick={() => setSeed(s => s + 1)}
          style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 6, padding: "2px 8px", fontSize: "1rem", cursor: "pointer", flexShrink: 0 }}
        >↻</button>
      </div>
      <div style={{ position: "relative", paddingTop: "56.25%" }}>
        {/* key inclut seed → rechargement propre sur clic ↻ ou changement d'ID YouTube */}
        <iframe
          key={`${live.youtube_id}-${seed}`}
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
          src={urlLecteurYoutube(live.youtube_id, { direct: true })}
          title={live.titre}
          allow={AUTORISATIONS_LECTEUR}
          allowFullScreen
        />
      </div>
      {live.description && (
        <div style={{ padding: "12px 16px", background: "#fff", borderTop: "1px solid #f0f0f0" }}>
          <p className="mb-0" style={{ fontSize: "0.88rem", color: "#444", lineHeight: 1.6 }}>{live.description}</p>
        </div>
      )}
    </div>
  );
}, (prev, next) => prev.live.youtube_id === next.live.youtube_id);

function LiveReplayCard({ live }) {
  const [show, setShow] = useState(false);
  return (
    <div className="card mb-3" style={{ borderRadius: 12 }}>
      <div className="card-body">
        <div className="d-flex align-items-center gap-3">
          <div style={{ width: 44, height: 44, borderRadius: 10, background: "#f0f4f8", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <i className="bi bi-play-circle-fill fs-4 text-secondary"></i>
          </div>
          <div className="flex-fill">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="badge bg-secondary" style={{ fontSize: "0.65rem" }}>REPLAY</span>
              {live.manuel_titre && <span className="badge bg-light text-dark border" style={{ fontSize: "0.65rem" }}>{live.manuel_titre}</span>}
            </div>
            <div className="fw-semibold" style={{ fontSize: "0.9rem" }}>{live.titre}</div>
            <div className="text-muted" style={{ fontSize: "0.78rem" }}>{new Date(live.scheduled_at).toLocaleDateString("fr-FR")}</div>
          </div>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setShow(v => !v)}>
            <i className={`bi ${show ? "bi-chevron-up" : "bi-play"} me-1`}></i>{show ? "Fermer" : "Revoir"}
          </button>
        </div>
        {show && (
          <div style={{ position: "relative", paddingTop: "56.25%", marginTop: 12, borderRadius: 8, overflow: "hidden" }}>
            <iframe
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
              src={urlLecteurYoutube(live.youtube_id)}
              title={live.titre}
              allow={AUTORISATIONS_LECTEUR}
              allowFullScreen
            />
          </div>
        )}
      </div>
    </div>
  );
}

function LiveEndScreen({ live, onNavigateMessages }) {
  const [showReplay, setShowReplay] = useState(false);
  return (
    <div className="card mb-3 border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
      <div style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)", padding: "28px 24px", textAlign: "center" }}>
        <div style={{ fontSize: "2.8rem", marginBottom: 8 }}>🎓</div>
        <h5 className="text-white fw-bold mb-1">Merci d'avoir participé !</h5>
        <p className="mb-1 text-white" style={{ opacity: 0.9, fontSize: "0.9rem" }}>{live.titre}</p>
        {live.manuel_titre && (
          <span className="badge mt-1" style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: "0.72rem" }}>{live.manuel_titre}</span>
        )}
      </div>
      <div className="card-body text-center" style={{ background: "#fff" }}>
        <p className="mb-1" style={{ color: "#333", fontSize: "0.92rem", lineHeight: 1.6 }}>
          Ce live est maintenant terminé. Tu as des questions ou des préoccupations
          sur les sujets abordés ?
        </p>
        <p className="mb-3" style={{ color: "#555", fontSize: "0.88rem" }}>
          Envoie un message à ton enseignant directement sur la plateforme —
          il est là pour t'aider.
        </p>
        <div className="d-flex gap-2 justify-content-center flex-wrap mb-3">
          <button className="btn btn-primary btn-sm" onClick={onNavigateMessages}>
            <i className="bi bi-chat-dots me-1"></i>Envoyer un message
          </button>
          <button
            className={`btn btn-sm ${showReplay ? "btn-secondary" : "btn-outline-secondary"}`}
            onClick={() => setShowReplay(v => !v)}
          >
            <i className={`bi ${showReplay ? "bi-chevron-up" : "bi-play-circle"} me-1`}></i>
            {showReplay ? "Fermer le replay" : "Revoir ce live"}
          </button>
        </div>
        {showReplay && (
          <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: 10, overflow: "hidden" }}>
            <iframe
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
              src={urlLecteurYoutube(live.youtube_id)}
              title={live.titre}
              allow={AUTORISATIONS_LECTEUR}
              allowFullScreen
            />
          </div>
        )}
      </div>
    </div>
  );
}

function LiveDashboardSection({ lives, endedLives = [], onViewAll, onStatutChange, onNavigateMessages }) {
  // "en_cours" et "a_venir" dans le MÊME tableau → même instance React → iframe ne remonte pas
  const actifs   = lives.filter(l => l.statut === "en_cours" || l.statut === "a_venir").slice(0, 2);
  const termines = lives.filter(l => l.statut === "termine").slice(0, 1);
  const hasEnCours = actifs.some(l => l.statut === "en_cours");
  // Exclure les "terminés" déjà gérés par endedLives pour éviter les doublons
  const endedIds = new Set(endedLives.map(l => l.id));
  const terminésSansEndScreen = termines.filter(l => !endedIds.has(l.id));

  if (actifs.length === 0 && terminésSansEndScreen.length === 0 && endedLives.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          {hasEnCours
            ? <span className="badge bg-danger px-2 py-1" style={{ fontSize: "0.75rem" }}>🔴 EN DIRECT</span>
            : <span className="badge bg-secondary px-2 py-1" style={{ fontSize: "0.75rem" }}>LIVE</span>
          }
          <h5 className="fw-bold mb-0" style={{ color: "var(--navy, #1e3a5f)" }}>Sessions en direct</h5>
        </div>
        <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={onViewAll}>
          Tous les lives <i className="bi bi-chevron-right"></i>
        </button>
      </div>
      {endedLives.map(l => (
        <LiveEndScreen key={`end-${l.id}`} live={l} onNavigateMessages={onNavigateMessages} />
      ))}
      {actifs.map(l => (
        <LiveCountdownCard
          key={l.id}
          live={l}
          onStart={() => l.statut === "a_venir" && onStatutChange && onStatutChange(l.id, "en_cours")}
        />
      ))}
      {terminésSansEndScreen.map(l => <LiveReplayCard key={l.id} live={l} />)}
    </div>
  );
}

// Section défini HORS de LivesPage : référence stable entre re-renders → les enfants ne sont jamais
// démontés/remontés uniquement parce que LivesPage se re-rend (évite la destruction des iframes)
function LiveSection({ title, badge, color, children }) {
  return (
    <div className="mb-4">
      <div className="d-flex align-items-center gap-2 mb-3">
        <span className={`badge bg-${color}`} style={{ fontSize: "0.72rem" }}>{badge}</span>
        <h6 className="fw-bold mb-0" style={{ color: "var(--navy, #1e3a5f)" }}>{title}</h6>
      </div>
      {children}
    </div>
  );
}

function LivesPage({ lives, endedLives = [], onStatutChange, onNavigateMessages }) {
  // "en_cours" et "a_venir" dans le MÊME tableau → même instance React → iframe ne remonte pas
  const actifs   = lives.filter(l => l.statut === "en_cours" || l.statut === "a_venir");
  const termines = lives.filter(l => l.statut === "termine");
  const endedIds = new Set(endedLives.map(l => l.id));
  const terminésSansEndScreen = termines.filter(l => !endedIds.has(l.id));

  const hasEnCours = actifs.some(l => l.statut === "en_cours");

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-4">
        <i className="bi bi-broadcast fs-4 text-danger"></i>
        <h4 className="fw-bold mb-0" style={{ color: "var(--navy, #1e3a5f)" }}>Quick Lives</h4>
      </div>

      {lives.length === 0 && endedLives.length === 0 && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-broadcast" style={{ fontSize: "3rem", opacity: 0.3 }}></i>
          <p className="mt-3">Aucune session live disponible pour l'instant.</p>
          <p className="small">Les lives seront visibles ici dès qu'ils sont programmés pour ton manuel.</p>
        </div>
      )}

      {endedLives.length > 0 && (
        <LiveSection title="Live terminé" badge="TERMINÉ" color="dark">
          {endedLives.map(l => (
            <LiveEndScreen key={`end-${l.id}`} live={l} onNavigateMessages={onNavigateMessages} />
          ))}
        </LiveSection>
      )}

      {actifs.length > 0 && (
        <LiveSection
          title={hasEnCours ? "En direct maintenant" : "Sessions à venir"}
          badge={hasEnCours ? "🔴 EN DIRECT" : "À VENIR"}
          color={hasEnCours ? "danger" : "secondary"}
        >
          {actifs.map(l => (
            <LiveCountdownCard
              key={l.id}
              live={l}
              onStart={() => l.statut === "a_venir" && onStatutChange && onStatutChange(l.id, "en_cours")}
            />
          ))}
        </LiveSection>
      )}

      {terminésSansEndScreen.length > 0 && (
        <LiveSection title="Replays disponibles" badge="REPLAY" color="success">
          {terminésSansEndScreen.map(l => <LiveReplayCard key={l.id} live={l} />)}
        </LiveSection>
      )}
    </div>
  );
}

// ── StudentDashboard ───────────────────────────────
// ── Helpers d'activités ───────────────────────────
function activityMeta(type) {
  if (type === "qcm")        return { icon: "bi-grid-3x3-gap-fill",  color: "#6f42c1", label: "QCM terminé" };
  if (type === "exercice")   return { icon: "bi-pencil-fill",         color: "#198754", label: "Exercice résolu" };
  if (type === "video")      return { icon: "bi-play-circle-fill",    color: "#f5820d", label: "Vidéo regardée" };
  if (type === "devoir")     return { icon: "bi-pencil-square",       color: "#0d6efd", label: "Devoir remis" };
  if (type === "evaluation") return { icon: "bi-clipboard-check-fill",color: "#dc2626", label: "Évaluation remise" };
  return { icon: "bi-circle", color: "#64748b", label: type };
}

function scoreColor(score) {
  if (score === null || score === undefined) return "#64748b";
  if (score >= 70) return "#198754";
  if (score >= 50) return "#f5820d";
  return "#dc2626";
}

function gradeColor(gradeStr) {
  if (!gradeStr) return "#64748b";
  const num = parseFloat(String(gradeStr).replace(",", ".").split("/")[0]);
  if (isNaN(num)) return "#64748b";
  if (num < 5)  return "#dc3545";
  if (num > 16) return "#0d6efd";
  return "#e6a817";
}

function mention(score) {
  if (score >= 90) return { label: "Excellent",   color: "#198754", bg: "#d1fae5" };
  if (score >= 75) return { label: "Très bien",   color: "#059669", bg: "#d1fae5" };
  if (score >= 60) return { label: "Bien",        color: "#0d6efd", bg: "#dbeafe" };
  if (score >= 50) return { label: "Passable",    color: "#f5820d", bg: "#fff7ed" };
  return            { label: "À améliorer",       color: "#dc2626", bg: "#fee2e2" };
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60)   return "À l'instant";
  if (diff < 3600) return `Il y a ${Math.round(diff / 60)} min`;
  if (diff < 86400)return `Il y a ${Math.round(diff / 3600)}h`;
  if (diff < 172800)return "Hier";
  if (diff < 604800)return `Il y a ${Math.round(diff / 86400)} jours`;
  return new Date(dateStr).toLocaleDateString("fr-FR");
}

// ── RecentActivities (accueil) ────────────────────
function RecentActivities({ onViewAll }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    api.get("activites/").then(r => setActivities(r.data.slice(0, 4))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-2 text-muted text-center" style={{ fontSize: "0.85rem" }}><div className="spinner-border spinner-border-sm me-2"></div>Chargement...</div>;

  return (
    <>
      <div className="ql-activity-list">
        {activities.length === 0
          ? <div className="text-muted text-center py-3" style={{ fontSize: "0.85rem" }}>Aucune activité encore. Lance un QCM ou regarde une vidéo !</div>
          : activities.map((a, i) => {
              const meta = activityMeta(a.type);
              return (
                <div key={i} className="ql-activity-row">
                  <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: "1.1rem", minWidth: 20 }}></i>
                  <span className="ql-act-type">{meta.label}</span>
                  <span className="ql-act-name">{a.name}</span>
                  {a.score !== null && a.score !== undefined && (
                    <span className="ql-act-score" style={{ color: scoreColor(a.score) }}>Score : {a.score}%</span>
                  )}
                  <span className="ql-act-time">{timeAgo(a.date)}</span>
                </div>
              );
            })}
      </div>
      <button className="ql-see-all" onClick={onViewAll}>
        Voir toutes mes activités <i className="bi bi-arrow-right ms-1"></i>
      </button>
    </>
  );
}

// ── AdminActivitesView ────────────────────────────
function AdminActivitesView() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState("");
  const [filter, setFilter]         = useState("tout");

  useEffect(() => {
    api.get("admin/activites/")
      .then(r => setActivities(r.data))
      .catch(err => {
        const status = err?.response?.status;
        if (status === 403 || status === 401) {
          setError("Accès refusé. Votre compte doit avoir le statut Staff dans Django admin.");
        } else {
          setError("Impossible de charger les activités. Vérifiez que le serveur est démarré.");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = activities
    .filter(a => filter === "tout" || a.type === filter)
    .filter(a => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (a.user_name || "").toLowerCase().includes(q) ||
             (a.name || "").toLowerCase().includes(q) ||
             (a.user_classe || "").toLowerCase().includes(q);
    });

  const typeIcon  = { qcm: "bi-patch-check-fill", exercice: "bi-pencil-fill", video: "bi-play-circle-fill", devoir: "bi-pencil-square", evaluation: "bi-clipboard-check-fill" };
  const typeColor = { qcm: "#6f42c1", exercice: "#198754", video: "#f5820d", devoir: "#0d6efd", evaluation: "#dc2626" };
  const typeLabel = { qcm: "QCM", exercice: "Exercice", video: "Vidéo", devoir: "Devoir", evaluation: "Évaluation" };

  const fmtDate = (d) => {
    if (!d) return "–";
    return new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;

  if (error) return (
    <div className="alert alert-danger mt-4">
      <i className="bi bi-shield-lock-fill me-2"></i>{error}
    </div>
  );

  return (
    <div>
      <div className="ql-view-header">
        <h4 className="fw-bold mb-0">Activités de tous les élèves</h4>
        <span className="badge bg-secondary">{activities.length} activités</span>
      </div>

      {/* Filtres */}
      <div className="d-flex gap-2 flex-wrap mb-3 mt-3">
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 220 }}
          placeholder="Rechercher un élève ou une activité…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {["tout", "qcm", "exercice", "video", "devoir", "evaluation"].map(f => (
          <button key={f}
            className={`btn btn-sm ${filter === f ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setFilter(f)}>
            {f === "tout" ? "Tout" : typeLabel[f]}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox fs-1 d-block mb-2"></i>
          Aucune activité trouvée.
        </div>
      ) : (
        <div className="ql-admin-act-table">
          <div className="ql-admin-act-head">
            <span>Élève</span>
            <span>Classe</span>
            <span>Type</span>
            <span>Activité</span>
            <span>Score</span>
            <span>Date</span>
          </div>
          {filtered.map((a, i) => (
            <div key={i} className="ql-admin-act-row">
              <span className="fw-semibold">{a.user_name}</span>
              <span className="text-muted" style={{ fontSize: "0.82rem" }}>{a.user_classe || "–"}</span>
              <span>
                <i className={`bi ${typeIcon[a.type]}`} style={{ color: typeColor[a.type] }}></i>
                {" "}<span style={{ fontSize: "0.82rem" }}>{typeLabel[a.type]}</span>
              </span>
              <span style={{ fontSize: "0.85rem" }}>{a.name}</span>
              <span>
                {a.score != null
                  ? <span className={`badge ${a.score >= 50 ? "bg-success" : "bg-danger"}`}>{a.score}%</span>
                  : a.grade
                    ? <span className="badge" style={{ background: gradeColor(a.grade), color: "#fff" }}>{a.grade}</span>
                    : <span className="text-muted">–</span>}
              </span>
              <span style={{ fontSize: "0.78rem", color: "#64748b" }}>{fmtDate(a.date)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── DevoirDetailView ───────────────────────────────
// Sujet d'un devoir ou d'une évaluation : lecture (copie de l'appareil si
// gardée), téléchargement et « Garder hors connexion ».
function SujetPdf({ cle, url, titre, rubrique, nomFichier }) {
  const [ouvert, setOuvert] = useState(false);
  const local = useFichierHorsConnexion(cle);
  const enLigne = useEnLigne();
  if (!url) return null;
  return (
    <>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <button className="btn btn-outline-primary btn-sm" onClick={() => setOuvert(true)} disabled={!local && !enLigne}>
          <i className="bi bi-arrows-fullscreen me-1"></i>Lire le sujet
        </button>
        {enLigne && (
          <DownloadLink url={url} filename={nomFichier} mimeType="application/pdf" className="btn btn-sm btn-primary">
            <i className="bi bi-download me-1"></i>Télécharger le sujet
          </DownloadLink>
        )}
        <BoutonHorsConnexion cle={cle} url={url} titre={titre} rubrique={rubrique} />
      </div>
      {ouvert && (local
        ? <PdfLocal plein adresse={local.adresse} titre={titre} onClose={() => setOuvert(false)} />
        : <DocumentViewer titre={titre} url={url} onClose={() => setOuvert(false)} />)}
    </>
  );
}

// Copie remise sans réseau : gardée sur l'appareil, envoyée au retour de la connexion.
async function remettreCopie({ nature, element, content, file, setElement, setError, setAttente }) {
  const fd = new FormData();
  if (content.trim()) fd.append("content", content);
  if (file) fd.append("file", file);
  try {
    const r = await api.post(`${nature}/${element.id}/submit/`, fd, {
      transformRequest: [(data, headers) => { delete headers["Content-Type"]; return data; }],
    });
    setElement(prev => ({ ...prev, has_rendu: true, rendu: r.data }));
    return true;
  } catch (err) {
    if (erreurReseau(err)) {
      await mettreCopieEnAttente({ nature, id: element.id, titre: element.title, content: content.trim(), fichier: file });
      setAttente(true);
      return true;
    }
    setError(err?.response?.data?.detail || "Erreur lors de la soumission.");
    return false;
  }
}

function CopieEnAttente() {
  return (
    <div className="alert alert-warning d-flex align-items-start gap-2 mb-3" role="status">
      <i className="bi bi-hourglass-split mt-1"></i>
      <span>Pas de réseau : votre copie est gardée sur l'appareil et partira automatiquement dès le retour de la connexion. L'heure de réception par le serveur fera foi.</span>
    </div>
  );
}

function DevoirDetailView({ devoir: initDevoir, onBack }) {
  const [devoir, setDevoir]       = useState(initDevoir);
  const [numPages, setNumPages]   = useState(null);
  const [content, setContent]     = useState("");
  const [file, setFile]           = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState("");
  const [attente, setAttente]     = useState(false);
  const fileRef                   = useRef(null);

  const rendu    = devoir.rendu;
  const hasRendu = devoir.has_rendu;
  useEffect(() => { marquerOuvert("devoirs", devoir.id); }, [devoir.id]);

  const submit = async () => {
    if (!content.trim() && !file) { setError("Ajoutez une réponse ou un fichier."); return; }
    setSubmitting(true); setError("");
    const ok = await remettreCopie({ nature: "devoirs", element: devoir, content, file, setElement: setDevoir, setError, setAttente });
    if (ok) { setContent(""); setFile(null); }
    setSubmitting(false);
  };

  return (
    <div className="ql-solver">
      {/* Breadcrumb */}
      <div className="ql-breadcrumb">
        <button className="ql-bc-link" onClick={onBack}>
          <i className="bi bi-chevron-left me-1"></i>Devoirs
        </button>
        <i className="bi bi-chevron-right ql-bc-sep"></i>
        <span className="ql-bc-current">{devoir.title}</span>
      </div>

      {/* Header */}
      <div className="ql-solver-header">
        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
          <span className="ql-ex-type-badge"><i className="bi bi-pencil-square me-1"></i>Devoir</span>
          {hasRendu
            ? <span className="badge bg-success">Fait ✓</span>
            : devoir.date_echeance && (() => {
                const diff = Math.round((new Date(devoir.date_echeance) - new Date().setHours(0,0,0,0)) / 86400000);
                if (diff < 0)  return <span className="badge bg-danger">En retard</span>;
                if (diff === 0) return <span className="badge bg-warning text-dark">Aujourd'hui</span>;
                if (diff <= 3) return <span className="badge bg-warning text-dark">Dans {diff}j</span>;
                return null;
              })()
          }
        </div>
        <h4 className="ql-solver-title">{devoir.title}</h4>
        {devoir.description && <p className="ql-solver-statement"><MathText text={devoir.description} /></p>}
        {devoir.image_url && (
          <figure className="my-3">
            <img src={devoir.image_url} alt={`Schéma de l'énoncé : ${devoir.title}`} style={{maxWidth:"100%",width:"760px",height:"auto",display:"block"}} />
            <figcaption className="text-muted mt-1" style={{fontSize:"0.85rem"}}>Schéma de l’énoncé, non à l’échelle.</figcaption>
          </figure>
        )}
        <div className="d-flex gap-2 flex-wrap mt-2">
          {devoir.matiere_nom  && <span className="text-muted" style={{fontSize:"0.85rem"}}><i className="bi bi-book me-1"></i>{devoir.matiere_nom}</span>}
          {devoir.module_title && <span className="text-muted" style={{fontSize:"0.85rem"}}><i className="bi bi-collection me-1"></i>{devoir.module_title}</span>}
          {devoir.date_echeance && <span className="text-muted" style={{fontSize:"0.85rem"}}><i className="bi bi-calendar-check me-1"></i>À rendre le {new Date(devoir.date_echeance).toLocaleDateString("fr-FR")}</span>}
        </div>
      </div>

      {/* Sujet PDF */}
      <SujetPdf cle={`devoir:${devoir.id}`} url={devoir.pdf_url} titre={devoir.title} rubrique="devoirs" nomFichier={`${devoir.title || "devoir"}.pdf`} />
      {attente && <CopieEnAttente />}

      {devoir.corrige_envoye && (
        <CorrigeEleve
          titre={`Corrigé — ${devoir.title}`}
          texte={devoir.bareme_envoye ? `${devoir.corrige_envoye}\n\nBarème\n${devoir.bareme_envoye}` : devoir.corrige_envoye}
        />
      )}

      {/* ── Correction reçue ── */}
      {rendu?.grade && (
        <div className="ql-devoir-correction-card mb-3">
          <div className="ql-devoir-corr-header">
            <i className="bi bi-patch-check-fill me-2 text-success"></i>
            <strong>Devoir corrigé</strong>
            {rendu.graded_by_name && <span className="text-muted ms-2">par {rendu.graded_by_name}</span>}
          </div>
          <div className="ql-devoir-corr-note">Note : <strong style={{ color: gradeColor(rendu.grade) }}>{rendu.grade}</strong></div>
          {rendu.teacher_feedback && (
            <div className="ql-devoir-corr-feedback">{rendu.teacher_feedback}</div>
          )}
          <CorrectionFileViewer url={rendu.correction_file_url} label="Correction du professeur" />
        </div>
      )}

      {/* ── Rendu déjà soumis, pas encore corrigé ── */}
      {hasRendu && !rendu?.grade && (
        <div className="alert alert-success d-flex align-items-center gap-2 mb-3">
          <i className="bi bi-check-circle-fill"></i>
          <span>Devoir remis le {new Date(rendu.submitted_at).toLocaleString("fr-FR")}. En attente de correction.</span>
        </div>
      )}

      {/* ── Formulaire de rendu (masqué si déjà corrigé) ── */}
      {!rendu?.graded_by ? (
        <div className="ql-devoir-submit-card">
          <h6 className="fw-semibold mb-3">
            <i className="bi bi-upload me-2 text-primary"></i>
            {hasRendu ? "Modifier mon rendu" : "Remettre le devoir"}
          </h6>

          <div className="mb-3">
            <label className="form-label small fw-semibold">Réponse écrite (optionnel)</label>
            <textarea
              className="form-control"
              rows={4}
              placeholder="Écrivez vos résultats, calculs, réponses…"
              value={content}
              onChange={e => setContent(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label className="form-label small fw-semibold">Fichier (photo ou PDF)</label>
            <input
              ref={fileRef}
              type="file"
              className="form-control"
              accept="image/*,application/pdf"
              onChange={e => setFile(e.target.files[0] || null)}
            />
            <ConseilScan />
            {rendu?.file_url && !file && (
              <div className="mt-1 text-muted" style={{fontSize:"0.8rem"}}>
                <i className="bi bi-paperclip me-1"></i>
                Fichier précédent : <DownloadLink url={rendu.file_url} filename={rendu.file_url.split("?")[0].split("/").pop()} downloadOnWeb={false} titre="Mon rendu">voir</DownloadLink>
              </div>
            )}
          </div>

          {error && <div className="alert alert-danger py-2">{error}</div>}

          <button
            className="btn btn-primary w-100"
            disabled={submitting}
            onClick={submit}
          >
            {submitting
              ? <><span className="spinner-border spinner-border-sm me-2"></span>Envoi…</>
              : <><i className="bi bi-send-fill me-1"></i>{hasRendu ? "Mettre à jour" : "Soumettre le devoir"}</>}
          </button>
        </div>
      ) : (
        <div className="alert alert-info d-flex gap-2">
          <i className="bi bi-lock-fill mt-1 flex-shrink-0"></i>
          <span>La note attribuée par le professeur est définitive. Le devoir ne peut plus être modifié. Si vous souhaitez contester votre note, vous pouvez contacter le professeur via la <strong>messagerie</strong> pour en discuter.</span>
        </div>
      )}
    </div>
  );
}

// ── DevoirsView ────────────────────────────────────
// Corrigé envoyé par l'enseignant : à voir en plein écran ou à télécharger.
function CorrigeEleve({ titre, url, texte }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <div className="ql-devoir-correction-card mb-3">
      <div className="ql-devoir-corr-header">
        <i className="bi bi-patch-check-fill me-2 text-success"></i>
        <strong>Ton enseignant t'a envoyé la correction</strong>
      </div>
      <div className="ql-doc-buttons">
        <button type="button" className="btn btn-success btn-sm" onClick={() => setOuvert(true)}>
          <i className="bi bi-eye me-1"></i>Voir la correction
        </button>
        {url && (
          <DownloadLink url={url} filename={`${titre}.pdf`} mimeType="application/pdf" className="btn btn-outline-success btn-sm">
            <i className="bi bi-download me-1"></i>Télécharger la correction
          </DownloadLink>
        )}
      </div>
      {ouvert && <DocumentViewer titre={titre} url={url} texte={texte} onClose={() => setOuvert(false)} />}
    </div>
  );
}

// Mémorise la position dans une liste avant d'ouvrir un élément, puis la restaure au retour.
function useListScrollMemory(selected) {
  const saved = useRef(null);
  const scroller = () => document.querySelector(".ql-content");
  const open = (select) => (item) => {
    saved.current = { content: scroller()?.scrollTop || 0, page: window.scrollY || 0 };
    select(item);
    requestAnimationFrame(() => {
      if (scroller()) scroller().scrollTop = 0;
      window.scrollTo(0, 0);
    });
  };
  useEffect(() => {
    if (selected || !saved.current) return;
    const position = saved.current;
    saved.current = null;
    requestAnimationFrame(() => {
      if (scroller()) scroller().scrollTop = position.content;
      window.scrollTo(0, position.page);
    });
  }, [selected]);
  return open;
}

function DevoirsView({ initialDevoirId = null }) {
  const [devoirs, setDevoirs]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const openDevoir = useListScrollMemory(selected)(setSelected);

  useEffect(() => {
    getAvecCache("devoirs/")
      .then(r => {
        setDevoirs(r.data);
        const demande = initialDevoirId && r.data.find(d => d.id === initialDevoirId);
        if (demande) setSelected(demande);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- remonté par une clé quand le devoir demandé change

  if (selected) {
    return <DevoirDetailView devoir={selected} onBack={() => setSelected(null)} />;
  }

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;

  if (devoirs.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-pencil-square fs-1 d-block mb-3"></i>
        <p>Aucun devoir disponible pour l'instant.</p>
      </div>
    );
  }

  return (
    <div className="ql-eval-list">
      <h5 className="fw-bold mb-4">Devoirs</h5>
      <div className="row g-3">
        {devoirs.map(d => {
          const submitted = d.has_rendu;
          const graded    = d.rendu?.grade;
          let badge = null;
          if (submitted && graded) {
            badge = <span className="badge bg-success ms-2">Corrigé ✓</span>;
          } else if (submitted) {
            badge = <span className="badge bg-success ms-2">Fait ✓</span>;
          } else if (d.date_echeance) {
            const diff = Math.round((new Date(d.date_echeance) - new Date().setHours(0,0,0,0)) / 86400000);
            if (diff < 0)  badge = <span className="badge bg-danger ms-2">En retard</span>;
            else if (diff === 0) badge = <span className="badge bg-warning text-dark ms-2">Aujourd'hui</span>;
            else if (diff <= 3)  badge = <span className="badge bg-warning text-dark ms-2">Dans {diff}j</span>;
          }
          return (
            <div key={d.id} className="col-12 col-md-6">
              <div className="ql-eval-card" style={{ cursor: "pointer" }} onClick={() => openDevoir(d)}>
                <div className="ql-eval-icon" style={{ background: submitted ? "#f0fdf4" : "#fff3e0", color: submitted ? "#16a34a" : "#f5820d" }}>
                  <i className={`bi bi-${submitted ? "check-circle-fill" : "pencil-square"}`}></i>
                </div>
                <div className="ql-eval-body">
                  <div className="ql-eval-title"><PointNouveauContenu type="devoirs" id={d.id} />{d.title}{badge}</div>
                  {d.description && <div className="ql-eval-desc">{d.description}</div>}
                  <div className="ql-eval-meta">
                    {d.matiere_nom  && <span><i className="bi bi-book me-1"></i>{d.matiere_nom}</span>}
                    {d.module_title && <span><i className="bi bi-collection me-1"></i>{d.module_title}</span>}
                    {d.date_echeance && (
                      <span><i className="bi bi-calendar-check me-1"></i>À rendre le {new Date(d.date_echeance).toLocaleDateString("fr-FR")}</span>
                    )}
                  </div>
                </div>
                <div className="ql-eval-actions">
                  <button className="btn btn-sm btn-outline-primary" onClick={e => { e.stopPropagation(); openDevoir(d); }}>
                    <i className="bi bi-arrow-right me-1"></i>Ouvrir
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── EvaluationsView ───────────────────────────────
// ── EvaluationDetailView ─────────────────────────────
function EvaluationDetailView({ evaluation: initEval, onBack }) {
  const [evaluation, setEvaluation] = useState(initEval);
  const [numPages,   setNumPages]   = useState(null);
  const [content,    setContent]    = useState("");
  const [file,       setFile]       = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");
  const [attente,    setAttente]    = useState(false);
  const fileRef = useRef(null);

  const rendu    = evaluation.rendu;
  const hasRendu = evaluation.has_rendu;
  useEffect(() => { marquerOuvert("evaluations", evaluation.id); }, [evaluation.id]);

  const submit = async () => {
    if (!content.trim() && !file) { setError("Ajoutez une réponse ou un fichier."); return; }
    setSubmitting(true); setError("");
    const ok = await remettreCopie({ nature: "evaluations", element: evaluation, content, file, setElement: setEvaluation, setError, setAttente });
    if (ok) { setContent(""); setFile(null); }
    setSubmitting(false);
  };

  const pageWidth = Math.min(window.innerWidth - 80, 750);

  return (
    <div className="ql-eval-detail">
      {/* En-tête */}
      <div className="ql-devoir-detail-header mb-3">
        <button className="btn btn-sm btn-outline-secondary me-3" onClick={onBack}>
          <i className="bi bi-arrow-left me-1"></i>Retour
        </button>
        <div className="flex-grow-1">
          <h5 className="fw-bold mb-0">{evaluation.title}</h5>
          {evaluation.description && <p className="ql-solver-statement mt-1 mb-0">{evaluation.description}</p>}
          <div className="d-flex gap-2 flex-wrap mt-1">
            {evaluation.matiere_nom && <span className="text-muted" style={{fontSize:"0.85rem"}}><i className="bi bi-book me-1"></i>{evaluation.matiere_nom}</span>}
            {evaluation.classe_nom  && <span className="text-muted" style={{fontSize:"0.85rem"}}><i className="bi bi-people me-1"></i>{evaluation.classe_nom}</span>}
            {evaluation.date_eval   && <span className="text-muted" style={{fontSize:"0.85rem"}}><i className="bi bi-calendar me-1"></i>{new Date(evaluation.date_eval).toLocaleDateString("fr-FR")}</span>}
          </div>
        </div>
      </div>

      {/* Sujet PDF */}
      <SujetPdf cle={`evaluation:${evaluation.id}`} url={evaluation.pdf_url} titre={evaluation.title} rubrique="evaluations" nomFichier={`${evaluation.title || "evaluation"}.pdf`} />
      {attente && <CopieEnAttente />}

      {evaluation.corrige_url && (
        <CorrigeEleve titre={`Corrigé — ${evaluation.title}`} url={evaluation.corrige_url} />
      )}

      {/* Correction reçue */}
      {rendu?.grade && (
        <div className="ql-devoir-correction-card mb-3">
          <div className="ql-devoir-corr-header">
            <i className="bi bi-patch-check-fill me-2 text-success"></i>
            <strong>Évaluation corrigée</strong>
            {rendu.graded_by_name && <span className="text-muted ms-2">par {rendu.graded_by_name}</span>}
          </div>
          <div className="ql-devoir-corr-note">Note : <strong style={{ color: gradeColor(rendu.grade) }}>{rendu.grade}</strong></div>
          {rendu.teacher_feedback && (
            <div className="ql-devoir-corr-feedback">{rendu.teacher_feedback}</div>
          )}
          <CorrectionFileViewer url={rendu.correction_file_url} label="Correction du professeur" />
        </div>
      )}

      {/* Déjà soumis, pas encore corrigé */}
      {hasRendu && !rendu?.grade && (
        <div className="alert alert-success d-flex align-items-center gap-2 mb-3">
          <i className="bi bi-check-circle-fill"></i>
          <span>Évaluation remise le {new Date(rendu.submitted_at).toLocaleString("fr-FR")}. En attente de correction.</span>
        </div>
      )}

      {/* Formulaire de soumission (masqué si déjà corrigé) */}
      {!rendu?.graded_by ? (
        <div className="ql-devoir-submit-card">
          <h6 className="fw-semibold mb-3">
            <i className="bi bi-upload me-2 text-primary"></i>
            {hasRendu ? "Modifier mon rendu" : "Remettre l'évaluation"}
          </h6>
          <div className="mb-3">
            <label className="form-label small fw-semibold">Réponse écrite (optionnel)</label>
            <textarea
              className="form-control"
              rows={4}
              placeholder="Écrivez vos réponses…"
              value={content}
              onChange={e => setContent(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label small fw-semibold">Fichier (photo ou PDF)</label>
            <input
              ref={fileRef}
              type="file"
              className="form-control"
              accept="image/*,application/pdf"
              onChange={e => setFile(e.target.files[0] || null)}
            />
            <ConseilScan />
          </div>
          {error && <div className="alert alert-danger py-2">{error}</div>}
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={submitting}
          >
            {submitting
              ? <><span className="spinner-border spinner-border-sm me-2"></span>Envoi…</>
              : <><i className="bi bi-send me-2"></i>{hasRendu ? "Mettre à jour" : "Soumettre"}</>
            }
          </button>
        </div>
      ) : (
        <div className="alert alert-info d-flex gap-2">
          <i className="bi bi-lock-fill mt-1 flex-shrink-0"></i>
          <span>La note attribuée par le professeur est définitive. L'évaluation ne peut plus être modifiée. Si vous souhaitez contester votre note, vous pouvez contacter le professeur via la <strong>messagerie</strong> pour en discuter.</span>
        </div>
      )}
    </div>
  );
}

function EvaluationsView({ initialEvaluation = null }) {
  const [evals,    setEvals]   = useState([]);
  const [loading,  setLoading] = useState(true);
  // Depuis l'annonce « Ma progression », seul l'identifiant est connu : la fiche
  // complète est reprise de la liste.
  const [selected, setSelected] = useState(initialEvaluation?.pdf_url ? initialEvaluation : null);
  const openEvaluation = useListScrollMemory(selected)(setSelected);

  useEffect(() => {
    getAvecCache("evaluations/")
      .then(r => {
        setEvals(r.data);
        if (initialEvaluation && !initialEvaluation.pdf_url) {
          const fiche = r.data.find(e => e.id === initialEvaluation.id);
          if (fiche) setSelected(fiche);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- remonté par une clé quand l'évaluation demandée change

  if (selected) {
    return <EvaluationDetailView evaluation={selected} onBack={() => setSelected(null)} />;
  }

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;

  if (evals.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-clipboard2-x fs-1 d-block mb-3"></i>
        <p>Aucune évaluation disponible pour l'instant.</p>
      </div>
    );
  }

  return (
    <div className="ql-eval-list">
      <h5 className="fw-bold mb-4">Évaluations</h5>
      <div className="row g-3">
        {evals.map(ev => {
          const isCorrige = ev.rendu?.grade;
          const isFait    = ev.has_rendu && !isCorrige;
          return (
            <div key={ev.id} className="col-12 col-md-6">
              <div
                className="ql-eval-card"
                style={{ cursor: "pointer" }}
                onClick={() => openEvaluation(ev)}
              >
                <div className="ql-eval-icon">
                  <i className="bi bi-file-earmark-text-fill"></i>
                </div>
                <div className="ql-eval-body">
                  <div className="ql-eval-title mb-1" style={{ overflowWrap: "anywhere" }}><PointNouveauContenu type="evaluations" id={ev.id} />{ev.title}</div>
                  <div className="d-flex align-items-center gap-1 mb-1 flex-wrap">
                    {ev.obligatoire && (
                      <span className="ql-devoir-badge" style={{ background: "#fee2e2", color: "#b91c1c", fontSize: "0.7rem" }}>
                        Obligatoire
                      </span>
                    )}
                    {isCorrige && (
                      <span className="ql-devoir-badge" style={{background:"#dcfce7",color:"#15803d",fontSize:"0.7rem"}}>
                        Corrigé ✓
                      </span>
                    )}
                    {isFait && (
                      <span className="ql-devoir-badge" style={{background:"#dbeafe",color:"#1d4ed8",fontSize:"0.7rem"}}>
                        Remis ✓
                      </span>
                    )}
                  </div>
                  {ev.description && <div className="ql-eval-desc">{ev.description}</div>}
                  <div className="ql-eval-meta">
                    {ev.matiere_nom && <span><i className="bi bi-book me-1"></i>{ev.matiere_nom}</span>}
                    {ev.classe_nom  && <span><i className="bi bi-people me-1"></i>{ev.classe_nom}</span>}
                    {ev.date_eval   && <span><i className="bi bi-calendar me-1"></i>{new Date(ev.date_eval).toLocaleDateString("fr-FR")}</span>}
                  </div>
                </div>
                <div className="ql-eval-actions" onClick={e => e.stopPropagation()}>
                  <button className="btn btn-sm btn-outline-primary" onClick={() => openEvaluation(ev)}>
                    <i className="bi bi-eye me-1"></i>Ouvrir
                  </button>
                  <DownloadLink url={ev.pdf_url} filename={`${ev.title || "evaluation"}.pdf`} mimeType="application/pdf" className="btn btn-sm btn-primary">
                    <i className="bi bi-download me-1"></i>PDF
                  </DownloadLink>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── ActivitesView ─────────────────────────────────
function ActivitesView({ learningMinutes = 0 }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState("tout");

  useEffect(() => {
    api.get("activites/").then(r => setActivities(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "tout" ? activities : activities.filter(a => a.type === filter);

  // Données pour le graphique d'évolution (QCM + exercices avec score)
  const scored = [...activities]
    .filter(a => a.score !== null && a.score !== undefined)
    .reverse(); // chronologique
  const chartData = {
    labels: scored.map((a, i) => `${i + 1}`),
    datasets: [{
      label: "Score (%)",
      data: scored.map(a => a.score),
      borderColor: "#0d6efd",
      backgroundColor: "rgba(13,110,253,0.08)",
      pointBackgroundColor: scored.map(a => scoreColor(a.score)),
      pointRadius: 5,
      fill: true,
      tension: 0.35,
    }],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const idx = items[0].dataIndex;
            return scored[idx]?.name || "";
          },
          label: (item) => `Score : ${item.raw}%`,
        },
      },
    },
    scales: {
      y: { min: 0, max: 100, ticks: { callback: v => `${v}%` }, grid: { color: "#f1f5f9" } },
      x: { grid: { display: false }, ticks: { display: false } },
    },
  };

  // Statistiques
  const qcmResults   = activities.filter(a => a.type === "qcm");
  const exResults    = activities.filter(a => a.type === "exercice");
  const vidResults   = activities.filter(a => a.type === "video");
  const devoirResults = activities.filter(a => a.type === "devoir");
  const evalResults   = activities.filter(a => a.type === "evaluation");
  const allScores   = activities.filter(a => a.score != null).map(a => a.score);
  const avgScore    = allScores.length ? Math.round(allScores.reduce((s, v) => s + v, 0) / allScores.length) : null;
  const bestScore   = allScores.length ? Math.max(...allScores) : null;
  const m           = avgScore !== null ? mention(avgScore) : null;

  const fmtTime = (mins) => {
    if (!mins || mins < 1) return "0 min";
    const h = Math.floor(mins / 60);
    const m2 = mins % 60;
    if (h === 0) return `${m2} min`;
    if (m2 === 0) return `${h}h`;
    return `${h}h ${m2}min`;
  };
  // Utilise le vrai temps tracké par le heartbeat
  const estimatedMins = learningMinutes;

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;

  return (
    <div>
      <div className="ql-view-header">
        <h3 className="ql-view-title"><i className="bi bi-clock-history me-2"></i>Mes activités</h3>
        <span className="ql-view-count">{activities.length} activité{activities.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Résumé */}
      <div className="ql-act-stat-grid">
        <div className="ql-act-stat-card" style={{ borderColor: "#6f42c1" }}>
          <i className="bi bi-grid-3x3-gap-fill" style={{ color: "#6f42c1" }}></i>
          <div className="ql-act-stat-val">{qcmResults.length}</div>
          <div className="ql-act-stat-lbl">QCM réalisés</div>
          <div className="ql-act-stat-sub">{qcmResults.filter(q => q.passed).length} réussis</div>
        </div>
        <div className="ql-act-stat-card" style={{ borderColor: "#198754" }}>
          <i className="bi bi-pencil-fill" style={{ color: "#198754" }}></i>
          <div className="ql-act-stat-val">{exResults.length}</div>
          <div className="ql-act-stat-lbl">Exercices faits</div>
          <div className="ql-act-stat-sub">&nbsp;</div>
        </div>
        <div className="ql-act-stat-card" style={{ borderColor: "#f5820d" }}>
          <i className="bi bi-play-circle-fill" style={{ color: "#f5820d" }}></i>
          <div className="ql-act-stat-val">{vidResults.length}</div>
          <div className="ql-act-stat-lbl">Vidéos regardées</div>
          <div className="ql-act-stat-sub">&nbsp;</div>
        </div>
        {avgScore !== null && (
          <div className="ql-act-stat-card" style={{ borderColor: scoreColor(avgScore) }}>
            <i className="bi bi-trophy-fill" style={{ color: scoreColor(avgScore) }}></i>
            <div className="ql-act-stat-val" style={{ color: scoreColor(avgScore) }}>{avgScore}%</div>
            <div className="ql-act-stat-lbl">Score moyen</div>
            <div className="ql-act-stat-sub">Meilleur : {bestScore}%</div>
          </div>
        )}
        <div className="ql-act-stat-card" style={{ borderColor: "#0dcaf0" }}>
          <i className="bi bi-clock-fill" style={{ color: "#0dcaf0" }}></i>
          <div className="ql-act-stat-val" style={{ color: "#0891b2" }}>{fmtTime(estimatedMins)}</div>
          <div className="ql-act-stat-lbl">Temps d'apprentissage</div>
          <div className="ql-act-stat-sub">Temps total sur la plateforme</div>
        </div>
        {devoirResults.length > 0 && (
          <div className="ql-act-stat-card" style={{ borderColor: "#0d6efd" }}>
            <i className="bi bi-pencil-square" style={{ color: "#0d6efd" }}></i>
            <div className="ql-act-stat-val">{devoirResults.length}</div>
            <div className="ql-act-stat-lbl">Devoirs remis</div>
            <div className="ql-act-stat-sub">{devoirResults.filter(d => d.grade).length} corrigé{devoirResults.filter(d => d.grade).length !== 1 ? "s" : ""}</div>
          </div>
        )}
        {evalResults.length > 0 && (
          <div className="ql-act-stat-card" style={{ borderColor: "#dc2626" }}>
            <i className="bi bi-clipboard-check-fill" style={{ color: "#dc2626" }}></i>
            <div className="ql-act-stat-val">{evalResults.length}</div>
            <div className="ql-act-stat-lbl">Évaluations remises</div>
            <div className="ql-act-stat-sub">{evalResults.filter(e => e.grade).length} corrigée{evalResults.filter(e => e.grade).length !== 1 ? "s" : ""}</div>
          </div>
        )}
      </div>

      {/* Mention */}
      {m && (
        <div className="ql-mention-card" style={{ background: m.bg, borderColor: m.color }}>
          <i className="bi bi-award-fill me-2" style={{ color: m.color, fontSize: "1.3rem" }}></i>
          <div>
            <div style={{ fontWeight: 700, color: m.color, fontSize: "1rem" }}>Mention : {m.label}</div>
            <div style={{ fontSize: "0.8rem", color: "#64748b" }}>Basée sur ton score moyen de {avgScore}%</div>
          </div>
        </div>
      )}

      {/* Graphique d'évolution */}
      {scored.length >= 2 && (
        <div className="ql-panel-card mt-3">
          <div className="ql-panel-head mb-2">
            <span className="fw-bold">Évolution de tes scores</span>
            <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>{scored.length} évaluations</span>
          </div>
          <div style={{ height: 180 }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="ql-tabs mt-3">
        {[
          { key: "tout",       label: "Tout" },
          { key: "qcm",       label: "QCM" },
          { key: "exercice",  label: "Exercices" },
          { key: "video",     label: "Vidéos" },
          { key: "devoir",    label: "Devoirs" },
          { key: "evaluation",label: "Évaluations" },
        ].map(f => (
          <button key={f.key} className={`ql-tab${filter === f.key ? " active" : ""}`} onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Liste */}
      {filtered.length === 0
        ? <div className="ql-empty-state mt-3"><i className="bi bi-clock"></i><p>Aucune activité pour le moment.</p></div>
        : (
          <div className="ql-act-full-list mt-2">
            {filtered.map((a, i) => {
              const meta = activityMeta(a.type);
              return (
                <div key={i} className="ql-act-full-row">
                  <div className="ql-act-full-icon" style={{ background: meta.color + "18" }}>
                    <i className={`bi ${meta.icon}`} style={{ color: meta.color }}></i>
                  </div>
                  <div className="ql-act-full-body">
                    <div className="ql-act-full-type">{meta.label}</div>
                    <div className="ql-act-full-name">{a.name}</div>
                  </div>
                  <div className="ql-act-full-right">
                    {a.score !== null && a.score !== undefined && (
                      <div className="ql-act-full-score" style={{ color: scoreColor(a.score) }}>
                        {a.score}%
                        {a.passed === true && <span className="ql-act-pass-badge ms-1">✓ Réussi</span>}
                        {a.passed === false && <span className="ql-act-fail-badge ms-1">✗ Échoué</span>}
                      </div>
                    )}
                    {a.score == null && a.grade && (
                      <div className="ql-act-full-score" style={{ color: gradeColor(a.grade) }}>{a.grade}</div>
                    )}
                    {a.score == null && !a.grade && (a.type === "devoir" || a.type === "evaluation") && (
                      <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>En attente</div>
                    )}
                    <div className="ql-act-full-time">{timeAgo(a.date)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

const STUDENT_AREAS = {
  dashboard: "STUDENT_DASHBOARD",
  modules: "COURSES",
  lecons: "COURSES",
  videos: "COURSES",
  ressources: "COURSES",
  exercices: "EXERCISES",
  exercice: "EXERCISES",
  qcm: "QCM",
  messages: "MESSAGING",
  notifications: "NOTIFICATIONS",
  profil: "ACCOUNT",
  abonnement: "ACCOUNT",
};

function StudentDashboard({ user, summary, modules, onLogout, onUserUpdate, onShowTour, onAreaChange, consumptionOnly = false, onRefreshAccount }) {
  const chapters = modules; // alias de compatibilité interne
  const [view, setView]         = useState({ name: "dashboard" });

  useEffect(() => {
    onAreaChange?.(STUDENT_AREAS[view.name] || "STUDENT_DASHBOARD");
  }, [onAreaChange, view.name]);
  const [activeTab, setActiveTab] = useState("continuer");
  const [banner, setBanner]         = useState(null);
  const [bannerVisible, setBannerVisible] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const [localUser, setLocalUser] = useState(user);
  const activeManuel = localUser?.accessible_manuels?.find((m) => m.active) || localUser?.accessible_manuels?.[0] || null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [parametresOuverts, setParametresOuverts] = useState(false);
  const [progressionOpen, setProgressionOpen] = useState(false);

  // Verrouille le scroll du body sur mobile quand le sidebar est ouvert
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [sidebarOpen]);
  const [astuceIdx,  setAstuceIdx]  = useState(0);
  const [astuceFade, setAstuceFade] = useState(true);
  const [sideDevoirs, setSideDevoirs] = useState([]);
  const [showRenewal, setShowRenewal] = useState(false);
  const [floatCalc,  setFloatCalc]   = useState(false);
  useEnvoiAutomatiqueDesCopies();

  useEffect(() => {
    const timer = setInterval(() => {
      setAstuceFade(false);
      setTimeout(() => {
        setAstuceIdx(i => (i + 1) % ASTUCES.length);
        setAstuceFade(true);
      }, 400);
    }, 15000);
    return () => clearInterval(timer);
  }, []);

  // Android visualViewport : ajuste la hauteur quand le clavier s'ouvre
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      const shell = document.querySelector(".ql-shell");
      if (shell) shell.style.height = vv.height + "px";
      document.documentElement.style.setProperty("--ql-viewport-height", `${vv.height}px`);
      document.documentElement.style.setProperty("--ql-viewport-top", `${vv.offsetTop}px`);
    };
    onResize();
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    return () => { vv.removeEventListener("resize", onResize); vv.removeEventListener("scroll", onResize); document.documentElement.style.removeProperty("--ql-viewport-height"); document.documentElement.style.removeProperty("--ql-viewport-top"); const shell = document.querySelector(".ql-shell"); if (shell) shell.style.height = ""; };
  }, []);

  // Quick Lives
  const [lives, setLives] = useState([]);
  const [endedLives, setEndedLives] = useState([]);
  const prevLivesRef = useRef([]);

  const refreshLives = useCallback(() => {
    api.get("lives/").then(r => {
      const newLives = r.data || [];
      const prev = prevLivesRef.current;

      // Détecter les transitions vers "terminé"
      const justEnded = newLives.filter(nl => {
        const prevLive = prev.find(pl => pl.id === nl.id);
        return nl.statut === "termine" && prevLive && prevLive.statut !== "termine";
      });

      prevLivesRef.current = newLives;

      if (justEnded.length > 0) {
        setEndedLives(existing => {
          const existingIds = new Set(existing.map(l => l.id));
          return [...existing, ...justEnded.filter(l => !existingIds.has(l.id))];
        });
      }

      // Ne mettre à jour le state QUE si quelque chose a changé :
      // nombre de lives différent, ou au moins un statut différent.
      // Pendant un live actif sans changement, setLives n'est PAS appelé → zéro re-render.
      const hasChange =
        newLives.length !== prev.length ||
        newLives.some(nl => {
          const pl = prev.find(p => p.id === nl.id);
          return !pl || pl.statut !== nl.statut || pl.youtube_id !== nl.youtube_id;
        });

      if (hasChange) {
        setLives(newLives);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    refreshLives();
    // 15s pour une détection rapide du passage à "terminé"
    const timer = setInterval(refreshLives, 15000);
    return () => clearInterval(timer);
  }, [refreshLives]);

  // Appelé quand le décompte atteint zéro → passer en_cours côté local + API
  // Met aussi à jour prevLivesRef pour que refreshLives détecte bien la transition vers "terminé"
  const handleLiveStatutChange = useCallback((liveId, newStatut) => {
    setLives(prev => {
      const updated = prev.map(l => l.id === liveId ? { ...l, statut: newStatut } : l);
      prevLivesRef.current = updated;
      return updated;
    });
    api.patch(`lives/${liveId}/`, { statut: newStatut }).catch(() => {});
  }, []);

  // Charger les prochains devoirs pour le panneau latéral
  useEffect(() => {
    api.get("devoirs/").then(r => {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const upcoming = (r.data || [])
        .filter(d => !d.date_echeance || new Date(d.date_echeance) >= today)
        .slice(0, 3);
      setSideDevoirs(upcoming);
    }).catch(() => {});
  }, []);

  function handleProfileSave(updated) {
    setLocalUser(u => ({ ...u, ...updated }));
    if (onUserUpdate) onUserUpdate(prev => ({ ...prev, ...updated }));
  }

  const dismissBanner = () => setBannerVisible(false);

  // Pastilles rouges (messages non lus) : rechargées à chaque changement d'écran
  const compteurs = useCompteurs(view.name);
  const totalPastilles = ["messages", "lecons", "videos", "exercices", "evaluations", "devoirs"]
    .reduce((total, cle) => total + (compteurs[cle] || 0), 0);
  useNotificationsDeLaRubrique(RUBRIQUES_ELEVE[view.name], compteurs.notifications);
  const [visite, setVisite] = useState(() => !visiteDejaVue("eleve", user));

  // Chaque contenu nouveau garde sa pastille jusqu'à ce que l'élève l'ouvre.
  useEffect(() => {
    if (view.name === "exercice-solver" && view.data?.exercice?.id) marquerOuvert("exercices", view.data.exercice.id);
  }, [view.name, view.data]);

  // Épreuves à traiter impérativement (annonce sous « Devoirs » dans Ma progression)
  const [epreuves, setEpreuves] = useState([]);
  useEffect(() => {
    api.get("evaluations/obligatoires/").then(r => setEpreuves(r.data || [])).catch(() => {});
  }, [view.name]);
  // Une épreuve rendue quitte la carte, comme un devoir rendu ; les retards restent affichés.
  const epreuvesARendre = epreuves.filter(e => !e.rendu_le);

  // Search state
  const [searchQ, setSearchQ]         = useState("");
  const [showSearch, setShowSearch]   = useState(false);
  const [searchResults, setSearchResults] = useState(EMPTY_SEARCH_RESULTS);
  const [searchState, setSearchState] = useState({ loading: false, error: false, page: 1, hasNext: false });
  const searchTimerRef = useRef(null);
  const searchRequestRef = useRef(0);

  const CALC_FLOAT_PAGES = ["devoirs", "evaluations", "exercices", "qcm", "messages"];
  const contentRef = useRef(null);
  const pendingScrollRef = useRef(null);
  const currentScroll = () => ({ content: contentRef.current?.scrollTop || 0, page: window.scrollY || 0 });
  const restoreScroll = useCallback(() => {
    const saved = pendingScrollRef.current;
    if (!saved) return;
    pendingScrollRef.current = null;
    requestAnimationFrame(() => {
      if (contentRef.current) contentRef.current.scrollTop = saved.content;
      window.scrollTo(0, saved.page);
    });
  }, []);
  const navigateBack = (name, data, scroll) => {
    navigate(name, data);
    pendingScrollRef.current = scroll || null;
  };

  const navigate = (name, data = null) => {
    pendingScrollRef.current = null;
    if (name === "calculatrice" && CALC_FLOAT_PAGES.includes(view.name)) {
      setFloatCalc(true);
      setSidebarOpen(false);
      return;
    }
    setView({ name, data });
    setSidebarOpen(false);
  };

  useEffect(() => () => window.clearTimeout(searchTimerRef.current), []);

  const loadSearchPage = async (query, page = 1) => {
    const requestId = ++searchRequestRef.current;
    setSearchState(previous => ({ ...previous, loading: true, error: false }));
    try {
      const data = await searchCatalogue(query, { page, pageSize: 5 });
      if (requestId !== searchRequestRef.current) return;
      setSearchResults(previous => {
        if (page === 1) return { ...EMPTY_SEARCH_RESULTS, ...data.results };
        const merged = {};
        Object.keys(EMPTY_SEARCH_RESULTS).forEach(key => {
          const suite = data.results[key] || EMPTY_SEARCH_RESULTS[key];
          merged[key] = { ...suite, items: [...previous[key].items, ...suite.items] };
        });
        return merged;
      });
      setSearchState({ loading: false, error: false, page: data.page, hasNext: data.has_next });
    } catch (requestError) {
      if (requestId === searchRequestRef.current) {
        setSearchState(previous => ({ ...previous, loading: false, error: true }));
      }
    }
  };

  const handleSearch = (q) => {
    setSearchQ(q);
    window.clearTimeout(searchTimerRef.current);
    searchRequestRef.current += 1;
    setSearchResults(EMPTY_SEARCH_RESULTS);
    if (!q.trim()) {
      setSearchState({ loading: false, error: false, page: 1, hasNext: false });
      return;
    }
    searchTimerRef.current = window.setTimeout(() => loadSearchPage(q.trim(), 1), 300);
  };

  const handleSearchSelect = async (type, item) => {
    setSearchQ("");
    setShowSearch(false);
    try {
      if (["lesson", "video", "audio", "chapter"].includes(type)) {
        const ch = chapters.find(c => c.id === item.module);
        if (ch) navigate("module", buildModuleSearchTarget(type, item, ch));
        else navigate({ video: "videos", audio: "audios" }[type] || "dashboard");
      } else if (type === "devoir") {
        navigate("devoirs", { devoirId: item.id });
      } else if (type === "exercise") {
        const response = await api.get(`exercices/${item.id}/`);
        navigate("exercice-solver", { exercice: response.data, back: "exercices" });
      } else if (type === "qcm") {
        const response = await api.get(`qcms/${item.id}/`);
        navigate("qcm-player", { qcm: response.data, back: "qcm" });
      } else if (type === "evaluation") {
        const response = await api.get(`evaluations/${item.id}/`);
        navigate("evaluations", { evaluation: response.data });
      }
    } catch (selectionError) {
      qlAlert("Ce contenu n’est plus accessible. Actualisez votre abonnement ou votre manuel.");
    }
  };

  // Données de progression — chargées et rafraîchissables localement
  const [stats, setStats] = useState(summary || {});
  const progressionModules = Object.fromEntries((stats.modules_progress || []).map((m) => [m.module_id, m]));
  const modulesAContinuer = [...chapters]
    .sort((a, b) => {
      const da = progressionModules[a.id]?.last_activity || "";
      const db = progressionModules[b.id]?.last_activity || "";
      return da === db ? 0 : db.localeCompare(da);
    })
    .slice(0, 3);
  const refreshStats = useCallback(() => {
    api.get("dashboard/").then(r => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { refreshStats(); }, [refreshStats]);

  // Bannière superadmin — réapparaît à chaque connexion
  useEffect(() => {
    api.get("banner/").then(r => {
      setBanner(r.data || null);
      setBannerVisible(true);
    }).catch(() => {});
  }, []);

  // Heartbeat : envoie un ping toutes les 60s pour comptabiliser le temps réel
  useEffect(() => {
    const sendPing = () => api.post("ping/").catch(() => {});
    sendPing(); // premier ping à l'ouverture
    const interval = setInterval(sendPing, 60_000);
    return () => clearInterval(interval);
  }, []);

  const prenom        = (localUser?.nom_complet || localUser?.username || "Élève").split(" ")[0];
  const chapCompleted       = stats.chapters_completed    ?? 0;
  const exercisesDone       = stats.exercises_done        ?? 0;
  const qcmPassed           = stats.qcm_passed            ?? 0;
  const videosViewed        = stats.videos_viewed         ?? 0;
  const avgScore            = stats.average_score         ?? 0;
  const learningMinutes     = stats.learning_minutes      ?? 0;
  const totalChapters       = stats.total_chapters        ?? 1;
  const totalVideos         = stats.total_videos          ?? 1;
  const totalExercises      = stats.total_exercises       ?? 1;
  const totalQcms           = stats.total_qcms            ?? 1;
  const devoirsSubmitted    = stats.devoirs_submitted     ?? 0;
  const totalDevoirs        = stats.total_devoirs         ?? 0;
  const evaluationsSubmitted = stats.evaluations_submitted ?? 0;
  const totalEvaluations    = stats.total_evaluations     ?? 0;

  const formatTime = (mins) => {
    if (mins < 1) return "0 min";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  };

  const totalDone = chapCompleted + exercisesDone + qcmPassed + videosViewed + devoirsSubmitted + evaluationsSubmitted;
  const totalMax  = (stats.total_chapters    ?? 0)
                  + (stats.total_exercises   ?? 0)
                  + (stats.total_qcms        ?? 0)
                  + (stats.total_videos      ?? 0)
                  + (stats.total_devoirs     ?? 0)
                  + (stats.total_evaluations ?? 0);
  const globalPct = totalMax > 0 ? Math.min(100, Math.round((totalDone / totalMax) * 100)) : 0;

  const donutData = {
    datasets: [{ data: [globalPct, 100 - globalPct], backgroundColor: ["#0d6efd", "#e8f2fb"], borderWidth: 0 }],
  };

  const sidebarActive = view.name === "module" ? "modules"
    : view.name === "lecon-detail" ? "lecons"
    : view.name;

  const showBanner = !!banner && bannerVisible;

  const handleRenewalSuccess = (data) => {
    setShowRenewal(false);
    const newEnd = data?.abonnement?.end_date;
    if (newEnd) {
      setLocalUser(prev => ({ ...prev, subscription_end_date: newEnd, subscription_active: true }));
      if (onUserUpdate) onUserUpdate(prev => ({ ...prev, subscription_end_date: newEnd, subscription_active: true }));
    }
    refreshStats();
  };

  return (
    <LearningProgressProvider revision={stats}>
    <NouveautesContext.Provider value={compteurs.nouveaux || {}}>
    <div className="ql-shell">
      {showRenewal && !consumptionOnly && (
        <RenewalModal
          onClose={() => setShowRenewal(false)}
          onSuccess={handleRenewalSuccess}
        />
      )}
      {/* ── BANNIÈRE SUPERADMIN ── */}
      {showBanner && (banner.media_type || banner.youtube_id) && (
        <BannierePublicitaire banner={banner} onClose={dismissBanner} />
      )}
      {showBanner && !banner.media_type && !banner.youtube_id && (
        <div className={`ql-banner ql-banner-${banner.msg_type}`}>
          <div className="ql-banner-body">
            <i className={`bi ${
              banner.msg_type === "success" ? "bi-check-circle-fill" :
              banner.msg_type === "warning" ? "bi-exclamation-triangle-fill" :
              banner.msg_type === "danger"  ? "bi-x-octagon-fill" :
              "bi-info-circle-fill"
            } ql-banner-icon`}></i>
            <div className="ql-banner-text">
              {banner.title && <strong className="ql-banner-title">{banner.title}</strong>}
              <span className="ql-banner-content"><MathText text={banner.content} /></span>
            </div>
          </div>
          <button className="ql-banner-close" onClick={dismissBanner} aria-label="Fermer">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      {/* ── OVERLAY MOBILE ── */}
      {sidebarOpen && (
        <div className="ql-sb-overlay" onClick={() => setSidebarOpen(false)} />
      )}
      {progressionOpen && (
        <div className="ql-sb-overlay" onClick={() => setProgressionOpen(false)} />
      )}

      {/* ── SIDEBAR ── */}
      <aside className={`ql-sb${sidebarOpen ? " ql-sb-open" : ""}`}>
        <div className="ql-sb-logo" onClick={() => navigate("dashboard")} style={{ cursor: "pointer" }}>
          <i className="bi bi-mortarboard-fill ql-sb-logo-icon"></i>
          <div>
            <div className="ql-sb-logo-top">QUICK LEARNING</div>
            <div className="ql-sb-logo-bot">ESPACE ÉLÈVE</div>
          </div>
        </div>

        <button
          data-visite="dashboard"
          className={`ql-sb-active-btn${view.name === "dashboard" ? " is-active" : ""}`}
          onClick={() => navigate("dashboard")}
        >
          <i className="bi bi-house-fill"></i><span>Tableau de bord</span>
        </button>

        <NavSection label="APPRENDRE"    items={NAV_APPRENDRE}    activeView={sidebarActive} onNav={navigate} compteurs={compteurs} />
        <NavSection label="ÉVALUER"      items={NAV_EVALUER}      activeView={sidebarActive} onNav={navigate} compteurs={compteurs} />
        <NavSection label="OUTILS"       items={NAV_OUTILS}       activeView={sidebarActive} onNav={navigate} />
        <NavSection label="COMMUNIQUER"  items={NAV_COMMUNIQUER}  activeView={sidebarActive} onNav={navigate} compteurs={compteurs} />
        <NavSection label="LIVE"         items={NAV_LIVE}         activeView={sidebarActive} onNav={navigate} />

        <div className="flex-fill"></div>

        <div className="ql-sb-classe-card">
          <div className="ql-sb-avatar">
            {localUser?.photo
              ? <img src={localUser.photo} alt="" className="ql-sb-avatar-img" />
              : <i className="bi bi-person-fill"></i>}
          </div>
          <div>
            <div className="ql-sb-classe-title">Ma classe</div>
            <div className="ql-sb-classe-sub">{localUser?.classe || "–"}</div>
          </div>
          <i className="bi bi-chevron-down ms-auto"></i>
        </div>

        <div className="ql-sb-bottom">
          <a className="ql-sb-link" href="/parrainage">
            <i className="bi bi-gift"></i><span>Mon code de parrainage</span>
          </a>
          <button className="ql-sb-link" onClick={() => { setParametresOuverts(true); setSidebarOpen(false); }}><i className="bi bi-gear"></i><span>Paramètres</span></button>
          <button className="ql-sb-link" onClick={onLogout}>
            <i className="bi bi-box-arrow-right"></i><span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <div className="ql-main-wrap">
        {/* Topbar */}
        <header className="ql-topbar">
          <div className="d-flex align-items-center gap-3 ql-topbar-gauche">
            <button className="ql-icon-btn ql-hamburger" data-visite="menu" onClick={() => setSidebarOpen(v => !v)} aria-label="Menu">
              <i className={`bi ${sidebarOpen ? "bi-x-lg" : "bi-list"} fs-4`}></i>
              {!sidebarOpen && <Pastille nombre={totalPastilles} className="ql-pastille--menu" />}
            </button>
            <div className="ql-search" data-visite="recherche" style={{ position: "relative" }}>
              <i className="bi bi-search"></i>
              <input
                placeholder="Code ou titre : MEC-LDN-QCM-01..."
                value={searchQ}
                onFocus={() => setShowSearch(true)}
                onBlur={() => setTimeout(() => setShowSearch(false), 200)}
                onChange={e => handleSearch(e.target.value)}
              />
              {showSearch && (
                <SearchDropdown
                  q={searchQ}
                  results={searchResults}
                  loading={searchState.loading}
                  error={searchState.error}
                  page={searchState.page}
                  hasNext={searchState.hasNext}
                  onMore={(nextPage) => loadSearchPage(searchQ.trim(), nextPage)}
                  onSelect={handleSearchSelect}
                  onClose={() => setShowSearch(false)}
                />
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 ql-topbar-droite">
            <span data-visite="notifications" className="d-inline-flex"><NotifBell /></span>
            <IncomingCallListener />
            <button
              data-visite="progression"
              className="ql-icon-btn ql-progression-btn"
              onClick={() => setProgressionOpen(v => !v)}
              title="Ma progression"
              aria-label="Ma progression"
            >
              <i className="bi bi-bar-chart-line-fill fs-5"></i>
            </button>
            <button className="ql-icon-btn" data-visite="aide" onClick={() => setVisite(true)} title="Aide — Comment ça marche ?"><i className="bi bi-question-circle fs-5"></i></button>
            <div
              data-visite="profil"
              className="ql-user-chip"
              onClick={() => setShowProfile(true)}
              title="Modifier mon profil"
              style={{ cursor: "pointer" }}
            >
              {localUser?.photo
                ? <img src={localUser.photo} alt="" className="ql-chip-avatar-img" />
                : <div className="ql-chip-avatar"><i className="bi bi-person-fill"></i></div>}
              <div>
                <div className="ql-chip-name">{localUser?.nom_complet || localUser?.username}</div>
                <div className="ql-chip-class">{localUser?.classe || ""}</div>
              </div>
              <i className="bi bi-chevron-down text-muted" style={{ fontSize: "0.75rem" }}></i>
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="ql-body">
          <main ref={contentRef} className={`ql-content${view.name === "messages" ? " ql-content--fullscreen" : ""}`}>
            {view.name !== "hors-connexion" && <BandeauHorsConnexion onVoir={() => navigate("hors-connexion")} />}

            {/* ── HOME ── */}
            {view.name === "dashboard" && (
              <>
                <h2 className="ql-greeting">Bonjour {prenom} 👋</h2>
                <p className="ql-greeting-sub">Prêt à continuer ton apprentissage aujourd'hui ?</p>

                <SubscriptionCountdown
                  endDateStr={localUser?.subscription_end_date}
                  onRenew={consumptionOnly ? undefined : () => setShowRenewal(true)}
                  consumptionOnly={consumptionOnly}
                  onRefresh={onRefreshAccount}
                />

                <LiveDashboardSection
                  lives={lives}
                  endedLives={endedLives}
                  onViewAll={() => navigate("lives")}
                  onStatutChange={handleLiveStatutChange}
                  onNavigateMessages={() => navigate("messages")}
                />

                <div className="ql-stat-grid">
                  <div className="ql-scard ql-scard--blue" style={{ cursor: "pointer" }} onClick={() => navigate("modules")}>
                    <i className="bi bi-book ql-scard-icon"></i>
                    <div className="flex-fill">
                      <div className="ql-scard-label">Modules étudiés</div>
                      <div className="ql-scard-val">{chapCompleted} / {totalChapters}</div>
                      <div className="ql-mini-bar mt-1"><div style={{ width: `${Math.min(100,Math.round((chapCompleted/totalChapters)*100))}%`, background: "#0d6efd" }}></div></div>
                      <div className="ql-scard-pct">{Math.round((chapCompleted/totalChapters)*100)}%</div>
                    </div>
                  </div>
                  <div className="ql-scard ql-scard--green" style={{ cursor: "pointer" }} onClick={() => navigate("exercices")}>
                    <i className="bi bi-check-circle ql-scard-icon"></i>
                    <div className="flex-fill">
                      <div className="ql-scard-label">Exercices réalisés</div>
                      <div className="ql-scard-val">{exercisesDone} / {totalExercises}</div>
                      <div className="ql-mini-bar mt-1"><div style={{ width: `${Math.min(100,Math.round((exercisesDone/totalExercises)*100))}%`, background: "#198754" }}></div></div>
                      <div className="ql-scard-pct">{Math.round((exercisesDone/totalExercises)*100)}%</div>
                    </div>
                  </div>
                  <div className="ql-scard ql-scard--purple" style={{ cursor: "pointer" }} onClick={() => navigate("resultats")}>
                    <i className="bi bi-trophy ql-scard-icon"></i>
                    <div className="flex-fill">
                      <div className="ql-scard-label">Score moyen</div>
                      <div className="ql-scard-val">{avgScore}%</div>
                      <div className="ql-scard-pct">Bien joué !</div>
                    </div>
                  </div>
                  <div className="ql-scard ql-scard--orange" style={{ cursor: "pointer" }} onClick={() => navigate("activites")}>
                    <i className="bi bi-clock ql-scard-icon"></i>
                    <div className="flex-fill">
                      <div className="ql-scard-label">Temps d'apprentissage</div>
                      <div className="ql-scard-val">{formatTime(learningMinutes)}</div>
                      <div className="ql-scard-pct">
                        {learningMinutes === 0 ? "Commence à apprendre !" : "Total cumulé"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ql-tabs">
                  {[
                    { key: "continuer",   label: "Continuer à apprendre" },
                    { key: "recents",     label: "Récemment consultés" },
                    { key: "recommandes", label: "Recommandé pour vous" },
                  ].map(t => (
                    <button key={t.key} className={`ql-tab${activeTab === t.key ? " active" : ""}`} onClick={() => setActiveTab(t.key)}>
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="ql-chapter-list">
                  {modulesAContinuer.map((ch, i) => {
                    const avancement = progressionModules[ch.id] || { percent: 0, last_activity: null };
                    return (
                    <div key={ch.id} className="ql-ch-row">
                      <div className="ql-ch-thumb" style={{ background: chColor(i) }}>
                        <i className="bi bi-diagram-2"></i>
                      </div>
                      <div className="ql-ch-info">
                        <div className="ql-ch-code">CH-{String(ch.order || i + 1).padStart(2, "0")}</div>
                        <div className="ql-ch-name">{ch.title}</div>
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <div className="ql-mini-bar" style={{ width: 120 }}>
                            <div style={{ width: `${avancement.percent}%`, background: chColor(i), transition: "width .6s ease" }}></div>
                          </div>
                          <span style={{ fontSize: "0.75rem", color: "#64748b" }}>{avancement.percent}% terminé</span>
                        </div>
                      </div>
                      <div className="ql-ch-action">
                        <button className="btn btn-outline-primary btn-sm" onClick={() => navigate("module", ch)}>
                          <i className="bi bi-play-fill me-1"></i>Continuer
                        </button>
                        <div className="ql-ch-last">Dernière activité</div>
                        <div className="ql-ch-when">{avancement.last_activity ? timeAgo(avancement.last_activity) : "Pas encore commencé"}</div>
                      </div>
                    </div>
                    );
                  })}
                  {chapters.length === 0 && (
                    <div className="text-center text-muted py-5">
                      <i className="bi bi-book-half" style={{ fontSize: "2.5rem" }}></i>
                      <p className="mt-2 mb-0">Aucun module disponible.</p>
                    </div>
                  )}
                </div>

                <button className="ql-see-all" onClick={() => navigate("modules")}>
                  <i className="bi bi-book me-1"></i>Voir tous mes modules
                  <i className="bi bi-chevron-right ms-1"></i>
                </button>

                <h5 className="fw-bold mt-4 mb-3" style={{ color: "var(--navy)" }}>Activités récentes</h5>
                <RecentActivities onViewAll={() => navigate("activites")} />
              </>
            )}

            {/* ── LIVES ── */}
            {view.name === "lives" && (
              <LivesPage
                lives={lives}
                endedLives={endedLives}
                onStatutChange={handleLiveStatutChange}
                onNavigateMessages={() => navigate("messages")}
              />
            )}

            {/* ── MODULES ── */}
            {view.name === "modules" && (
              <ModulesView modules={modules} onOpen={(mod) => navigate("module", mod)} />
            )}

            {/* ── LEÇONS (liste globale) ── */}
            {view.name === "lecons" && (
              <LeconsView modules={modules} onOpenModule={(mod) => navigate("module", mod)} />
            )}

            {/* ── MODULE DETAIL ── */}
            {view.name === "module" && view.data && (
              <ModuleDetail
                module={view.data}
                allModules={modules}
                onBack={() => navigate("modules")}
                onOpen={(mod) => navigate("module", mod)}
                onSolve={(ex) => navigate("exercice-solver", { exercice: ex, back: "module", module: { ...view.data, _returnTab: "exercices" }, scroll: currentScroll() })}
                onPlay={(qcm) => navigate("qcm-player", { qcm, back: "module", module: { ...view.data, _returnTab: "qcm" }, scroll: currentScroll() })}
                onWatched={refreshStats}
                onReady={restoreScroll}
              />
            )}

            {/* ── VIDEOS ── */}
            {view.name === "videos" && <VideosView chapters={modules} onWatched={refreshStats} />}

            {/* ── COURS AUDIO ── */}
            {view.name === "audios" && (
              <div>
                <div className="ql-view-header">
                  <h3 className="ql-view-title"><i className="bi bi-headphones me-2"></i>Cours audio</h3>
                </div>
                <CoursAudio onProgression={refreshStats} />
              </div>
            )}

            {/* ── EXERCICES ── */}
            {view.name === "exercices" && (
              <ExercicesView
                chapters={modules}
                onSolve={(ex) => navigate("exercice-solver", { exercice: ex, back: "exercices", scroll: currentScroll() })}
                onReady={restoreScroll}
              />
            )}

            {/* ── EXERCICE SOLVER ── */}
            {view.name === "exercice-solver" && view.data && (
              <ExerciceSolverView
                exercice={view.data.exercice}
                onSave={refreshStats}
                onBack={() => {
                  if (view.data.back === "module") navigateBack("module", view.data.module, view.data.scroll);
                  else navigateBack("exercices", null, view.data.scroll);
                }}
              />
            )}

            {/* ── QCM ── */}
            {view.name === "qcm" && (
              <QCMView
                chapters={chapters}
                onPlay={(qcm) => navigate("qcm-player", { qcm, back: "qcm", scroll: currentScroll() })}
                onReady={restoreScroll}
              />
            )}

            {/* ── QCM PLAYER ── */}
            {view.name === "qcm-player" && view.data && (
              <QCMPlayerView
                qcm={view.data.qcm}
                onSave={refreshStats}
                onBack={() => {
                  if (view.data.back === "module") navigateBack("module", view.data.module, view.data.scroll);
                  else navigateBack("qcm", null, view.data.scroll);
                }}
              />
            )}

            {/* ── ACTIVITES ── */}
            {view.name === "activites" && <ActivitesView learningMinutes={learningMinutes} />}

            {/* ── EVALUATIONS ── */}
            {view.name === "evaluations" && <EvaluationsView key={view.data?.evaluation?.id || "liste"} initialEvaluation={view.data?.evaluation || null} />}

            {/* ── DEVOIRS ── */}
            {view.name === "devoirs" && <DevoirsView key={view.data?.devoirId || "liste"} initialDevoirId={view.data?.devoirId || null} />}

            {/* ── MESSAGES ── */}
            {view.name === "messages" && <AssistanceEleve user={user} />}

            {/* ── ADMIN ACTIVITES ── */}
            {view.name === "admin-activites" && <AdminActivitesView />}

            {/* ── RESSOURCES ── */}
            {view.name === "ressources" && (
              <RessourcesView modules={modules} navigate={navigate} />
            )}

            {/* ── MES RÉSULTATS ── */}
            {view.name === "resultats" && <MesResultatsView />}

            {/* ── FAVORIS ── */}
            {view.name === "favoris" && <MesFavorisView navigate={navigate} />}

            {/* ── NOTES ── */}
            {view.name === "notes" && <NotesOrganizationView />}

            {/* ── CALCULATRICE ── */}
            {view.name === "calculatrice" && <CalculatriceView />}

            {/* ── WIP ── */}
            {view.name === "formules" && <FormulesView />}

            {/* ── HORS CONNEXION ── */}
            {view.name === "hors-connexion" && <MesContenusHorsConnexion />}
          </main>

          {/* ── RIGHT PANEL ── */}
          <aside className={`ql-rpanel${progressionOpen ? " ql-rpanel-open" : ""}`} data-visite="progression">
            {activeManuel && (
              <div className="ql-panel-card ql-manuel-cover-card" onClick={() => navigate("modules")} style={{ cursor: "pointer" }}>
                {activeManuel.image_url
                  ? <img src={activeManuel.image_url} alt={activeManuel.title} className="ql-manuel-cover-img" />
                  : <i className="bi bi-book-half" style={{ fontSize: "2rem", color: "#64748b" }}></i>}
                <div className="ql-manuel-cover-info">
                  <div className="ql-manuel-cover-title">{activeManuel.title}</div>
                  {activeManuel.level && <div className="ql-manuel-cover-level">{activeManuel.level}</div>}
                  <ManuelSwitcher manuels={localUser?.manuels_classe || []} />
                </div>
              </div>
            )}
            <div className="ql-panel-card">
              <div className="ql-panel-head">
                <span className="fw-bold">Ma progression</span>
                <button className="ql-link-blue btn-link-bare" onClick={() => navigate("resultats")}>Voir le détail</button>
              </div>
              <div className="ql-donut-wrap">
                <Doughnut data={donutData} options={{ cutout: "76%", plugins: { tooltip: { enabled: false } }, maintainAspectRatio: true }} />
                <div className="ql-donut-center"><span className="ql-donut-pct">{globalPct}%</span></div>
              </div>
              <div className="text-center mb-3">
                <div className="fw-semibold" style={{ fontSize: "0.88rem" }}>Progression générale</div>
                <div className="ql-appreciation" style={{
                  color: globalPct >= 70 ? "#198754" : globalPct >= 40 ? "#f5820d" : "#64748b"
                }}>
                  {globalPct >= 86 ? "Excellent ! Continue ainsi 🏆"
                    : globalPct >= 70 ? "Très bien ! Tu progresses bien 👍"
                    : globalPct >= 50 ? "Bien ! Encore un effort 💪"
                    : globalPct >= 30 ? "Assez bien, continue à travailler !"
                    : globalPct > 0  ? "Bon début ! Reviens chaque jour 📚"
                    : "Commence à apprendre pour voir ta progression !"}
                </div>
              </div>
              <ProgressRow label="Cours"        val={chapCompleted}        max={totalChapters}   color="#0d6efd" />
              <ProgressRow label="Exercices"    val={exercisesDone}        max={totalExercises}  color="#198754" />
              <ProgressRow label="QCM réussis"  val={qcmPassed}            max={totalQcms}       color="#6f42c1" />
              <ProgressRow label="Vidéos"       val={videosViewed}         max={totalVideos}     color="#f5820d" />
              {totalDevoirs > 0 && (
                <ProgressRow label="Devoirs"    val={devoirsSubmitted}     max={totalDevoirs}    color="#0dcaf0" />
              )}
              {totalEvaluations > 0 && (
                <ProgressRow label="Évaluations" val={evaluationsSubmitted} max={totalEvaluations} color="#dc2626" />
              )}
            </div>

            {/* Score moyen */}
            {avgScore > 0 && (
              <div className="ql-panel-card ql-score-card">
                <div className="ql-panel-head">
                  <span className="fw-bold">Score moyen</span>
                </div>
                <div className="ql-score-display">
                  <div className="ql-score-circle" style={{
                    borderColor: avgScore >= 70 ? "#198754" : avgScore >= 50 ? "#f5820d" : "#dc2626"
                  }}>
                    <span className="ql-score-num" style={{
                      color: avgScore >= 70 ? "#198754" : avgScore >= 50 ? "#f5820d" : "#dc2626"
                    }}>{avgScore}%</span>
                  </div>
                  <div className="ql-score-info">
                    <div className="ql-score-label">
                      {avgScore >= 70 ? "Très bien !" : avgScore >= 50 ? "Passable" : "À améliorer"}
                    </div>
                    <div className="ql-score-sub">
                      {avgScore >= 70 ? "Tu maîtrises bien les QCM"
                        : avgScore >= 50 ? "Continue tes révisions"
                        : "Révise tes leçons et réessaie"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="ql-panel-card">
              <div className="ql-panel-head">
                <span className="fw-bold">Prochains devoirs</span>
                <button className="ql-link-blue btn-link-bare" onClick={() => navigate("devoirs")}>Voir tout</button>
              </div>
              {sideDevoirs.length === 0 ? (
                <div className="text-muted text-center py-3" style={{ fontSize: "0.82rem" }}>
                  <i className="bi bi-check2-circle me-1"></i>Aucun devoir à venir
                </div>
              ) : sideDevoirs.map(d => {
                const due = d.date_echeance ? new Date(d.date_echeance) : null;
                const { badge, badgeClass, delay } = devoirDelay(d.date_echeance);
                return (
                  <div key={d.id} className="ql-devoir-row" onClick={() => navigate("devoirs")} style={{ cursor: "pointer" }}>
                    <div className="ql-devoir-date">
                      <span className="ql-devoir-day">{due ? due.getDate().toString().padStart(2, "0") : "–"}</span>
                      <span className="ql-devoir-month">{due ? MOIS_FR[due.getMonth()] : "–"}</span>
                    </div>
                    <div className="flex-fill ms-2 overflow-hidden">
                      <div className="ql-devoir-title">{d.title}</div>
                      <div style={{ fontSize: "0.75rem", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {d.matiere_nom || d.module_title || ""}
                      </div>
                    </div>
                    <div className="text-end flex-shrink-0">
                      {badge && <div className={`ql-devoir-badge ${badgeClass || ""}`}>{badge}</div>}
                      <div style={{ fontSize: "0.72rem", color: "#94a3b8", whiteSpace: "nowrap" }}>{delay}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {epreuves.length > 0 && (
              <div className="ql-panel-card" data-epreuves-obligatoires>
                <div className="ql-panel-head">
                  <span className="fw-bold">Épreuves obligatoires</span>
                  <button className="ql-link-blue btn-link-bare" onClick={() => navigate("evaluations")}>Voir tout</button>
                </div>
                {epreuvesARendre.length === 0 && (
                  <div className="text-muted text-center py-3" style={{ fontSize: "0.82rem" }}>
                    <i className="bi bi-check2-circle me-1"></i>Aucune épreuve à rendre
                  </div>
                )}
                {epreuvesARendre.map(e => {
                  const due = e.echeance ? new Date(e.echeance) : null;
                  const jours = due ? Math.ceil((due - new Date()) / 86400000) : null;
                  const statut = e.rendu_le
                    ? (e.grade ? { badge: e.grade, classe: "", texte: "Corrigée" } : { badge: "Remise ✓", classe: "", texte: "En attente de correction" })
                    : e.en_retard ? { badge: "En retard", classe: "ql-devoir-badge--retard", texte: "Échéance dépassée" }
                    : { badge: jours <= 0 ? "Aujourd'hui" : jours === 1 ? "Demain" : `${jours} jours`, classe: jours <= 2 ? "ql-devoir-badge--urgent" : "",
                        texte: due ? `avant ${due.toLocaleTimeString("fr-FR", { timeZone: "Africa/Douala", hour: "2-digit", minute: "2-digit" })}` : "" };
                  return (
                    <div key={e.id} className="ql-devoir-row" onClick={() => navigate("evaluations", { evaluation: { id: e.id } })} style={{ cursor: "pointer" }}>
                      <div className="ql-devoir-date">
                        <span className="ql-devoir-day">{due ? due.toLocaleDateString("fr-FR", { timeZone: "Africa/Douala", day: "2-digit" }) : "–"}</span>
                        <span className="ql-devoir-month">{due ? MOIS_FR[Number(due.toLocaleDateString("en-US", { timeZone: "Africa/Douala", month: "numeric" })) - 1] : "–"}</span>
                      </div>
                      <div className="flex-fill ms-2 overflow-hidden">
                        <div className="ql-devoir-title">{e.title}</div>
                        <div style={{ fontSize: "0.75rem", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {e.matiere_nom || "Épreuve"}
                        </div>
                      </div>
                      <div className="text-end flex-shrink-0">
                        <div className={`ql-devoir-badge ${statut.classe}`}>{statut.badge}</div>
                        <div style={{ fontSize: "0.72rem", color: "#94a3b8", whiteSpace: "nowrap" }}>{statut.texte}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="ql-panel-card">
              <div className="ql-panel-head">
                <span className="fw-bold">Mes badges</span>
                <button className="ql-link-blue btn-link-bare">Voir tous</button>
              </div>
              <div className="d-flex justify-content-around py-2">
                {computeBadges(stats).map((b, i) => (
                  <div key={i} className={`ql-badge-item${b.earned ? "" : " ql-badge-locked"}`} title={b.earned ? b.label : b.locked}>
                    <div className="ql-badge-icon" style={{ background: b.earned ? b.color : "#cbd5e1" }}>
                      {b.earned
                        ? <i className={`bi ${b.icon}`}></i>
                        : <i className="bi bi-lock-fill"></i>
                      }
                    </div>
                    <div className="ql-badge-label">{b.label}</div>
                    <div className="ql-badge-sub">{b.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ql-panel-card ql-astuce">
              <i className="bi bi-lightbulb-fill ql-astuce-icon"></i>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Astuce du jour</div>
                <div
                  className="ql-astuce-body"
                  style={{ opacity: astuceFade ? 1 : 0, transition: "opacity 0.4s ease" }}
                >
                  <p className="mb-1 small text-muted">{ASTUCES[astuceIdx].text}</p>
                  <div style={{ fontSize: "0.8rem", color: "var(--orange)", fontWeight: 600 }}>
                    {ASTUCES[astuceIdx].sign}
                  </div>
                </div>
                <div className="ql-astuce-dots">
                  {ASTUCES.map((_, i) => (
                    <button
                      key={i}
                      className={`ql-astuce-dot${i === astuceIdx ? " active" : ""}`}
                      onClick={() => { setAstuceFade(false); setTimeout(() => { setAstuceIdx(i); setAstuceFade(true); }, 200); }}
                      title={`Message ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {visite && <VisiteGuidee etapes={ETAPES_ELEVE} espace="eleve" user={user} onClose={() => setVisite(false)} />}

      {/* ── Calculatrice flottante ── */}
      {floatCalc && <FloatingCalc onClose={() => setFloatCalc(false)} />}

      {parametresOuverts && <Parametres onClose={() => setParametresOuverts(false)} />}
      <BarreAccesRapide vueActive={sidebarActive} onNavigate={navigate} compteurs={compteurs} />
      {showProfile && (
        <ProfileModal
          user={localUser}
          onClose={() => setShowProfile(false)}
          onSave={handleProfileSave}
          consumptionOnly={consumptionOnly}
          onAccountDeleted={onLogout}
        />
      )}
    </div>
    </NouveautesContext.Provider>
    </LearningProgressProvider>
  );
}

export default StudentDashboard;
